import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from "gsap";
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { Water } from 'three/examples/jsm/objects/Water2.js';
import { Water as Ocean } from 'three/examples/jsm/objects/Water.js';

import * as CANNON from 'cannon-es';
import { GUI } from 'dat.gui'
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { EventEmitter } from '@billjs/event-emitter';

import AxL from "./axl_obfuscated.js";
// import AxL from "./axl.js";

import Flamingo from './Flamingo.js';
import HexFloor from './HexFloor.js';
import WordSoup from './WordSoup.js';
import CharacterController from './CharacterController.js';

import UI from './UI.js';
import Translator from './Translator.js';
import ChatDialog from './ChatDialog.js';
import YinkaColor from './YinkaColors.js';

import Birds from './Birds.js';
import SoundEffects from './SoundEffects.js';
import { get_distance, degrees_to_radians, radians_to_degrees, shuffle } from './Utils.js';
import { colorPalette } from './YinkaColors.js';
import ParticleSystem from './ParticleSystem.js';
import DialogManager from './DialogManager.js';
import MusicLMManager from './MusicLMManager.js';
import ToolTips from './ToolTips.js';

// testin gmobile stuff
import MobilePadController from './MobilePadController.js';

const FIND_WORDS = true;
const TEST_MOBILE = false;
const DEBUG_MODE = false;

var now, delta, then = Date.now();
var interval = 1000 / 30;

var isMobile = require('is-mobile');
let FALLBACKMODE = false;

const floorTextureData = {
  "kalimba": { path: "flower", ratio: .75, "limit": 7 },
  "bass": { path: "eyes", ratio: 1, "limit": 8 },
  "percussion": { path: "throw", ratio: 1.32, "limit": 8 }
};

const levelConfig = require('../static/assets/level_config.json');
const levelPublic = require('../static/assets/level_prompts_public.json');

const attributions = {
  "Models": [
    { "label": "Lowpoly Trees", "link": "https://www.cgtrader.com/free-3d-models/plant/leaf/low-poly-plant-set" },
    { "label": "Flying Bird", "link": "https://sketchfab.com/3d-models/flying-bird-eb843194e06d429ebef7dd4aa7e265c1" },
    { "label": "Ferns", "link": "https://sketchfab.com/3d-models/rock-and-plants-0243da4c6c644ac099033d5de89ac82e" },
    { "label": "Clouds", "link": "https://sketchfab.com/3d-models/cartoon-cloud-f31be54812a348df86d94171098dae19" },
    { "label": "Apples", "link": "https://www.cgtrader.com/free-3d-models/food/fruit/apple-5a953730-5803-4770-8739-cc535f18b9eb" }
  ],
  "Sounds": [
    { "label": "Birds", "link": "https://pixabay.com/sound-effects/birds-chirping-04-6771/" },
    { "label": "Splash", "link": "https://pixabay.com/sound-effects/splash-6213/" },
    { "label": "Flamingo Calls", "link": "https://pixabay.com/sound-effects/045208-flamingos-64177/" }
  ]
};

export default class PlayableFlamingo extends EventEmitter {

  constructor(axl) {
    super();
    this.axl = axl;

    this.modelsLoaded = 0;

    this.ui = new UI();
    this.translator = new Translator(); // for loading text all ui text
    this.musiclm = new MusicLMManager();

    // axl freeze / unfreeze
    this.isAnimating = true;
    this.isFrozen = false;
    this.beforeFreeze = { 'mute': false };
    this.pauseAnimation = true; // needed?


    this.finishedBeforeLaunch = false;
    this.waitingForFirstResume = false;

    // different app states
    this.initWorldDone = false;
    this.mode = "preview";
    this.controlsState = "default";
    this.isColoredIn = false;
    this.followCam = true;
    this.is_using_api = true;
    this.destinationX = 0; // needed?
    this.levelName = "community";
    this.helperHintLabel = "";
    this.isZoomed = false;
    this.didFirstResume = false;
    this.didWorld = false;
    this.endLevelName = "spirit";
    this.benchBodies = [];
    this.loadingPct = { val: 0 };
    this.guardianTile = 212;
    this.planks = null;
    this.seekTargets = [];
    this.jumpsCount = 0;
    this.solvedWords = [];

    // resuable vecs
    this.distVec = new THREE.Vector3();
    this.lookAtPtVec = new THREE.Vector3();
    this.AXIS_Y = new THREE.Vector3(0, 1, 0);
    this.moveDir = new THREE.Vector3(0, 0, 0);
    this.tempVec = new THREE.Vector3(0, 0, 0);
    // interactivity
    this.isDoubleClick = false;
    this.clickableObjects = [];       // array of all clickable objects for raycasting

    // objects and data
    this.pineTrees = [];              // array of trees added
    this.prompts = ["", "", "", "", "", ""];
    this.maxFlamingos = 5;
    this.eldersFound = 0;
    this.promptWordBank = []; // all prompt words that have been chosen by you
    this.otherUserPromptTM = null;
    this.vizTimerLen = { val: 220 };
    this.userColors = [0xee6e37, 0xee65c9, 0xc463ff];
    this.userColorsHex = ['#ef8a40', '#ee65c9', '#c463ff'];
    this.allEldersFound = [];
    this.elderQTms = [];
    this.currentSongVol = 1;
    this.onboardBTMs = [];
    this.speechBubblePos = null;

    // pick random image
    this.artworksNum = { "community": Math.floor(Math.random() * 18), "spirit": Math.floor(Math.random() * 18) };

    // audio settings
    this.muted = false;

    // keyboard and start state flags
    this.gameStarted = false;
    this.didOpenDoors = false;
    this.ignoreNextSpaceUp = false;
    this.spaceNeedsLift = false;
    this.enterNeedsLift = false;
    this.didSetSkipTutorial = false;


    // mobile settings
    if (this.isMobile()) {
      document.body.classList.add("mobile");
      this.mobilepad = new MobilePadController();

      document.getElementById("three-scene").addEventListener('touchstart', function (event) {
        event.preventDefault();
      }, false);

    }

    if (!AxL.can["download"]) {
      document.body.classList.add('no-download');
    }

  }

  isMobile() {
    if (TEST_MOBILE) return true;
    return isMobile({ tablet: true, featureDetect: true });
  }

  //----------------------- AXL
  handleAxlFreeze() {

    this.isFrozen = true;

    this.beforeFreeze = {};

    if (this.isMuted()) {
      this.beforeFreeze['mute'] = true;
    } else {
      this.beforeFreeze['mute'] = false;
    }

    this.handleMute();
    this.isAnimating = false;

  }

  handleAxlResume() {

    this.isFrozen = false;

    this.isAnimating = true;
    this.animate();
    if (this.beforeFreeze['mute'] == false)
      this.handleUnMute();
  }

  handleAxlTrack(action, label = null, value = null) {
    if (this.axl == null) {
      return;
    }
    if (label != null && value != null) {
      this.axl.sendMessage(AxL.ChildToHost.TRACK, { action: action, label: label, value: value });
    } else if (label != null) {
      this.axl.sendMessage(AxL.ChildToHost.TRACK, { action: action, label: label });
    } else if (value != null) {
      this.axl.sendMessage(AxL.ChildToHost.TRACK, { action: action, value: value });
    } else {
      this.axl.sendMessage(AxL.ChildToHost.TRACK, { action: action });
    }

  }

  handleAxlFirstResume() {
    console.log("handleAxlFirstResume");

    document.querySelector("#landing-door").classList.add("zoom1");

    if (!this.finishedBeforeLaunch) {
      console.log(" no handleAxlFirstResume, ", this.finishedBeforeLaunch);
      this.waitingForFirstResume = true;
      return;

    }

    const video = document.getElementById('home-video');
    if (this.isMobile()) {
      video.src = './videos/Dreaming_4.mov';
    } else {
      video.src = './videos/Dreaming_4b.mov';
    }
    video.load();

    video.addEventListener('loadeddata', () => {
      // Video has finished loading
      console.log('Video is loaded!');
      video.play();
      document.getElementById('home-video-container').classList.add("show");
    });

    this.ui.init();
    this.ui.startWorldLoadingAnimation();

    this.initWorld();


  }

  //-----------------------  world and ui initializations

  async initUI() {

    // iframe players
    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Define the onYouTubeIframeAPIReady function
    window.onYouTubeIframeAPIReady = function () {
      const videoId = 'mzzxR2N3gEs';

      const playerContainer2 = document.getElementById('info-bio-popup-video-trailer');
      const playerDiv2 = document.createElement('div');
      playerDiv2.id = `player-${videoId}`; // Use a unique ID
      playerContainer2.appendChild(playerDiv2);
      this.player2 = new YT.Player(`player-${videoId}`, {
        height: 'auto',
        width: '100%',
        videoId: videoId,
        playerVars: {
          // Optional player parameters:
          // https://developers.google.com/youtube/player_parameters
          'autoplay': 0, // 0: Off, 1: On
          'controls': 1, // 0: Off, 1: On
          'rel': 0, // 0: Related videos disabled, 1: Related videos enabled
          'showinfo': 0, // 0: Show video title and player controls, 1: Hide video title and player controls
        },
        events: {
          'onStateChange': this.handleYouTubeState.bind(this)
        }
      });


    }.bind(this);


    if (AxL.can.useAltIcon) {
      document.querySelector(".share-button").classList.add("ios");
      document.getElementById("share-image").classList.add("ios");
      document.getElementById("collected-images-popup").classList.add("ios");
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("fallbackmode")) {
      FALLBACKMODE = true;
      document.getElementById("landing-loading-start-container").style.display = "none";
      document.getElementById("fallback-mode").innerText = "This experiment in currently unavailable. Please come back later to play.";
      document.getElementById("fallback-mode").classList.add("show");
    }

    this.instrument = levelConfig[this.levelName]['instrument'];
    document.body.classList.add(this.instrument);

    // ui for interfaces / start screens
    //this.ui.init();

    this.updateElderUi();

    // ui to chat with other flamingos
    this.chatDialog = new ChatDialog();
    this.chatDialog.on("chat-button", function (val) { this.handleChatDialogButton(val.data) }.bind(this));
    this.chatDialog.on("click_chat", function (val) { this.playNextDialogPart() }.bind(this));

    // flamingo head animation on landing page
    let flamingoHeadEl = document.querySelector(".flamingo-head");
    this.flamingoHeadTL = gsap.timeline({ repeat: -1, repeatDelay: 10, delay: 3 });
    this.flamingoHeadTL.to(flamingoHeadEl, { bottom: 0, left: "50%", ease: "elastic.out(1, 0.65)", duration: 1 });
    this.flamingoHeadTL.to(flamingoHeadEl, { rotate: 13, duration: .25 });
    this.flamingoHeadTL.to(flamingoHeadEl, { rotate: -8, duration: .25, delay: 1 });
    this.flamingoHeadTL.to(flamingoHeadEl, { rotate: 0, duration: .25, delay: .5 });
    this.flamingoHeadTL.to(flamingoHeadEl, { bottom: "-25%", left: "60%", duration: .5, delay: 2 });

    document.getElementById("landing-enter").addEventListener('click', function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      if (this.gameStarted) return;
      this.didFirstStart = true;
      this.handleAxlTrack("click_enter");
      this.handleStartButtonClicked();
    }.bind(this));

    document.getElementById("jump-button").addEventListener('touchstart', function (e) {
      e.preventDefault();
    });

    document.getElementById("jump-button").addEventListener('touchmove', function (e) {
      e.preventDefault();
    });

    document.getElementById("jump-button").addEventListener('pointerdown', function (e) {
      e.preventDefault();

      if (this.mobilepad.jumpTimeout) {
        clearTimeout(this.mobilepad.jumpTimeout);
      }

      this.mobilepad.doJumpOn();

      this.didJump();
      this.showMobileControls();

      e.stopPropagation();

    }.bind(this));

    document.getElementById("jump-button").addEventListener('pointerout', function (e) {

      if (this.mobilepad.jumpTimeout) {
        clearTimeout(this.mobilepad.jumpTimeout);
      }

      this.mobilepad.doJumpOff();

    }.bind(this));

    document.getElementById("jump-button").addEventListener('pointerup', function (e) {

      if (this.mobilepad.jumpTimeout) {
        clearTimeout(this.mobilepad.jumpTimeout);
      }

      this.mobilepad.doJumpOff();

    }.bind(this));

    document.getElementById("mobile-pad").addEventListener('pointerdown', function (e) {
      this.showMobileControls();
    }.bind(this));

    document.getElementById("mobile-pad").addEventListener('click', function (e) {
      this.showMobileControls();
    }.bind(this));

    // window resize
    window.addEventListener('resize', function (e) { this.onWindowResize(e); }.bind(this));

    // volume mute
    document.getElementById("volume").addEventListener('click', function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.muted = !this.muted;
      if (!this.muted) {
        e.currentTarget.classList.remove("off");

        this.handleUnMute();
      } else {
        e.currentTarget.classList.add("off");

        this.handleMute();
      }


    }.bind(this));

    //---
    // ui buttons
    document.querySelector('.more-info-icon').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.toggleMoreInfoPopup();
    }.bind(this));

    document.getElementById('home-video-container').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.toggleMoreInfoPopup();
    }.bind(this));

    document.getElementById('info-bio-popup-bd').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.toggleMoreInfoPopup();
    }.bind(this));

    document.getElementById('landing-about').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.toggleMoreInfoPopup();
    }.bind(this));


    document.getElementById('tab-button-about').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.ui.showTab("info-bio-popup-cont", 'tab-button-about');
    }.bind(this));

    document.getElementById('tab-button-howto').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.ui.showTab("info-bio-popup-cont-howto", 'tab-button-howto');
    }.bind(this));

    document.getElementById('tab-button-bio').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.ui.showTab("info-bio-popup-cont-bio", 'tab-button-bio');
    }.bind(this));

    document.getElementById('info-bio-popup-close').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      document.getElementById("info-bio-popup").classList.remove("show");
      document.getElementById("info-bio-popup-bd").classList.remove("show");
      if (this.player2) {
        this.player2.pauseVideo();
      }
    }.bind(this));

    document.getElementById('how-to-popup-close').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.dismissHowToPopup();
    }.bind(this));


    document.querySelector('.share-button').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      const title = this.translator.getDynamicText("share-title");
      const text = this.translator.getDynamicText("share-text");
      this.axl.sendMessage(AxL.ChildToHost.SHARE, { title: title, text: text });
      this.handleAxlTrack('share');
    }.bind(this));

    document.getElementById('share-image').addEventListener("click", function (e) {
      e.stopPropagation();
      this.shareArtwork(this.levelName);
      this.handleAxlTrack('share_artwork', this.levelName);
    }.bind(this));

    document.getElementById('level-1-share').addEventListener("click", function (e) {
      e.stopPropagation();
      this.shareArtwork("community");
      this.handleAxlTrack('share_artwork', "community");
    }.bind(this));

    document.getElementById('level-1-download').addEventListener("click", async function (e) {
      e.stopPropagation();
      this.downloadImage("community");
    }.bind(this));

    document.getElementById('level-2-share').addEventListener("click", function (e) {
      e.stopPropagation();
      this.shareArtwork("spirit");
      this.handleAxlTrack('share-artwork', "spirit");
    }.bind(this));

    document.getElementById('level-2-download').addEventListener("click", async function (e) {
      e.stopPropagation();
      this.downloadImage("spirit");
    }.bind(this));

    document.getElementById('download-image').addEventListener("click", function (e) {
      e.stopPropagation();
      this.downloadImage(this.levelName);
      this.handleAxlTrack('download', this.levelName);
    }.bind(this));

    document.getElementById('user-word-bank-button').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.toggleWordBank();
    }.bind(this));

    document.getElementById('zoom-button').addEventListener("click", function () {
      this.toggleUserZoomFromButton();
    }.bind(this));

    document.getElementById('word-bank-close').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.hideWordBank();
    }.bind(this));

    document.getElementById('level-art-show-button').addEventListener("click", function () {
      this.showCollectedImages();
    }.bind(this));

    document.getElementById('minimap-button').addEventListener("click", function () {
      this.toggleMiniMap();
    }.bind(this));

    document.getElementById("continue-image").addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      document.getElementById("ending-image").classList.remove("show");
      document.getElementById("end-level-popup").classList.add("show");
      if (this.levelName == this.endLevelName) {
        document.getElementById("go-to-next-level").style.display = "none";
        document.getElementById("end-level-popup-next-text").style.display = "none";
      }
      this.initEndLevelPopupHighlight();
    }.bind(this));

    document.getElementById("go-to-next-level").addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.clearEndLevelPopupHighlight();
      this.beginResetLevel();
    }.bind(this));

    document.getElementById("go-to-next-level").addEventListener("mouseenter", function () {
      this.updateEndLevelPopupHighlight(0);
    }.bind(this));

    document.getElementById("next-level-after").addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.beginResetLevel();
    }.bind(this));

    document.getElementById("stay-and-play").addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.clearEndLevelPopupHighlight();
      this.handleChooseStayAndPlay();
    }.bind(this));

    document.getElementById("stay-and-play").addEventListener("mouseenter", function () {
      let buttons = this.getEndLevelPopupButtons();
      let idx = buttons.indexOf(document.getElementById("stay-and-play"));
      if (idx !== -1) {
        this.updateEndLevelPopupHighlight(idx);
      }
    }.bind(this));

    document.getElementById("user-helper-hint-button").addEventListener("click", function () {
      this.toggleHelperHint();
    }.bind(this));

    document.getElementById("helper-hints").addEventListener("click", function (e) {
      let contEl = document.getElementById("helper-hints-container");
      if (!contEl.classList.contains("onboarding"))
        this.closeHelperHint();
      else if (contEl.classList.contains("onboarding")) {
        // next
        this.handleNextOnboarding();
      }

    }.bind(this));

    document.getElementById("feedback").addEventListener("click", function (e) {
      this.closeFeedback();

    }.bind(this));


    document.getElementById("user-input-close").addEventListener("click", function (e) {
      this.hideUserInputBubble();
    }.bind(this));

    document.getElementById("collected-images-popup-close").addEventListener("click", function (e) {
      document.getElementById("collected-images-popup").classList.remove("show");
    }.bind(this));

    document.getElementById('helper-hints-prev').addEventListener("click", function (e) {
      this.handlePrevOnboarding();
    }.bind(this));

    document.getElementById('helper-hints-next').addEventListener("click", function (e) {
      this.handleNextOnboarding();
    }.bind(this));

    document.getElementById('rewind-dialog').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.handleReplayDialog();
    }.bind(this));

    document.getElementById('skip-dialog').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.handleSkipDialog();
    }.bind(this));

    document.getElementById('next-dialog').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.playNextDialogPart();
    }.bind(this));

    document.getElementById('prev-dialog').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      //this.handleReplayDialog(;
      this.playPrevDialogPart();
    }.bind(this));

    document.getElementById('pause-play-dialog').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      this.handlePausePlayDialog();
    }.bind(this));

    document.getElementById('go-button').addEventListener("click", function (e) {
      this.handleUserSelectPrompt();
    }.bind(this));

    document.getElementById("user-select-color").addEventListener("change", function (e) {
      document.getElementById("user-select-color").setAttribute("data-color", document.getElementById("user-select-color").value);
    }.bind(this));

    let benchElements = [document.getElementById("user-select-color"), document.getElementById("user-select-instrument"), document.getElementById("go-button")];
    benchElements.forEach((el, idx) => {
      if (el) {
        el.addEventListener("mouseenter", () => {
          if (this.isBenchBubbleOpen()) {
            this.updateBenchBubbleHighlight(idx);
          }
        });
        el.addEventListener("focus", () => {
          if (this.isBenchBubbleOpen()) {
            this.updateBenchBubbleHighlight(idx);
          }
        });
      }
    });

    // ui text load
    await this.translator.load();


    document.getElementById('end-level-pond-popup-close').addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      document.getElementById("end-level-pond-popup").classList.remove("show");
    }.bind(this));


    // tool tips
    if (!this.isMobile()) {
      let tooltips = new ToolTips();
      tooltips.init(this.translator);
    }

    this.setHelperHint("hint-elders", "primary");

    // set how to play text
    let howTo = document.getElementById("how-to-controls");
    let howToPop = document.getElementById("how-to-popup-controls");
    if (this.isMobile()) {
      howTo.innerHTML += this.translator.getDynamicText("controls-mobile-how-to");
      howToPop.innerHTML += this.translator.getDynamicText("controls-mobile-how-to");
      document.getElementById("next-level-after").innerHTML = this.translator.getDynamicText("next-level-after-mobile");
    } else {
      howTo.innerHTML += this.translator.getDynamicText("controls-desktop-how-to-menu");
      howToPop.innerHTML += this.translator.getDynamicText("controls-desktop-how-to");
    }

    let creditEl = document.getElementById("info-bio-popup-credits");
    creditEl.innerHTML += "<b>Models:</b> ";
    attributions["Models"].forEach((item, i) => {
      let a = document.createElement("a");
      a.setAttribute("data-url", item.link);
      a.innerText = item.label;

      creditEl.appendChild(a);

      if (i < attributions["Models"].length - 1)
        creditEl.innerHTML += ", "
    });

    creditEl.innerHTML += " <b>Sounds:</b> ";
    attributions["Sounds"].forEach((item, i) => {
      let a = document.createElement("a");
      a.setAttribute("data-url", item.link);

      a.innerText = item.label;

      creditEl.appendChild(a);


      if (i < attributions["Sounds"].length - 1)
        creditEl.innerHTML += ", "
    });

    let allLinks = document.querySelectorAll("a");
    allLinks.forEach((item, i) => {
      if (item.getAttribute("data-url")) {
        item.addEventListener("click", function () {
          this.axl.sendMessage(AxL.ChildToHost.NAVIGATE, item.getAttribute("data-url"));
        }.bind(this));
      }
    });

    // set default artworks
    this.setArtworkForLevel(this.levelName);
    this.setPlayBenchOptions(this.levelName);
  }

  async initWorld() {

    this.setLoadingTo(50, 3, 2);

    this.soundEffects = new SoundEffects();
    this.soundEffects.init();
    this.soundEffects.fadeInClip("intro", 2000);

    if (FALLBACKMODE == true) {
      return;
    }

    // this.setLoadingTo(60,4);
    let firstWord = this.dialogManager.getGuardianSolution(this.levelName);;//wordToData[item]['prompt'];
    this.musiclm.preloadMusicLMTrack(firstWord, firstWord, this.levelName, firstWord, "keyword");


    await this.initHexFloor();


    // -- make flamingos
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    await this.initFlamingos();
    console.log("Added flamingos.");


    this.mode = "listen"; // needed?

    // create the environment - sky, water, floor pattern, trees etc.
    await this.createInitialEnvironment();
    await this.addTreesToScene();
    console.log("Added environment.");


    this.initWorldDone = true;
    this.setLoadingTo(70, 4);

    // set up physics
    this.world = new CANNON.World({
      gravity: new CANNON.Vec3(0, -9.82, 0), // m/s²
    });
    this.world.solver.iterations = 20;
    this.addPhysicsObjects();
    if (this.levelName == "spirit") {
      await this.addCentralTree();
      this.makeBridge();

      // show restart button
      let reloadEl = document.getElementById("first-level-reload");
      reloadEl.classList.add("prompt-display-item");
      reloadEl.addEventListener("click", function () {
        this.beginResetLevel("community");
        reloadEl.classList.remove("show");
        reloadEl.classList.remove("prompt-display-item");
      }.bind(this));


    }

    let currentInner = document.querySelector(".play-park-inst").innerHTML;
    document.querySelector(".play-park-inst").innerHTML = currentInner + this.translator.getDynamicText("end-level-play-park-extra-" + this.levelName);;

    console.log("Added Physics");
    this.setLoadingTo(80, 4);

    // add benches (do after physics inited)
    let benchTiles = levelConfig[this.levelName]["benches"];
    benchTiles.forEach((item, i) => {
      this.addBench("", 0, item);
    });

    // place elder flamingos now that other world objects in place
    this.setHiddenFlamingos();

    // setup the words
    await this.initWordSoup();

    this.setLoadingTo(90, 4);
    // setup mobile controls
    if (this.isMobile()) {
      this.mobilepad.init(this.myFlamingo, this.physicsCharacter);
      this.mobilepad.on("first_move", function () {
        this.doFirstUserMove();
      }.bind(this));
      this.mobilepad.on("did_move", function () {
        if (this.isZoomed) {
          this.endZoomOut();
          this.isZoomed = false;
        }
      }.bind(this));
      this.mobilepad.on("pointer_down", function () {
        this.isPointerDown = true;
      }.bind(this));
      this.mobilepad.on("pointer_up", function () {
        this.isPointerDown = false;
      }.bind(this));
    }

    //
    if (this.isMuted()) {
      this.soundEffects.mute();
    }

    this.animate();
    console.log("Start animation.");

    this.onWindowResize();

    this.setLoadingTo(100, 1);

    this.isDoorReady = true;
    if (this.isLandingReady) {
      this.openDoors();
    }

    console.log("World Ready.");
    this.onWorldReady();

    // -- preload music from musiclm
    this.preloadTracks();
  }

  async initBeforeLaunch() {
    console.log("initBeforeLaunch");

    // dialogs
    this.dialogManager = new DialogManager();
    await this.dialogManager.load();

    // loaders
    this.textureLoader = new THREE.TextureLoader();
    this.loader = new GLTFLoader();
    this.yinkaColors = new YinkaColor();

    console.log("Inited loaders.");

    // -- three js setup
    this.initThreeJS();


    console.log("Inited three js and hex.");

    // init window events , mose and keyboard
    let threeDiv = document.getElementById("three-scene");
    window.addEventListener('keydown', function (e) { this.onKeyPress(e) }.bind(this));
    window.addEventListener('keyup', function (e) { this.onKeyUp(e) }.bind(this));
    window.addEventListener('blur', function () {
      this.spaceNeedsLift = false;
      this.enterNeedsLift = false;
      if (this.myFlamingo && this.myFlamingo.keyHash) {
        for (let k in this.myFlamingo.keyHash) {
          this.myFlamingo.keyHash[k] = false;
        }
      }
      if (this.physicsCharacter && this.physicsCharacter.keyHash) {
        for (let k in this.physicsCharacter.keyHash) {
          this.physicsCharacter.keyHash[k] = false;
        }
      }
    }.bind(this));
    threeDiv.addEventListener('pointermove', function (e) { this.onPointerMove(e); }.bind(this));
    threeDiv.addEventListener('pointerdown', function (e) { this.onPointerDown(e); }.bind(this));
    window.addEventListener('pointerup', function (e) { this.onPointerUp(e); }.bind(this));
    window.addEventListener('wheel', function (e) { this.onWheel(e); }.bind(this));

    this.finishedBeforeLaunch = true;
    console.log("this.waitingForFirstResume", this.waitingForFirstResume, this.finishedBeforeLaunch);
    if (this.waitingForFirstResume) {
      this.waitingForFirstResume = false;
      console.log("this.waitingForFirstResume do handleAxlFirstResume");
      this.handleAxlFirstResume();
    }
  }

  async initThreeJS() {

    this.camera = new THREE.PerspectiveCamera(45, (window.innerWidth) / window.innerHeight, 0.1, 1000);
    let radius = 80;
    let angle = degrees_to_radians(90 + .5 * 180);
    this.cameraAngle = angle;
    this.cameraDestRadius = 1 + Math.random() * 1.5;
    this.camera.position.set(radius * Math.sin(angle), 80, radius * Math.cos(angle));
    this.camera.lookAt(0, 0, 0);

    // three scene and renderer
    this.scene = new THREE.Scene();
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, logarithmicDepthBuffer: false });


    // only set pixel ration on desktop and ios (no deviceratio on android)
    if (!this.isMobile() || this.isIOS()) {
      this.renderer.setPixelRatio(window.devicePixelRatio);
    }

    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    if (this.isMobile()) {
      this.renderer.shadowMap.type = THREE.PCFShadowMap;
    }

    this.originObj = new THREE.Mesh(new THREE.SphereGeometry(.25, 10, 10), new THREE.MeshBasicMaterial({ color: 0xff0000 }));
    this.originObj.position.set(1.3, 3.0, -1.42);

    // debug stats
    this.stats = Stats();

    // add three renderer to DOM
    document.getElementById("three-scene").appendChild(this.renderer.domElement);


    // orbit controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.minPolarAngle = 0;
    this.controls.maxPolarAngle = Math.PI * .5;//.5;
    this.controls.dampingFactor = 0.05;
    this.controls.maxDistance = 90;


    // clock util
    this.clock = new THREE.Clock();

    // lighting
    const light = new THREE.DirectionalLight(0xececec, .75);//, 100 );
    light.position.set(80, 80, 80);
    light.shadow.camera.top = 30;
    light.shadow.camera.bottom = -30;
    light.shadow.camera.right = 30;
    light.shadow.camera.left = -30;
    light.castShadow = true;
    this.light = light;

    // hemishpere light
    const skyColor = 0xececec;
    const groundColor = 0x333333;
    const hlight = new THREE.HemisphereLight(skyColor, groundColor)
    this.scene.add(hlight);


    if (this.isMobile()) {
      light.shadow.mapSize.set(2048, 2048);
    } else {
      light.shadow.mapSize.set(4096, 4096);
    }

    this.scene.add(light);

    // raycaster
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();

    // objects for camera following
    let geometry = new THREE.SphereGeometry(.05, 10, 10);
    let mat = new THREE.MeshStandardMaterial({ color: 0xff00ff });
    this.posHelperCam = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: 0xff00ff }));
    this.posHelperTarget = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: 0xffff00 }));
    this.posHelperCamB = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: 0xff0000 }));
    this.posHelperTargetB = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: 0x00ff00 }));

    // add mts skyline
    let skyGeo = new THREE.SphereGeometry(100, 30, 30);
    let texture = await this.textureLoad("./textures/mts.png");
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(3, 1);
    texture.needsUpdating = true;
    var material = new THREE.MeshBasicMaterial({
      map: texture, transparent: true, opacity: 1
    });

    let skyline = new THREE.Mesh(skyGeo, material);
    skyline.material.side = THREE.BackSide;
    this.scene.add(skyline);

    console.log("Three js scene ready.");

  }

  async initHexFloor() {
    this.hexFloor = new HexFloor(this.scene);
    let imgMap = null;
    if (levelConfig[this.levelName]['worldmap'] != null) {
      imgMap = await this.textureLoad('./textures/' + levelConfig[this.levelName]['worldmap'] + '.png')
    }
    this.hexFloor.generateFloorData(imgMap, levelConfig[this.levelName]['benches']);
    levelConfig[this.levelName]['benches'].forEach((item, i) => {
      this.hexFloor.floorData[item]["bench"] = 1;
    });
    return true;
  }

  async initFlamingos() {
    // load all flamingos
    await this.onLoadedFlamingoModel();
    // call backs and events
    this.myFlamingo.on("entered_water", function () {
      this.soundEffects.playClip("splash", .5);
      if (this.levelCompleted && !this.playedDream) {
        this.playedDream = true;
        this.returnedToPool();
      } else if (this.levelCompleted && this.didLevelEnd) {

        this.setFeedback("hint-pool-bubbles");
        this.showFeedback(3000);
        if (!this.myFlamingo.prevEnvironment.includes("water"))
          this.jumpsCount = 0;

        this.physicsCharacter.animation = 'dance';

      }
    }.bind(this));

    this.myFlamingo.on("entered_clouds", function (val) {
      if (this.levelCompleted) {
        this.playInParkSong("clouds");
        this.startCloudEffect(val.data);
        this.handleAxlTrack("play_clouds", this.levelName);

        // bird follows
        this.tempVec.copy(this.myFlamingo.group.position);
        this.tempVec.y += .5;
        this.seekTargets[0] = this.tempVec;
      }
    }.bind(this));

    this.myFlamingo.on("enter_bench", function (val) {
      this.enterBench(val.data);
    }.bind(this));

    this.myFlamingo.on("exit_bench", function (val) {
      this.exitBench();
    }.bind(this));

    this.myFlamingo.on("enter_floor", function (val) {

      this.didEnterFloor = true;

      if (this.levelCompleted) {
        this.jumpsCount = 0;
      }
    }.bind(this));

    this.myFlamingo.on("exit_floor", function (val) {

      if (this.levelCompleted && this.helperHintLabel == "hint-floor-dance") {
        this.closeHelperHint();
      }
      this.didEnterFloor = false;
    }.bind(this));

    this.myFlamingo.on("did_eat", function (val) {
      this.physicsCharacter.flyStrength = 35;
      setTimeout(function () {
        this.physicsCharacter.flyStrength = 20;
      }.bind(this), 15000);
    }.bind(this));


    console.log("Flamingos inited.");
    return true;
  }

  async initWordSoup() {
    // init the prompt words in the space
    this.wordSoup = new WordSoup(this.scene);
    this.wordSoup.userColors = this.userColors;
    let paths = this.wordSoup.getModelPaths();
    for (let i = 0; i < paths.length; i++) {
      let path = paths[i].path;
      let letter = paths[i].letter;
      let model = await this.modelLoader(path);
      this.wordSoup.models[letter] = model.scene;
    }

    let levelWords = [];
    for (let i = 0; i < levelConfig[this.levelName]["keywords"].length; i++) {
      if (levelConfig[this.levelName]["keywords"][i]["riddle"] == true) {
        if (levelConfig[this.levelName]["keywords"][i]["riddle"] == true) {
          levelWords.push(levelConfig[this.levelName]["keywords"][i]["keyword"]);
        }
      }

    }
    await this.wordSoup.init(this.levelName, levelWords);

    // events from user flamingo
    this.myFlamingo.on("selected_word", function (val) {
      this.flamingoCaughtAWord();

    }.bind(this));

    // flamingo collided or pointed at word to choose it
    this.wordSoup.on("play_word", function (val) {

      let word = val.data;

      this.addPromptToWordBank(word);

    }.bind(this));

    this.wordSoup.on("selected_word_from_world", function (val) {

      let word = val.data;
      this.handleAxlTrack('collect_word', word);

    }.bind(this));

    this.wordSoup.on("no_longer_has_word", function (val) {
      let tileIndex = val.data;
      this.hexFloor.allTiles[tileIndex].hasWord = false;
      this.stopBlinkTile(tileIndex);
    }.bind(this));

    this.wordSoup.on("update_signpost", function (val) {

      this.updateSignPostSelected(val.data);

    }.bind(this));

    this.wordSoup.on("close_to", function (val) {

      if (!this.myFlamingo.inOpeningSeq) {
        this.colorFlagPost(val.data);
        this.distVec.set(this.myFlamingo.group.position.x, this.flagPosts[val.data].position.y, this.myFlamingo.group.position.z);
        this.flagPosts[val.data].lookAt(this.distVec);
      }

    }.bind(this));

    let disallowedTiles = [];
    if (this.levelName == "spirit") {
      disallowedTiles = [218, 238, 258, , 278, 318, 337, 356, 355, 372, 371, 389, 387, 367, 346, 326, 325, 203, 202, 182, 161, 141, 121, 123, 102, 82, 64, 65, 66, 87, 68, 69, 70, 72, 92, 53, 54, 55, 76, 96, 97, 117, 137, 157, 178, 198];
    }
    this.wordSoup.placeWordsOnTiles(this.hexFloor.allTiles, this.flamingos, levelConfig[this.levelName]["benches"], disallowedTiles);

    this.wordSoup.updateWordHeights(this.hexFloor.floorData);

    await this.initSignPosts();

    return true;
  }

  async initSignPosts() {
    this.flagPosts = [];
    let flagModel = await this.modelLoader('./models/flag2.glb');
    let geo = new THREE.BoxGeometry(.15, 1, .02);
    let mat = new THREE.MeshToonMaterial({ color: new THREE.Color(0xDEBA5C0), side: THREE.DoubleSide });
    let lookAtPt = new THREE.Vector3(0, 0, 0);
    this.wordSoup.wordMeshes.forEach((item, i) => {
      let mesh = flagModel.scene.clone();
      mesh.scale.set(.35, .35, .35);
      mesh.traverse((child) => {
        if (child.isMesh && child.name.includes("00")) {
          child.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0x000000), side: THREE.DoubleSide });
        }
        else {
          child.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0x999999) });
        }

      });

      mesh.position.copy(item.position);
      mesh.quaternion.copy(item.quaternion);
      mesh.position.y += .25;
      lookAtPt.y = mesh.position.y;
      mesh.lookAt(lookAtPt);
      this.scene.add(mesh);
      this.flagPosts.push(mesh);

    });
    return true;
  }

  // preload music tracks so ready for interactions
  async preloadTracks() {

    let words = [];
    let questions = this.dialogManager.getSolutions(this.levelName);
    questions.forEach((item, i) => {
      words.push(item['solution']);
    });


    let prefix = "";
    if (levelConfig[this.levelName].hasOwnProperty('mlmprefix')) {
      prefix = levelConfig[this.levelName]['mlmprefix'];
    }

    let firstWord = this.dialogManager.getGuardianSolution(this.levelName);
    let prompts = [];
    for (let i = 0; i < words.length; i++) {
      let item = words[i];
      prompts.push(item);
      let query = prefix + item;
      if (firstWord != item || this.levelName == this.endLevelName) {
        this.musiclm.preloadMusicLMTrack(query, item, this.levelName, item, "keyword");
        await new Promise(r => setTimeout(r, 3000));
      } else {
        console.log("skip ", item);
      }

    }


    let query = prefix + prompts.join(" ");
    this.musiclm.preloadMusicLMTrack(query, "final", this.levelName, "final", "final");
    await new Promise(r => setTimeout(r, 3000));

    if (levelConfig[this.levelName].hasOwnProperty("treeprefix")) {
      let treePrompt = levelConfig[this.levelName]["treeprefix"];
      this.musiclm.preloadMusicLMTrack(treePrompt, "trees", this.levelName, "treeprefix");
      await new Promise(r => setTimeout(r, 3000));
    }

    if (levelConfig[this.levelName].hasOwnProperty("centertreeprefix")) {
      let treePrompt = levelConfig[this.levelName]["centertreeprefix"];
      this.musiclm.preloadMusicLMTrack(treePrompt, "centertree", this.levelName, "centertreeprefix");
      await new Promise(r => setTimeout(r, 3000));
    }

    if (levelConfig[this.levelName].hasOwnProperty("pondprefix")) {
      let nPrompt = levelConfig[this.levelName]["pondprefix"];
      this.musiclm.preloadMusicLMTrack(nPrompt, "pond", this.levelName, "pondprefix");
      await new Promise(r => setTimeout(r, 3000));
    }

    if (levelConfig[this.levelName].hasOwnProperty("floorprefix")) {
      let nPrompt = levelConfig[this.levelName]["floorprefix"];
      this.musiclm.preloadMusicLMTrack(nPrompt, "floor", this.levelName, "floorprefix");
      await new Promise(r => setTimeout(r, 3000));
    }

    if (levelConfig[this.levelName].hasOwnProperty("cloudsprefix")) {
      let nPrompt = levelConfig[this.levelName]["cloudsprefix"];
      this.musiclm.preloadMusicLMTrack(nPrompt, "clouds", this.levelName, "cloudsprefix");
      await new Promise(r => setTimeout(r, 3000));
    }

    if (levelConfig[this.levelName].hasOwnProperty("bridgeprefix")) {
      let nPrompt = levelConfig[this.levelName]["bridgeprefix"];
      this.musiclm.preloadMusicLMTrack(nPrompt, "bridge", this.levelName, "bridgeprefix");
      await new Promise(r => setTimeout(r, 3000));
    }

    if (levelConfig[this.levelName].hasOwnProperty("leafprefix")) {
      let nPrompt = levelConfig[this.levelName]["leafprefix"];
      this.musiclm.preloadMusicLMTrack(nPrompt, "leaf", this.levelName, "leafprefix");
      await new Promise(r => setTimeout(r, 3000));
    }


  }

  setPlayBenchOptions(levelName) {
    let parent = document.getElementById("user-select-instrument");
    parent.innerHTML = "";
    for (const item in levelConfig[levelName]["dream-prompts"]["instruments"]) {
      let option = document.createElement("option");
      option.value = item;
      option.innerText = item.charAt(0).toUpperCase() + item.slice(1);
      parent.appendChild(option);
    }

    parent = document.getElementById("user-select-color");
    parent.innerHTML = "";


    let colors = Object.keys(levelConfig[levelName]["dream-prompts"]["colors"]);//["Blue","Purple","Red","Orange","Yellow","Green","Pink"];
    colors.forEach((item, i) => {
      let option = document.createElement("option");
      option.value = colors[i].toLowerCase();
      option.innerText = colors[i].charAt(0).toUpperCase() + colors[i].slice(1);
      parent.appendChild(option);
    });


  }

  setArtworkForLevel(levelName) {
    let index = 0;
    let el = document.getElementById("ending-image");
    let path = this.getArtworkPathForLevel(levelName);
    el.style.backgroundImage = "url('" + path + "')";
  }


  //-----------------------
  // User presses "Enter" on red doors
  handleStartButtonClicked(e) {
    if (this.gameStarted) return;
    this.gameStarted = true;
    this.didFirstStart = true;

    let landingEnter = document.getElementById("landing-enter");
    if (landingEnter) {
      landingEnter.classList.add("hide");
      landingEnter.disabled = true;
      landingEnter.blur();
    }
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    document.querySelector("#landing-door").classList.add("zoom");

    this.soundEffects.fadeClip("intro", 2000);
    this.soundEffects.playClip("birds", .5);
    this.musiclm.initAudioContext();

    // document.getElementById("landing-about").classList.add("hide");
    document.getElementById("landing-title-and-credits").classList.remove("centered");

    let el = document.getElementById("landing-door");
    el.style.pointerEvents = "none";
    el.classList.add("hidden");
    el.parentNode.classList.add("hidden");

    document.getElementById("volume").classList.add("show");

    //animate out flamingo head
    if (this.flamingoHeadTL) {
      this.flamingoHeadTL.kill();
      let flamingoHeadEl = document.querySelector(".flamingo-head");
      gsap.to(flamingoHeadEl, {
        left: "60%", bottom: "-25%", rotate: 0, duration: .25, onComplete: function () {
          flamingoHeadEl.style.opacity = 0;
        }
      });

    }

    if (this.isDoorReady) {

      this.openDoors();
    }

  }


  //----------------------- ui landing transitions and loading animation
  setLoadingTo(pct, duration, delay = 0) {

    if ((this.loadingPct.val > pct && this.loadingPct.val >= 100) || this.loadingGoal > pct) {
      console.log(this.loadingPct.val, pct);
      document.getElementById("landing-loading").classList.remove("show");
      if (!this.didFirstStart) {
        document.getElementById("landing-enter").classList.remove("hide");
        // document.getElementById("landing-about").classList.remove("hide");
      }
      return;
    }

    this.loadingGoal = pct;

    if (this.loadingTween) {
      this.loadingTween.kill();
    }

    this.loadingTween = gsap.to(this.loadingPct, {
      val: pct, duration: duration, delay: delay, overwrite: true,
      onUpdate:
        function () {
          document.getElementById("loading-pct").innerText = this.loadingPct.val.toFixed(0) + "%";
        }.bind(this),
      onComplete:
        function () {
          if (this.loadingPct.val < 100) {
            gsap.to(this.loadingPct, {
              val: 99, duration: 200, delay: delay + duration, onUpdate: function () {
                document.getElementById("loading-pct").innerText = this.loadingPct.val.toFixed(0) + "%";
              }.bind(this)
            });
          } else {
            document.getElementById("landing-loading").classList.remove("show");
            if (!this.didFirstStart)
              document.getElementById("landing-enter").classList.remove("hide");
            document.getElementById("landing-about").classList.remove("hide");
          }
        }.bind(this)
    });


  }

  openDoors() {
    if (this.didOpenDoors) return;
    this.didOpenDoors = true;

    console.log("Start Camera Animation");

    const video = document.getElementById('home-video');
    video.pause();

    let vector2 = new THREE.Vector3(0, 0, 1);
    vector2.applyQuaternion(this.camera.quaternion);
    this.posHelperCam.position.copy(this.controls.target);
    this.posHelperCam.translateOnAxis(vector2, 5);
    this.posHelperCam.position.y = this.myFlamingo.group.position.y + 1;

    // setup camera animation of flamingo flying down
    let camAnimDuration = 20;
    let flamingoAnimDuration = camAnimDuration - .75;
    let userDestPos = { x: 0, y: 0, z: 0 };

    let cameraDestPos = { x: 0, y: 1, z: -2.25 };
    if (this.levelName == "spirit") {
      userDestPos.z = -2;
      cameraDestPos.z = -3.25;
    }
    this.animateCameraTo({ x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z }, { x: 0, y: .25, z: 0 }, 28, 0, 0)
    gsap.to(this.myFlamingo.group.position, {
      x: userDestPos.x, y: userDestPos.y, z: userDestPos.z, duration: 27.25, delay: 0, onComplete: function () {
        this.myFlamingo.inOpeningSeq = false;
        this.myFlamingo.animationName = "";
        this.myFlamingo.setAction(null);
        this.physicsCharacter.body.position.copy(this.myFlamingo.group.position);
        this.physicsCharacter.body.quaternion.copy(this.myFlamingo.group.quaternion);
        this.readyToMove = true;
        if (this.isMobile()) {
          this.showMobileControls();
        }
      }.bind(this)
    });

    this.physicsCharacter.body.position.set(userDestPos.x, userDestPos.y, userDestPos.z);

    document.getElementById("landing-title").classList.remove("hide");

    setTimeout(function () {
      this.playBubbleOnBoarding();
    }.bind(this), 1000);

    setTimeout(function () {
      document.getElementById("landing-title").classList.add("hide");
      document.getElementById("landing-yinka").classList.add("animate-out");
    }.bind(this), 8000);

    this.ui.showPage("main-world");
    this.ui.pauseWorldLoadingAnimation();
    this.ui.openDoors();

    let uiToHide = document.querySelectorAll(".hide-on-open");
    uiToHide.forEach((item, i) => {
      item.classList.remove("show");
    });

    setTimeout(function () {
      document.getElementById("landing-door").style.opacity = 0;
    }.bind(this), 2000);
  }

  setCameraAndFlamingoForFlyDown() {

    this.controls.maxDistance = 90;
    let radius = 80;
    let angle = degrees_to_radians(90 + .5 * 180);
    this.camera.position.set(radius * Math.sin(angle), 50, radius * Math.cos(angle));
    this.camera.lookAt(0, 0, 0);
    this.followCam = false;

    let temp = new THREE.Object3D();
    temp.position.copy(this.camera.position);
    temp.rotation.copy(this.camera.rotation);
    temp.updateMatrix();
    temp.translateZ(-2.5);
    temp.translateY(-1);

    this.myFlamingo.group.position.copy(temp.position);
    this.myFlamingo.group.lookAt(0, this.myFlamingo.group.position.y, 0);
    this.myFlamingo.setAction(this.myFlamingo.animationActions['fly'], 'fly');

    let vector2 = new THREE.Vector3(0, 0, 1);
    vector2.applyQuaternion(this.camera.quaternion);
    this.posHelperCam.position.copy(this.controls.target);
    this.posHelperCam.translateOnAxis(vector2, 5);
    this.posHelperCam.position.y = this.myFlamingo.group.position.y + 1;
    this.myFlamingo.inOpeningSeq = true;
    this.myFlamingo.mode = "flying";

    let camAnimDuration = 20;
    let flamingoAnimDuration = camAnimDuration - .75;
    let userDestPos = { x: 0, y: 0, z: -2 };
    let cameraDestPos = { x: 0, y: 1, z: -3.25 };
    //  animateCameraTo( position, lookTarget, durationCam, durationTarget, delay = 0, state = null){

    // this.animateCameraTo({x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z},{x:0,y:.25,z:0},28,0,0);
    console.log("this.camera.position", this.camera.position);
    gsap.to(this.camera.position, {
      x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z, duration: 15, overwrite: true, delay: 0, onComplete: function () {
        this.controlsState = "default";
        this.controls.enabled = true;
      }.bind(this)
    });

    let lookTarget = { x: 0, y: .25, z: 0 };
    gsap.to(this.controls.target, { x: lookTarget.x, y: lookTarget.y, z: lookTarget.z, duration: 15, overwrite: true, delay: 0 });

    gsap.to(this.myFlamingo.group.position, {
      x: userDestPos.x, y: userDestPos.y, z: userDestPos.z, duration: 14.5, delay: 0, onComplete: function () {
        this.myFlamingo.inOpeningSeq = false;
        this.myFlamingo.animationName = "";
        this.myFlamingo.setAction(null);
        this.physicsCharacter.body.position.copy(this.myFlamingo.group.position);
        this.physicsCharacter.body.quaternion.copy(this.myFlamingo.group.quaternion);
        this.readyToMove = true;
        this.controls.maxDistance = 60;
        this.followCam = true;
        this.showOnboardingControls();

        if (this.isMobile()) {
          this.showMobileControls();
        }
      }.bind(this)
    });

    this.physicsCharacter.body.position.set(userDestPos.x, userDestPos.y, userDestPos.z);

  }


  //----------------------- post init loading
  // post loading, everything after game begins
  async onWorldReady() {

    // birds
    const glbBird = await this.modelLoader('./models/flying_bird.glb');
    let totalBirds = (this.isMobile()) ? 5 : 6;
    this.birds = new Birds(this.scene, glbBird, totalBirds);
    await this.birds.init();
    console.log("Birds inited.");

    this.lazyLoadFlamingoIdles();

    await this.preloadRugTextures();

    this.makeDownloadableImage(this.levelName);

    let bubble = await this.textureLoad("./textures/bubble2.png");
    this.bubbles = new ParticleSystem(this.scene, bubble);
    this.bubbles.init(50, this.myFlamingo.tempVec.set(0, 0, 0));

    this.instancedMeshPlants();


    console.log("Post loading done.");
  }

  updateSignPostSelected(index) {
    let mesh = this.flagPosts[index];
    mesh.visible = false;
  }


  //-----------------------  Flamingos: loading and interactions
  async onLoadedFlamingoModel() {

    this.flamingos = [];


    let r = 1.5;
    let total = this.maxFlamingos;
    let angle = degrees_to_radians((360 / total));

    // load animations
    let animations = {};

    const gltfWalk = await this.modelLoader('./models/Flamingo_Walk_1_2_V2.gltf');
    animations['walk'] = gltfWalk.animations[0];

    const gltfJump = await this.modelLoader('./models/Flamingo_jump_V2_2.gltf');
    animations['jump'] = gltfJump.animations[0];

    const gltfFly = await this.modelLoader('./models/Flamingo_Fly_1_V2.gltf');
    animations['fly'] = gltfFly.animations[0];

    const gltfIdle = await this.modelLoader('./models/Wait_3.gltf');//Flamingo_Wait.gltf');//Wait_3.gltf');
    animations['idle'] = gltfIdle.animations[0];

    // create all f
    for (let i = 0; i < total; i++) {

      const gltf = await this.modelLoader('./models/Flamingo_Dance.gltf');
      animations['dance'] = gltf.animations[0];

      const f = gltf.scene;//clone(gltf.scene);

      f.rotateY(degrees_to_radians(180));

      f.traverse((item) => {

        item.castShadow = true;

        // if(item.isMesh){
        //   this.clickableObjects.push(item);
        // }

      });


      let newFlamingo = new Flamingo(this.scene);

      if (i == 0) {

        newFlamingo.makeFlamingo(f, true, 0xee6e37, gltf, animations);
        newFlamingo.group.name = "me";
        newFlamingo.makeBaby();
        newFlamingo.isMe = true;
        this.flamingo = f;
        this.myFlamingo = newFlamingo;

        let temp = new THREE.Object3D();
        temp.position.copy(this.camera.position);
        temp.rotation.copy(this.camera.rotation);
        temp.updateMatrix();
        temp.translateZ(-2.5);
        temp.translateY(-1);

        this.myFlamingo.group.position.copy(temp.position);
        this.myFlamingo.group.lookAt(0, this.myFlamingo.group.position.y, 0);//.copy(temp.quaternion);
        newFlamingo.group.opos = new THREE.Vector3().copy(this.myFlamingo.group.position);

        this.myFlamingo.on("splash", function (val) {
          this.soundEffects.playClip("splash", val.data);
        }.bind(this));

        // this.myFlamingo.on("started_dance", function(val){
        //   //this.soundEffects.playClip("calls", .25);
        // }.bind(this));

        // this.myFlamingo.on("stopped_dance", function(val){
        //   //this.soundEffects.pauseClip("calls");
        // }.bind(this));

        this.myFlamingo.on("exit_tile", async function (val) {
          let tileIndex = val.data;
          let tile = this.hexFloor.allTiles[tileIndex];
          if (tile.hasElder) {
            this.exitElderConversation(this.currentFlamingoChatIndex);
          }

        }.bind(this));

        this.myFlamingo.on("on_tile", async function (val) {


          let tile = this.hexFloor.allTiles[val.data];

          if (tile.hasElder && tile.hasOwnProperty("blinkTL")) {
            tile.blinkTL.pause();
          }

          // add plants
          if ((this.levelCompleted && !tile.hasBench)) {
            if (this.levelName == "community") {
              let center_tiles = [228, 208, 188, 189, 170, 171, 192, 212, 232, 231, 250];
              if (center_tiles.includes(tile.tileIndex)) {
                this.updateBadge("tones");
                let toneId = center_tiles.indexOf(tile.tileIndex);
                this.soundEffects.playClip("c-tone-" + toneId);
                this.highlightTileOnTone(tile);
              } else {
                for (let i = 0; i < 2; i++)
                  this.addNewFernPlant(tile, .25 + i * .1);
              }
            } else {
              for (let i = 0; i < 2; i++)
                this.addNewFernPlant(tile, .25 + i * .1);
            }
          }

          // words
          let hasTileIndex = null;
          if (tile.hasWord == true) {
            hasTileIndex = tile.tileIndex;
            this.wordSoup.updateTileHasWord(hasTileIndex, this.myFlamingo.group.position);

            // select word when enter tile
            this.flamingoCaughtAWord();
          }

          // does it have another flamingo
          if (tile.hasElder) {
            this.enterElderConversation(tile.elderIndex);
          }

          if (tile.hasBench && this.levelCompleted) {

            let pos = this.benches[tile.benchIndex].position;
            this.onBenchPos = new THREE.Vector3(pos.x, pos.y + 1, pos.z);

          }

        }.bind(this));



      } else {
        let pinks = [0xFB89CB, 0xFC67C8, 0xFD4DC6, 0xFF1AC1, 0xFF1AC1];
        let color = pinks[i - 1];
        newFlamingo.makeFlamingo(f, false, color, gltf, animations);
        newFlamingo.group.position.set(0, 1000, 0);
        newFlamingo.group.opos = new THREE.Vector3().copy(newFlamingo.group.position);
        newFlamingo.makeGray();

      }

      this.flamingos.push(newFlamingo);
    }

    return true;
  }

  async lazyLoadFlamingoIdles() {
    for (let i = 1; i < this.maxFlamingos; i++) {
      let path = "./models/Wait_" + (i + 1) + ".gltf";
      const gltfIdle = await this.modelLoader(path);
      this.flamingos[i].animationActions['idle'] = this.flamingos[i].mixer.clipAction(gltfIdle.animations[0]);
      this.flamingos[i].setAction(this.flamingos[i].animationActions['idle'], 'idle');
    }
  }

  //-----------------------  Level Reset
  beginResetLevel(levelname = "spirit") {

    this.isResetting = true;

    // set url to reload with this level
    this.axl.sendMessage(AxL.ChildToHost.SET_URL_PARAMS, { level: levelname });

    // kill music
    this.musiclm.fadeOutSource();

    // ui
    document.getElementById("ending-image").classList.remove("show");
    document.getElementById("ending-level-ui").classList.remove("show");
    document.getElementById("next-level-after").classList.remove("show");
    document.getElementById("end-level-popup").classList.remove("show");
    document.getElementById("collected-images-popup").classList.remove("show");
    this.hideUserInputBubble();
    this.closeMiniMap();
    this.ui.closeDoors();
    this.spaceNeedsLift = false;
    this.enterNeedsLift = false;
    let promptItems = document.querySelectorAll(".prompt-display-item");
    promptItems.forEach((item, i) => {
      item.classList.add("show");
    });

    setTimeout(function () {
      this.resetLevel(levelname);
      try {
        this.musiclm.source.stop();
      } catch (e) {
        console.log("Error on stop source.");
      }
    }.bind(this), 1000);

    setTimeout(function () {
      document.getElementById("landing-loading").classList.add("show");
      document.querySelector(".yellow-layer").classList.remove("window-item");
    }.bind(this), 2000);

  }

  async resetLevel(levelName) {

    this.myFlamingo.inOpeningSeq = true;
    this.handleAxlTrack('new_level', this.levelName);

    this.didEndLevelPond = false;


    // ui
    this.loadingPct = 0;
    this.setLoadingTo(70, 4);
    document.getElementById("landing-middle-content").classList.remove("start-item");
    document.getElementById("landing-loading").classList.add("show");

    if (levelName == this.endLevelName) {
      document.getElementById("go-to-next-level").style.display = "none";
      document.getElementById("end-level-popup-cont").classList.remove("hide");
      document.getElementById("end-level-popup-play-park-info").classList.remove("hide");
    }

    let wbExplain = this.translator.getDynamicText("word-bank-explain");
    document.getElementById("word-bank-explain").innerText = wbExplain;
    document.getElementById("word-bank-explain").classList.add("hide");

    // set bench options ui
    this.setPlayBenchOptions(levelName);


    // turn off blinking tiles
    this.didBlinkFlags = false;
    if (this.blinkTLs) {
      this.blinkTLs.forEach((item, i) => {
        if (item != null)
          item.kill();
      });
      this.blinkTLs = [];
    }


    // remove bridge if go to level 1
    if (levelName == "community") {
      this.removeBridge();
    }

    this.resetLevelConfigs(levelName);

    // create artwork for this level
    this.makeDownloadableImage(levelName);

    // turn off some effects from play in park
    this.endCloudEffect();
    if (this.bubbles)
      this.bubbles.clearBubbles();

    // reset hex colors and rug tiles
    await this.resetFloorPattern();
    this.preloadRugTextures();
    this.hexFloor.resetClearFloor();
    let imgMap = null;
    if (levelConfig[this.levelName]['worldmap'] != null) {
      imgMap = await this.textureLoad('./textures/' + levelConfig[this.levelName]['worldmap'] + '.png')
    }
    this.hexFloor.generateFloorData(imgMap, levelConfig[this.levelName]['benches']);
    await this.hexFloor.makeFloor(this.instrument);
    this.clickableObjects.push(this.hexFloor.instanceMesh);
    this.resetTilePhysics();

    // remove plants
    if (this.plantMesh)
      this.plantMesh.count = 0;

    // reset trees
    this.pineTrees.forEach((item, i) => {
      this.scene.remove(item);
    });
    this.pineTrees = [];
    await this.addTreesToScene();
    this.resetTreePhysics();

    // make level  special items or remove
    if (this.levelName == "spirit") {
      await this.addCentralTree();
      this.makeBridge();
      document.getElementById("user-select-color").setAttribute("data-color", "purple");
    } else {

      document.querySelectorAll('.pp-extras').forEach(e => e.remove());

      // check if need to remove central tree
      if (this.centralTreeBody) {
        this.world.removeBody(this.centralTreeBody);
      }
    }

    let currentInner = document.querySelector(".play-park-inst").innerHTML;
    document.querySelector(".play-park-inst").innerHTML = currentInner + this.translator.getDynamicText("end-level-play-park-extra-" + this.levelName);;

    // reset flamingo positions
    this.setHiddenFlamingos();
    for (let i = 1; i < this.flamingos.length; i++) {
      this.flamingos[i].resetNpc();
    }

    // start preloading new music tracks
    this.preloadTracks();

    // reset words in world
    let levelWords = [];
    if (levelConfig[this.levelName].hasOwnProperty('solutions')) {
      levelWords = levelConfig[this.levelName]['solutions'];
    } else {
      for (let i = 0; i < levelConfig[this.levelName]["keywords"].length; i++) {
        if (levelConfig[this.levelName]["keywords"][i]["riddle"] == true)
          levelWords.push(levelConfig[this.levelName]["keywords"][i]["keyword"]);
      }
    }

    await this.wordSoup.init(this.levelName, levelWords);

    let disallowedTiles = [];
    if (this.levelName == "spirit") {
      disallowedTiles = [218, 238, 258, , 278, 318, 337, 356, 355, 372, 371, 389, 387, 367, 346, 326, 325, 203, 202, 182, 161, 141, 121, 123, 102, 82, 64, 65, 66, 87, 68, 69, 70, 72, 92, 53, 54, 55, 76, 96, 97, 117, 137, 157, 178, 198];
    }
    this.wordSoup.placeWordsOnTiles(this.hexFloor.allTiles, this.flamingos, levelConfig[this.levelName]["benches"], disallowedTiles);

    // reset flags
    this.resetSignPosts();

    // reset word bank
    this.solvedWords = [];
    this.promptWordBank = [];
    document.getElementById("word-bank-placeholder").classList.remove("hide");
    document.getElementById("word-bank-list").innerHTML = "";
    let badge = document.querySelector(".user-word-bank-button .badge");
    badge.innerText = this.promptWordBank.length;

    // reset benches
    this.resetBenches();

    // set artwork
    this.setArtworkForLevel(this.levelName);

    // set helper hint
    // this.setHelperHint("level-2-start","primary");
    this.setFeedback("level-2-start");

    this.setLoadingTo(100, 1);

    // set character position and camera
    setTimeout(function () {
      document.querySelector(".yellow-layer").classList.add("window-item");
      this.setCameraAndFlamingoForFlyDown();
    }.bind(this), 1000);

    setTimeout(function () {
      this.ui.openDoors();
      // this.showHelperHint();
      this.showFeedback();
      if (this.isMobile()) {
        this.showMobileControls();
      }


      setTimeout(function () {
        document.getElementById("landing-door").style.opacity = 0;
      }.bind(this), 2000);

    }.bind(this), 3000);


    this.isResetting = false;
  }

  resetLevelConfigs(levelName) {
    this.levelName = levelName;
    this.levelCompleted = false;
    this.allEldersFound = [];
    this.eldersFound = 0;
    this.instrument = levelConfig[levelName]["instrument"];
    this.playedDream = false;
    this.rugCount = 0;
    this.loadingPct = { val: 0 };
    this.didLevelEnd = false;

  }

  resetTilesColors() {

    this.hexFloor.resetTilesFinalColors(this.instrument);
    this.hexFloor.resetTilesToGrayscale();
  }

  async resetFloorPattern() {

    // load initial floor pattern texture
    let path = "./textures/" + floorTextureData[this.instrument].path + "/" + floorTextureData[this.instrument].path + "_";
    let thisPath = path + "0.png";

    let texture = await this.textureLoad(thisPath);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(12, 12 * floorTextureData[this.instrument].ratio);
    texture.needsUpdating = true;

    // change floor texture
    this.floorCircle.isMoving = false;

    this.floorTextureRepeat = 12;
    texture.repeat.set(this.floorTextureRepeat, this.floorTextureRepeat * floorTextureData[this.instrument].ratio);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.floorTexture = texture;
    this.floorTexture.needsUpdating = true;
    this.floorCircle.material.map = this.floorTexture;
    this.floorCircle.material.color = new THREE.Color(0xffffff);
    this.floorCircle.needsUpdating = true;
    this.floorCircle.receiveShadow = true;
    this.floorCircle.material.transparent = false;
    // gsap.to(this.floorCircle.material,{opacity:1,duration:1,onComplete:function(){
    //   this.floorCircle.material.transparent = false;
    // }.bind(this)});
  }

  resetTilePhysics() {
    this.hexFloor.allTiles.forEach((hex, i) => {
      this.hexBodies[i].position.y = -50 + (hex.scale.y / 2);
      if (hex.scale.y <= 0)
        this.hexBodies[i].position.y = -1000;
    });
  }

  resetTreePhysics() {
    this.treeBodies.forEach((item, i) => {
      if (this.pineTrees.length > i) {
        item.position.copy(this.pineTrees[i].position);
        item.position.y += 2;
      }
    });

  }

  resetBenches() {
    //

    let desiredIndexes = levelConfig[this.levelName]["benches"];

    let tiles = this.hexFloor.allTiles.filter((item) => {
      return (item.len > 10 && !item.hasTree && !item.hasWord && !item.hasElder && item.usable && !desiredIndexes.includes(item.tileIndex));
    });

    tiles.sort((a, b) => 0.5 - Math.random());

    desiredIndexes.forEach((index, i) => {
      let item = this.hexFloor.allTiles[index];
      if (!item.hasTree && !item.hasWord && !item.hasElder && item.usable) {
        tiles.unshift(item);
      } else {
      }
    });

    this.benches.forEach((item, i) => {
      this.stopBlinkBench(item);
      item.position.copy(tiles[i].position);
      item.position.y = tiles[i].scale.y * .5;
      item.tileIndex = tiles[i].tileIndex;
      tiles[i].hasBench = true;
      tiles[i].benchIndex = i;
      item.material.color = new THREE.Color(0x999999);

      const size = new THREE.Vector3(.365, .275, .475);

      this.benchBodies[i].position.copy(tiles[i].position);
      this.benchBodies[i].position.y += tiles[i].scale.y * .5 + size.y;

      item.traverse((mesh) => {
        if (mesh.isMesh)
          mesh.material.color = new THREE.Color(0x999999);
      });


      item.rotation.y -= (Math.PI);//Math.atan2(-pos.z,-pos.x) + (Math.PI/2);
      item.lookAt(new THREE.Vector3(0, item.position.y, 0));
      item.rotation.y += (Math.PI);
      this.benchBodies[i].quaternion.copy(item.quaternion);

    });

  }

  resetSignPosts() {
    let lookAtPt = new THREE.Vector3();
    this.wordSoup.wordMeshes.forEach((item, i) => {
      let mesh = this.flagPosts[i];
      mesh.traverse((child) => {
        if (child.isMesh && child.name.includes("00")) {
          child.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0x000000), side: THREE.DoubleSide });

        } else {
          child.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0xDEBA5C0) });
          // child.castShadow = true;
        }
      });

      mesh.position.copy(item.opos);
      mesh.quaternion.copy(item.quaternion);
      mesh.visible = true;
      //mesh.position.y += .25;
      lookAtPt.y = mesh.position.y;
      mesh.lookAt(lookAtPt);
    });
  }


  //-----------------------  onboarding and opening titles
  playBubbleOnBoarding() {
    if (this.bubbleIntroInterval) {
      clearInterval(this.bubbleIntroInterval);
      this.bubbleIntroInterval = null;
    }
    if (this.onboardBTMs) {
      this.onboardBTMs.forEach((item) => clearTimeout(item));
    }
    this.onboardBTMs = [];
    this.onBoardingCount = 0;
    document.getElementById("helper-hints-container").classList.add("onboarding");
    this.setSkipTutorial();
    this.playNextBubbleOnBoarding();

    this.bubbleIntroInterval = setInterval(function () {
      this.playNextBubbleOnBoarding();
    }.bind(this), 10000);

  }

  playNextBubbleOnBoarding(timer = 500) {

    let maxOnboarding = 4;

    if (this.onBoardingCount >= maxOnboarding) {
      clearInterval(this.bubbleIntroInterval);
      this.bubbleIntroInterval = null;
      this.endTutorial();
      return;
    }

    if (this.onBoardingCount >= 1) {
      document.getElementById("helper-hints-prev").classList.add("active");
    } else {
      document.getElementById("helper-hints-prev").classList.remove("active");
    }


    let textId = "onboarding-bubble-" + this.onBoardingCount;
    let el = document.getElementById("helper-hints");
    el.classList.add("onboarding-out");

    let tm = setTimeout(function () {
      this.setHelperHint(textId);
      el.classList.remove("onboarding-out");
      this.showHelperHint();
    }.bind(this), timer);
    this.onboardBTMs.push(tm);
    this.onBoardingCount++;




  }

  handlePrevOnboarding() {
    clearInterval(this.bubbleIntroInterval);
    this.bubbleIntroInterval = null;
    if (this.onBoardingCount >= 2) {
      this.onBoardingCount -= 2;
      this.playNextBubbleOnBoarding(500);
    }

  }

  handleNextOnboarding() {
    clearInterval(this.bubbleIntroInterval);
    this.bubbleIntroInterval = null;
    this.playNextBubbleOnBoarding(500);
  }

  setSkipTutorial() {
    let skipEl = document.getElementById("skip-tutorial");
    if (!this.didSetSkipTutorial) {
      this.didSetSkipTutorial = true;
      skipEl.addEventListener("click", function (e) {
        if (e && e.currentTarget) e.currentTarget.blur();
        this.endTutorial();
      }.bind(this));
    }

    skipEl.classList.add("show");
  }

  showOnboardingControls() {
    this.closeHelperHint();
    if (this.onboardBTMs) {
      this.onboardBTMs.forEach((item, i) => {
        clearTimeout(item);
      });
    }

    let el = document.getElementById("helper-hints");
    el.classList.remove("onboarding-out");

    this.setHelperHint("hint-first");

    document.getElementById("helper-hints-container").classList.remove("onboarding");
    setTimeout(function () {
      document.getElementById("how-to-popup").classList.add("show");
      document.getElementById("how-to-popup").focus();
    }.bind(this), 500);

  }

  endTutorial() {
    if (this.didFinishOnboarding) return;
    this.didFinishOnboarding = true;

    if (this.bubbleIntroInterval) {
      clearInterval(this.bubbleIntroInterval);
      this.bubbleIntroInterval = null;
    }

    if (this.onboardingMode != "info") {

      document.getElementById("landing-title").style.display = "none";
      document.getElementById("landing-yinka").style.display = "none";

      clearInterval(this.bubbleIntroInterval);
      this.showOnboardingControls();

      let userDestPos = { x: this.cameraDestRadius * Math.sin(this.cameraAngle), y: 0, z: this.cameraDestRadius * Math.cos(this.cameraAngle) };
      let cameraDestPos = { x: (this.cameraDestRadius + 2) * Math.sin(this.cameraAngle), y: 1, z: (this.cameraDestRadius + 2) * Math.cos(this.cameraAngle) };

      this.animateCameraTo({ x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z }, { x: this.destinationX, y: .25, z: 0 }, 0, 0, 0)
      this.controls.enabled = true;

      gsap.to(this.myFlamingo.group.position, { x: userDestPos.x, y: userDestPos.y, z: userDestPos.z, duration: 0, overwrite: true });
      this.myFlamingo.inOpeningSeq = false;
      this.myFlamingo.animationName = "";
      this.myFlamingo.setAction(null);
      //this.didFirstMove = true;
      this.myFlamingo.mode = "moving";
      this.readyToMove = true;
      this.physicsCharacter.body.position.copy(this.myFlamingo.group.position);
      this.physicsCharacter.body.quaternion.copy(this.myFlamingo.group.quaternion);

      this.controls.maxDistance = 12;

      document.querySelector(".more-info-icon").classList.add("show");
      document.querySelector(".share-button").classList.add("show");

      this.handleAxlTrack('game_start');
      this.startGameTime = Date.now();

      // blink elder guardian
      let tileIndex = this.guardianTile;
      let tile = this.hexFloor.allTiles[tileIndex];
      tile.prevColor = new THREE.Color().copy(tile.material.color);
      let prevColor = new THREE.Color(0x76c4f7);//new THREE.Color().copy(tile.material.color);
      let realColor = new THREE.Color(0x76c4f7);//new THREE.Color().copy(tile.material.color);
      let highlightColor = new THREE.Color(0xE65EBD);

      let tl = this.hexFloor.blinkTile(tileIndex, realColor, highlightColor, prevColor);
      tile.blinkTL = tl;
      this.guardianBlink = tl;
    }

    if (this.onboardTM)
      clearTimeout(this.onboardTM);
    if (this.chooseTM)
      clearTimeout(this.chooseTM);

    this.didFinishOnboarding = true;

    this.soundEffects.pauseClip("intro");

    // ui
    document.getElementById("skip-tutorial").classList.remove("show");
    let promptItems = document.querySelectorAll(".prompt-display-item");
    promptItems.forEach((item, i) => {
      item.classList.add("show");
    });
    this.showUserPanel();


    if (this.isMobile()) {
      document.getElementById("jump-button").classList.add("show");
      //document.getElementById("fly-button").classList.add("show");
      document.getElementById("mobile-pad").classList.add("show");

    }


  }

  showUserPanel() {
    document.getElementById("prompt-display").classList.add("show");
  }


  //----------------------- downloadable artwork for each level
  makeDownloadableImage(levelName) {
    if (!this.artworkImages) {
      this.artworkImages = {};
      this.artworkImagesBlobs = {};
    }
    let artworkImg = new Image();
    artworkImg.onload = function () {
      this.artworkImages[levelName] = document.createElement('canvas');
      this.artworkImages[levelName].width = 1580;
      this.artworkImages[levelName].height = 1740;//this.postcardH;
      let context = this.artworkImages[levelName].getContext('2d');
      context.imageSmoothingEnabled = false;
      context.clearRect(0, 0, this.artworkImages[levelName].width, this.artworkImages[levelName].height);
      context.drawImage(artworkImg, 0, 0);

      let collectedImageCanvas = document.getElementById("collected-images-level-" + levelName);
      collectedImageCanvas.width = 200 * window.devicePixelRatio;;
      collectedImageCanvas.height = 200 * window.devicePixelRatio;;
      let ccContext = collectedImageCanvas.getContext('2d');
      ccContext.imageSmoothingEnabled = false;
      ccContext.drawImage(artworkImg, 208, 208, 1150, 1150, 0, 0, collectedImageCanvas.width, collectedImageCanvas.height);

      var callback = function (filename) {
        return (b) => {
          this.artworkImagesBlobs[levelName] = b;
        };
      }.bind(this)
      this.artworkImages[levelName].toBlob(callback("dreaming_wih_flamingos.png"), "image/png");


    }.bind(this);
    artworkImg.src = this.getArtworkPathForLevel(levelName);

  }

  getArtworkPathForLevel(levelName) {
    let path = "./artworks/level_" + levelName + "/" + levelName + "_" + this.artworksNum[levelName] + ".png";
    return path;
  }

  downloadImage(levelName) {
    if (this.artworkImagesBlobs[levelName]) {

      const a = document.createElement("a");
      a.download = "dreaming_wih_flamingos.png";
      a.href = window.URL.createObjectURL(this.artworkImagesBlobs[levelName]);
      a.click();

    }

  }

  async shareArtwork(levelName) {
    //await this.makeDownloadableImage(levelName);
    const title = this.translator.getDynamicText("share-title");
    const text = this.translator.getDynamicText("share-text");
    if (AxL.can["shareFiles"]) {
      const imgData = this.artworkImages[levelName].toDataURL("image/png").split(",")[1];

      this.axl.sendMessage(AxL.ChildToHost.SHARE, {
        title: title,
        text: text,
        files: [{ data: imgData, name: "dreaming_wih_flamingos.png", type: "image/png" }],
      });
    } else {
      this.axl.sendMessage(AxL.ChildToHost.SHARE, { title: title, text: text });
    }
  }

  //----------------------- Mobile specific controls

  showMobileControls() {
    if (this.isMobile()) {
      document.getElementById("mobile-pad").classList.add("show");
      document.getElementById("jump-button").classList.add("show");
      //document.getElementById("fly-button").classList.add("show");

      document.getElementById("mobile-pad").classList.remove("inactive");
      document.getElementById("jump-button").classList.remove("inactive");
      //document.getElementById("fly-button").classList.remove("inactive");

    }
  }

  hideMobileControls() {
    if (this.isMobile()) {
      document.getElementById("mobile-pad").classList.remove("show");
      document.getElementById("jump-button").classList.remove("show");
      //document.getElementById("fly-button").classList.remove("show");

    }
  }

  deactivateMobileControls() {
    if (this.isMobile()) {
      document.getElementById("mobile-pad").classList.add("inactive");
      document.getElementById("jump-button").classList.add("inactive");
      //document.getElementById("fly-button").classList.add("inactive");

    }
  }


  //-----------------------  Music LM
  async createAndSendPrompt(keyword = "", fromOtherUser = false, otherIndex = null) {

    if (this.isFrozen)
      return;

    this.updateBadge("bench");

    this.waitingForPrompt = true;

    const newUid = Math.random() * 100000;
    this.callUid = newUid;

    const benchIndex = this.currentBenchIndex;
    this.myFlamingo.mode = "pause_moving";
    this.myFlamingo.startSittingAnimation();

    let myNewColor = document.getElementById("user-select-color").value;

    ///------> MAKE REQUEST
    let returnedError = false;
    let hasErrorMessage = false;

    try {

      let myColor = document.getElementById("user-select-color").value;
      let myInstrument = document.getElementById("user-select-instrument").value;

      this.lastBenchSelections = [myColor, myInstrument];

      const result = await this.musiclm.callApi(this.levelName, [myColor, myInstrument], "dream");

      // fake time await
      await new Promise(r => setTimeout(r, 3500));

      hasErrorMessage = result.hasOwnProperty('message');

      this.stopBlinkBench(this.benches[benchIndex]);

      this.waitingForPrompt = false;

      // check in sync
      if (this.callUid != newUid) {
        console.log("Cancel - new call updated.");
        return;
      }

      // console.log(result);


      // process result
      if ('error' in result && result.error === true) {

        console.log("ERROR", result.message);
        returnedError = true;
      }
      else if ('audio' in result) {
        const wav = result.audio;//result.results[0].audioData.data;
        this.musiclm.prevBuffers[newUid] = { audio: wav, channels: this.musiclm.channels };
        this.lastBufferSaved = newUid;

        let vol = (this.isMuted()) ? 0 : this.currentSongVol;
        let didSet = await this.musiclm.playStoredTrack(newUid, vol)

        this.currentPreloadTrack = null;
        this.currentPreloadTrackId = null;

        if (this.myFlamingo.environment == "bench") {
          let text = this.translator.getDynamicText("bench-played");
          document.getElementById("user-input-text").innerHTML = text;
          document.getElementById("user-input-container").classList.add("show");
        } else {

          this.setGenedTextFeedback("bench", "bench");

        }

        if (this.sparkles) {
          this.sparkles.setToSparkle(1, this.myFlamingo.group.position);
        }

        if (this.levelName == "spirit")
          this.myFlamingo.doColorChangeMe(myNewColor);

      } else {

        console.log("ERROR", result);
        returnedError = true;
      }

    } catch (error) {
      console.log(error);
      this.stopBlinkBench(this.benches[benchIndex]);
      returnedError = true;
    }

    if (returnedError == true) {

      let text = this.translator.getDynamicText("error-message");

      text = this.translator.getDynamicText("prompt-error");
      if (!hasErrorMessage) {
        text = this.translator.getDynamicText("mlm-error");
      }
      document.getElementById("user-input-text").innerHTML = text;

      // if(this.sparkles){
      //   this.sparkles.setToSparkle(0,this.myFlamingo.group.position);
      // }

      this.myFlamingo.endSittingAnimation();

    }

    if (this.sparkles) {
      this.sparkles.setToSparkle(0, this.myFlamingo.group.position);
    }

    document.getElementById("user-input-text").classList.remove("hide");
    document.getElementById("user-input").classList.remove("generating");
    document.getElementById("user-input-generating").classList.remove("show");
    document.getElementById("prompt-creation").classList.remove("hide");


  }

  // async dataUrlToArrayBuffers(dataUrl) {
  //     const res = await fetch(dataUrl);
  //     return await res.arrayBuffer();
  // }

  //  async callApi(prompts,docheck=false,myColor,myInstrument) {
  //  let body = {
  //     prompts: prompts,
  //     api: this.is_using_api,
  //     type:"dream",
  //     prefixStyle: [myColor,myInstrument],
  //     "levelName":this.levelName
  //   };
  //   if(docheck){
  //     body['docheck'] = true;
  //   }
  //
  //   const url = `${window.location.origin +(window.location.pathname === "/" ? "" : window.location.pathname)}/musiclm2`;
  //
  //   const response = await fetch(url, {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify(body),
  //   });
  //   return await response.json();
  // }

  async setMusicFromPreloadedTrack(trackId, destVol = 1, type = null, altWord = null) {
    console.log("trackId");

    if (!this.musiclm.prevBuffers.hasOwnProperty(trackId)) {
      let loadBackup = await this.musiclm.handleMusicLMLoadError(trackId, this.levelName);
      if (!loadBackup) {
        console.log("No backup.");
        return false;
      } else {
        console.log("Use backup.", trackId);
      }


    } else if (this.currentPreloadTrackId == trackId) {

      return false;
    }

    this.didTrackTm = setTimeout(function () { this.didTrackTm = null; }.bind(this), 2000);
    this.prevPreloadTrack = this.currentPreloadTrack;
    this.handleEndPrevTrack(this.prevPreloadTrack);
    this.currentPreloadTrackId = trackId;

    if (this.isMuted()) destVol = 0;

    let didSet = await this.musiclm.playStoredTrack(trackId, destVol);


    if (didSet && type != null) {
      this.setGenedTextFeedback(trackId, type, altWord);
    } else {
      return false;
    }


    this.currentPreloadTrack = trackId;


    return true;
  }

  setGenedTextFeedback(trackId, type, altWord = null) {
    let finalPrompt = null;

    if (trackId == "final")
      trackId = altWord;

    if (type == "prefix") {

      let pre = this.translator.getDynamicText("park-gened");//"The park generated a new song... ";
      finalPrompt = pre + " " + levelPublic[this.levelName][trackId + "prefix"] + ".";

    } else if (type == "bench") {
      let pre = this.translator.getDynamicText("bench-played-away");//"The park generated a new song... ";
      let colorPrompt = levelPublic[this.levelName]['dream-prompts']['colors'][this.lastBenchSelections[0]];
      let instrumentPrompt = levelPublic[this.levelName]['dream-prompts']['instruments'][this.lastBenchSelections[1]];

      finalPrompt = pre + " " + instrumentPrompt + " " + colorPrompt + ".";

    } else {
      return;
    }


    if (finalPrompt != null) {
      this.setFeedback("custom", finalPrompt);
      this.showFeedback(6000);
    }

  }

  handleEndPrevTrack(trackId) {
    if (trackId == "clouds") {
      this.endCloudEffect();
      this.seekTargets = [];
    } else if (trackId == "floor") {
      //this.floorCircle.isMoving = false;
    } else if (trackId == "leaf") {
      this.centralTree.finalLeavesColor = this.yinkaColors.getDarkGreen();
      this.colorInTree(this.pineTrees.length - 1);
      this.seekTargets = [];
    }

    this.myFlamingo.doColorChangeMe("default");

  }

  async playWordPlayInPark(word) {

    if (this.resetParkSongTM)
      clearTimeout(this.resetParkSongTM);

    let vol = (this.isMuted()) ? 0 : this.currentSongVol;

    let played = await this.setMusicFromPreloadedTrack(word, vol, "keyword");

    if (played) {
      let text = this.translator.getDynamicText("i-hear") + word + "!";
      this.helperHintLabel = null;
      let el = document.getElementById("helper-hints-text");
      el.innerHTML = text;
      let hintUuid = Math.random().toString(36).substring(2, 10);
      el.parentNode.setAttribute("data-uuid", hintUuid);
      this.showHelperHint(3000, "play-park");
    }

    if (this.wordSoup.holdingWordInBeak)
      this.wordSoup.wordIsPlaying();
  }

  //-----------------------  word bank/inventory
  initWordBank() {
    let el = document.getElementById("word-bank-list");
    let totalWords = this.wordSoup.wordMeshes.length;
    for (let i = 0; i < totalWords; i++) {
      let li = document.createElement("li");
      el.appendChild(li);
    }
  }

  addPromptToWordBank(word) {


    if (this.promptWordBank.includes(word)) {
      return;
    }


    document.getElementById("user-word-bank-button").classList.add("addedWord");
    setTimeout(() => {
      document.getElementById("user-word-bank-button").classList.remove("addedWord");
    }, 250)

    document.getElementById("word-bank-placeholder").classList.add("hide");
    document.getElementById("word-bank-explain").classList.remove("hide");

    this.promptWordBank.push(word);

    let el = document.getElementById("word-bank-list");
    let li = document.createElement("li");
    let button = document.createElement("button");
    button.innerText = word;
    button.setAttribute("data-word", word);

    li.appendChild(button);
    li.setAttribute("data-word", word);
    button.addEventListener("click", function (e) {
      if (e && e.currentTarget) e.currentTarget.blur();
      let myWord = e.currentTarget.getAttribute("data-word");
      this.selectPromptFromWordBank(myWord);
    }.bind(this));

    li.addEventListener("mouseenter", function () {
      let allLis = Array.from(document.querySelectorAll("#word-bank-list li"));
      let idx = allLis.indexOf(li);
      if (idx !== -1) {
        this.updateWordBankHighlight(idx);
      }
    }.bind(this));

    el.appendChild(li);

    let badge = document.querySelector(".user-word-bank-button .badge");
    badge.classList.add("show");
    badge.innerText = this.promptWordBank.length;
  }

  selectPromptFromWordBank(word) {
    this.hideWordBank();
    this.wordSoup.selectedFromWordBank(word, this.myFlamingo.group.position);

    if (this.chatState == "question" && !this.levelCompleted) {
      this.answeredFlamingo(word);
    } else if (this.levelCompleted) {
      this.playWordPlayInPark(word);
      this.handleAxlTrack("play_word", word);
    }

  }

  toggleWordBank() {
    let wel = document.getElementById("word-bank");
    if (wel.classList.contains("show")) {
      this.hideWordBank();
    } else {
      this.showWordBank();
    }
  }

  showWordBank() {

    this.closeHelperHint();

    let el = document.getElementById("word-bank-container");
    el.classList.add("show");

    let wel = document.getElementById("word-bank");
    wel.classList.add("show");

    if (this.isMobile()) {
      this.deactivateMobileControls();
    }

    this.wordBankCooldownUntil = Date.now() + 1000;
    this.initWordBankHighlight();

  }

  hideWordBank() {

    let el = document.getElementById("word-bank-container");
    el.classList.remove("show");

    let wel = document.getElementById("word-bank");
    wel.classList.remove("show");

    document.getElementById("word-bank-bubbles").classList.remove("show");

    if (this.isMobile() && this.readyToMove) {
      this.showMobileControls();
    }

    this.clearWordBankHighlight();
  }

  initWordBankHighlight() {
    let lis = Array.from(document.querySelectorAll("#word-bank-list li"));
    if (lis.length === 0) return;

    let isSelectable = (li) => {
      if (this.levelCompleted) return true;
      return !li.classList.contains("used") && !li.classList.contains("solved");
    };

    let firstSelectable = lis.findIndex(isSelectable);
    let targetIndex = firstSelectable !== -1 ? firstSelectable : 0;
    this.updateWordBankHighlight(targetIndex);
  }

  updateWordBankHighlight(targetIndex) {
    let lis = document.querySelectorAll("#word-bank-list li");
    if (!lis || lis.length === 0) return;

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= lis.length) targetIndex = lis.length - 1;

    this.highlightedWordIndex = targetIndex;

    lis.forEach((li, i) => {
      let btn = li.querySelector("button");
      if (i === targetIndex) {
        li.classList.add("highlight");
        if (btn) {
          btn.focus({ preventScroll: true });
        }
        if (typeof li.scrollIntoView === 'function') {
          li.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      } else {
        li.classList.remove("highlight");
        if (btn && document.activeElement === btn) {
          btn.blur();
        }
      }
    });
  }

  clearWordBankHighlight() {
    let lis = document.querySelectorAll("#word-bank-list li");
    lis.forEach((li) => {
      li.classList.remove("highlight");
      let btn = li.querySelector("button");
      if (btn && document.activeElement === btn) {
        btn.blur();
      }
    });
    this.highlightedWordIndex = null;
  }

  navigateWordBank(direction) {
    let lis = Array.from(document.querySelectorAll("#word-bank-list li"));
    if (lis.length === 0) return;

    let isSelectable = (li) => {
      if (this.levelCompleted) return true;
      return !li.classList.contains("used") && !li.classList.contains("solved");
    };

    let selectableIndices = lis.map((li, idx) => isSelectable(li) ? idx : -1).filter(idx => idx !== -1);
    if (selectableIndices.length === 0) {
      selectableIndices = lis.map((_, idx) => idx);
    }

    let currentPos = selectableIndices.indexOf(this.highlightedWordIndex);
    if (currentPos === -1) {
      currentPos = direction > 0 ? 0 : selectableIndices.length - 1;
    } else {
      currentPos = (currentPos + direction + selectableIndices.length) % selectableIndices.length;
    }

    this.updateWordBankHighlight(selectableIndices[currentPos]);
  }

  selectHighlightedWordBankItem() {
    let lis = document.querySelectorAll("#word-bank-list li");
    if (!lis || lis.length === 0) return;

    let index = this.highlightedWordIndex;
    if (index == null || index < 0 || index >= lis.length) {
      this.initWordBankHighlight();
      index = this.highlightedWordIndex;
    }

    if (index != null && lis[index]) {
      let isSelectable = this.levelCompleted || (!lis[index].classList.contains("used") && !lis[index].classList.contains("solved"));
      if (isSelectable) {
        let btn = lis[index].querySelector("button");
        if (btn) {
          btn.click();
        }
      }
    }
  }

  //----------------------- End level popup keyboard navigation
  getEndLevelPopupButtons() {
    let nextLevelBtn = document.getElementById("go-to-next-level");
    let stayPlayBtn = document.getElementById("stay-and-play");
    let buttons = [];
    if (nextLevelBtn && nextLevelBtn.style.display !== "none" && !nextLevelBtn.classList.contains("hide")) {
      buttons.push(nextLevelBtn);
    }
    if (stayPlayBtn && stayPlayBtn.style.display !== "none" && !stayPlayBtn.classList.contains("hide")) {
      buttons.push(stayPlayBtn);
    }
    return buttons;
  }

  initEndLevelPopupHighlight() {
    let buttons = this.getEndLevelPopupButtons();
    if (buttons.length === 0) return;
    this.updateEndLevelPopupHighlight(0);
  }

  updateEndLevelPopupHighlight(targetIndex) {
    let buttons = this.getEndLevelPopupButtons();
    if (!buttons || buttons.length === 0) return;

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= buttons.length) targetIndex = buttons.length - 1;

    this.highlightedEndLevelBtnIndex = targetIndex;

    buttons.forEach((btn, i) => {
      if (i === targetIndex) {
        btn.classList.add("highlight");
        btn.focus({ preventScroll: true });
      } else {
        btn.classList.remove("highlight");
        if (document.activeElement === btn) {
          btn.blur();
        }
      }
    });
  }

  navigateEndLevelPopup(direction) {
    let buttons = this.getEndLevelPopupButtons();
    if (buttons.length <= 1) return;

    let currentPos = this.highlightedEndLevelBtnIndex;
    if (currentPos == null || currentPos < 0 || currentPos >= buttons.length) {
      currentPos = 0;
    }
    let nextPos = (currentPos + direction + buttons.length) % buttons.length;
    this.updateEndLevelPopupHighlight(nextPos);
  }

  selectHighlightedEndLevelPopupItem() {
    let buttons = this.getEndLevelPopupButtons();
    if (!buttons || buttons.length === 0) return;

    let index = this.highlightedEndLevelBtnIndex;
    if (index == null || index < 0 || index >= buttons.length) {
      index = 0;
    }

    if (buttons[index]) {
      buttons[index].click();
    }
  }

  clearEndLevelPopupHighlight() {
    let nextLevelBtn = document.getElementById("go-to-next-level");
    let stayPlayBtn = document.getElementById("stay-and-play");
    if (nextLevelBtn) {
      nextLevelBtn.classList.remove("highlight");
      if (document.activeElement === nextLevelBtn) nextLevelBtn.blur();
    }
    if (stayPlayBtn) {
      stayPlayBtn.classList.remove("highlight");
      if (document.activeElement === stayPlayBtn) stayPlayBtn.blur();
    }
    this.highlightedEndLevelBtnIndex = null;
  }

  //----------------------- pop up for about info
  toggleMoreInfoPopup() {
    let el = document.getElementById("info-bio-popup");
    if (el.classList.contains("show")) {
      document.getElementById("info-bio-popup").classList.remove("show");
      document.getElementById("info-bio-popup-bd").classList.remove("show");
    } else {
      this.showMoreInfoPopUp();
    }
  }

  showMoreInfoPopUp() {
    let popupEl = document.getElementById("info-bio-popup");

    popupEl.classList.add("show");
    popupEl.focus();

    document.getElementById("info-bio-popup-bd").classList.add("show");

    this.closeHelperHint();
    this.hideWordBank();
    this.handleAxlTrack('more_info');

    // document.getElementById("landing-title").style.display = "none";
    // document.getElementById("landing-yinka").style.display = "none";

  }

  //-----------------------  user ui zoom
  toggleUserZoomFromButton() {

    this.isZoomed = !this.isZoomed;

    if (this.isZoomed) {
      this.controls.maxDistance = 80;


      if (this.myFlamingo.mode == "moving") {
        this.myFlamingo.mode = "pause_moving";
      }

      let cameraDestPos = { x: this.camera.position.x, y: 60, z: this.camera.position.z };

      let controlsDest = { x: this.controls.target.x, y: this.controls.target.y, z: this.controls.target.z };
      this.animateCameraTo({ x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z }, { x: controlsDest.x, y: controlsDest.y, z: controlsDest.z }, 2, 0, 0);

    }
    else {


      this.endZoomOut();
      let cameraDestPos = { x: this.posHelperCam.position.x, y: 1, z: this.posHelperCam.position.z };
      let controlsDest = { x: this.controls.target.x, y: this.controls.target.y, z: this.controls.target.z };
      this.animateCameraTo({ x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z }, { x: controlsDest.x, y: controlsDest.y, z: controlsDest.z }, 2, 0, 0);
      setTimeout(function () {
        this.controls.maxDistance = 12;
      }.bind(this), 2000);

    }

  }

  endZoomOut() {

    if (this.isMobile()) {
      this.controls.enableZoom = true;
    }


  }

  //-----------------------  mute and volume
  handleMute() {
    for (var sound in this.previewClips) {
      this.previewClips[sound].volume = 0;
    }
    if (this.soundEffects) {
      this.soundEffects.mute();
    }

    this.musiclm.mute();

    if (this.musiclm.source) {
      this.musiclm.gainNode.gain.value = 0; // 10 %
    }
  }

  handleUnMute() {

    for (var sound in this.previewClips) {
      this.previewClips[sound].volume = this.currentSongVol;
    }

    if (this.soundEffects) {
      this.soundEffects.unMute();
    }

    this.musiclm.unmute();

    if (this.musiclm.source) {
      this.musiclm.gainNode.gain.value = this.currentSongVol; // 10 %

    }
  }

  isMuted() {
    return this.muted;
  }

  handleYouTubeState(e) {
    if (e.data == 1) {
      this.handleMute();
      document.getElementById("volume").classList.add("off");
    } else if (e.data == 2) {
      this.handleUnMute();
      document.getElementById("volume").classList.remove("off");
    }
  }
  //----------------------- Helper Hints

  setHelperHint(hintLabel, type = "secondary") {
    this.helperHintLabel = hintLabel;
    this.helperHintType = type;
    let el = document.getElementById("helper-hints-text");
    el.innerHTML = this.translator.getDynamicText(hintLabel);

    if (hintLabel == "play-park" /*&& this.levelName == "spirit"*/) {

      let currentInner = document.querySelector("#helper-hints-container .play-park-inst").innerHTML;
      document.querySelector("#helper-hints-container .play-park-inst").innerHTML = currentInner + this.translator.getDynamicText("end-level-play-park-extra-" + this.levelName);;

    }
    let hintUuid = Math.random().toString(36).substring(2, 10);
    el.parentNode.setAttribute("data-uuid", hintUuid);
    if (type == "primary") {
      this.lastPrimaryHint = hintLabel;
    }
  }

  showHelperHint(timeout = 0, resetTo = null) {

    if (this.hhTm)
      clearTimeout(this.hhTm);

    this.hideWordBank();
    this.closeFeedback();
    let el = document.getElementById("helper-hints-container");
    el.classList.add("show");

    if (timeout > 0) {
      let uuid = document.getElementById("helper-hints").getAttribute("data-uuid");
      this.hhTm = setTimeout(function () {
        if (uuid == document.getElementById("helper-hints").getAttribute("data-uuid")) {
          this.closeHelperHint();
          if (resetTo != null) {
            setTimeout(function () { this.setHelperHint(resetTo); }.bind(this), 100);

          }
        }

      }.bind(this), timeout);
    }

    if (this.isMobile()) {
      let bb = document.getElementById("helper-hints").getBoundingClientRect();
      document.querySelector("#helper-hints-container #word-bank-bubbles").style.bottom = (bb.height + 10) + "px";
    }
  }

  closeHelperHint() {
    let el = document.getElementById("helper-hints-container");
    el.classList.remove("show");
  }

  dismissHowToPopup() {
    let howToPopup = document.getElementById("how-to-popup");
    if (howToPopup && howToPopup.classList.contains("show")) {
      howToPopup.classList.remove("show");
      this.showHelperHint();
      return true;
    }
    return false;
  }

  toggleHelperHint() {
    let el = document.getElementById("helper-hints-container");
    if (el.classList.contains("show")) {
      this.closeHelperHint();
    } else {
      this.showHelperHint();
      this.handleAxlTrack("helper_hint", this.helperHintLabel);
    }

  }

  setFeedback(label, custom = null) {

    this.feedbackLabel = label;
    let el = document.getElementById("feedback-text");

    if (custom != null) {

      el.innerText = custom;//this.translator.getDynamicText(label);

    } else {

      el.innerHTML = this.translator.getDynamicText(label);

    }

    let hintUuid = Math.random().toString(36).substring(2, 10);
    el.parentNode.setAttribute("data-uuid", hintUuid);

  }

  showFeedback(timeout = 0) {
    if (this.fTm)
      clearTimeout(this.fTm);

    this.hideWordBank();
    this.closeHelperHint();

    let el = document.getElementById("feedback-container");
    el.classList.add("show");

    document.querySelector("#feedback-container #word-bank-bubbles").classList.add("show");

    if (timeout > 0) {
      let uuid = document.getElementById("feedback").getAttribute("data-uuid");
      this.fTm = setTimeout(function () {
        if (uuid == document.getElementById("feedback").getAttribute("data-uuid")) {
          this.closeFeedback();
        }

      }.bind(this), timeout);
    }


  }

  closeFeedback() {
    let el = document.getElementById("feedback-container");
    el.classList.remove("show");
    document.querySelector("#feedback-container #word-bank-bubbles").classList.remove("show");

  }

  //----------------------- Mini map / compass

  updateMiniMap(fPos) {

    if (!this.minimap) {
      this.minimap = document.getElementById('minimap');
      this.minimap.width = 180;
      this.minimap.height = 180;//this.postcardH;
      this.minimapcontext = this.minimap.getContext('2d');
      this.minimapcontext.imageSmoothingEnabled = false;

    }

    if (!document.querySelector(".minimap").classList.contains("show")) {
      return;
    }

    this.minimapcontext.clearRect(0, 0, this.minimap.width, this.minimap.height);
    this.minimapcontext.save();
    this.minimapcontext.translate((this.minimap.width / 2), (this.minimap.height / 2));
    this.minimapcontext.rotate(this.myFlamingo.group.rotation.y);

    let origin = { x: 0, y: 0 };
    let scaleD = 4;


    // pond
    let pDist = scaleD * get_distance(fPos.x, fPos.z, 0, 0);
    this.drawMiniMapItemAsCircle(0, 0, origin.x, origin.y, pDist, 5 * scaleD, "#6ECAE7", false);

    //pattern floor
    if (this.levelCompleted) {
      let floorColor = "#FA4A3A30";
      if (this.levelName == "spirit") {
        floorColor = "#FFE74430";
      }
      this.drawMiniMapItemAsCircle(0, 0, origin.x, origin.y, pDist, 14 * scaleD, floorColor, false);
    }

    let distMax = 30;

    // trees
    if (this.pineTrees && this.levelCompleted) {
      this.pineTrees.forEach((item, i) => {
        let d = get_distance(fPos.x, fPos.z, item.position.x, item.position.z);
        if (d < distMax) {
          d *= scaleD;
          this.drawMiniMapItemAsCircle(item.position.x, item.position.z, origin.x, origin.y, d, 8, "#6BB64770", false);
        }
      });
    }

    // benches
    if (this.benches && this.levelCompleted) {
      this.benches.forEach((item, i) => {
        let d = get_distance(fPos.x, fPos.z, item.position.x, item.position.z);
        if (d < distMax) {
          d *= scaleD;
          this.drawMiniMapItemAsCircle(item.position.x, item.position.z, origin.x, origin.y, d, 8, "#FFCFE9", false);
        }
      });
    }

    //calculate flamingos and words in view
    for (let i = 1; i < this.flamingos.length; i++) {
      //if(!this.allEldersFound.includes(i)){
      let item = this.flamingos[i];
      let d = get_distance(fPos.x, fPos.z, item.group.position.x, item.group.position.z);
      if (d < distMax) {
        d *= scaleD;
        this.drawMiniMapItemAsCircle(item.group.position.x, item.group.position.z, origin.x, origin.y, d, 6, "#E65EBD", true);
      }
      //}
    }

    if (this.wordSoup) {
      this.wordSoup.wordMeshes.forEach((item, i) => {
        if (!this.promptWordBank.includes(item.name)) {
          let d = get_distance(fPos.x, fPos.z, item.position.x, item.position.z);
          if (d < distMax) {
            d *= scaleD;
            this.drawMiniMapItemAsCircle(item.position.x, item.position.z, origin.x, origin.y, d, 6, "yellow", true);
          }
        }

      });
    }


    // me
    this.drawMiniMapItemAsCircle(0, 0, 0, 0, 0, 6, "#ee6e37", true);


    // clouds
    if (this.musicClouds && this.levelCompleted) {
      this.musicClouds.forEach((item, i) => {
        let d = get_distance(fPos.x, fPos.z, item.position.x, item.position.z);
        if (d < 20) {
          d *= scaleD;
          this.drawMiniMapItemAsCircle(item.position.x, item.position.z, origin.x, origin.y, d, 9, "#ffffff60", false);
        }
      });
    }


    this.minimapcontext.restore();

    // me arrow
    this.minimapcontext.fillStyle = "#ee6e37";
    var path = new Path2D();
    path.moveTo((this.minimap.width / 2) + 6, (this.minimap.height / 2) - 9);
    path.lineTo((this.minimap.width / 2), (this.minimap.height / 2) - 13);
    path.lineTo((this.minimap.width / 2) - 6, (this.minimap.height / 2) - 9);
    this.minimapcontext.fill(path);


  }

  drawMiniMapItemAsCircle(sx, sz, originX, originY, d, r, color, shadow = true) {
    let fPos = this.myFlamingo.group.position;
    let angle = Math.atan2(fPos.z - sz, fPos.x - sx);
    let x = originX + d * Math.cos(angle);
    let y = originY + d * Math.sin(angle);

    if (shadow) {
      this.minimapcontext.beginPath();
      this.minimapcontext.arc(x + 1, y + 1, r, 0, 2 * Math.PI);
      this.minimapcontext.fillStyle = "#00000025";
      this.minimapcontext.fill();
      this.minimapcontext.closePath();
    }

    this.minimapcontext.beginPath();
    this.minimapcontext.arc(x, y, r, 0, 2 * Math.PI);
    this.minimapcontext.fillStyle = color;
    this.minimapcontext.fill();
    this.minimapcontext.closePath();

  }

  toggleMiniMap() {
    let mEl = document.querySelector(".minimap");
    if (mEl.classList.contains("show")) {
      mEl.classList.remove("show");
    } else {
      mEl.classList.add("show");
    }
  }

  closeMiniMap() {
    let mEl = document.querySelector(".minimap");
    mEl.classList.remove("show");
  }

  openMiniMap() {
    let mEl = document.querySelector(".minimap");
    mEl.classList.add("show");
  }

  //----------------------- Popup  Bubble for Bench selections

  setUserInputBubble(text, mode) {
    let el = document.getElementById("user-input");
    el.setAttribute("data-mode", mode);
    let textEl = document.getElementById("user-input-text");
    textEl.innerText = text;
  }

  showUserInputBubble() {
    document.getElementById("user-input-generating").classList.remove("show");
    document.getElementById("user-input").classList.remove("generating");
    document.getElementById("user-input-text").classList.remove("hide");
    document.getElementById("prompt-creation").classList.remove("hide");

    let el = document.getElementById("user-input-container");
    el.classList.add("show");

    this.initBenchBubbleHighlight();
  }

  hideUserInputBubble() {
    document.getElementById("user-input-container").classList.remove("show");
    document.getElementById("user-input").classList.remove("generating");
    this.clearBenchBubbleHighlight();
  }

  toggleUserInputBubble() {
    let el = document.getElementById("user-input-container");
    if (el.classList.contains("show")) {
      this.hideUserInputBubble();
    } else {
      this.showUserInputBubble();
    }
  }

  handleUserSelectPrompt() {

    let mode = document.getElementById("user-input").getAttribute("data-mode");
    if (mode == "bench") {

      let myColor = document.getElementById("user-select-color").value;
      let myIntrument = document.getElementById("user-select-instrument").value;
      let colorPrompt = levelPublic[this.levelName]['dream-prompts']['colors'][myColor];
      let instrumentPrompt = levelPublic[this.levelName]['dream-prompts']['instruments'][myIntrument];

      let waitText = this.translator.getDynamicText("music-generating") + " <span id='user-input-gen-instrument'>" + instrumentPrompt + "...</span> <span id='user-input-gen-color'>" + colorPrompt + "...</span>";
      document.getElementById("user-input-generating").innerHTML = waitText;

      setTimeout(function () {
        document.getElementById("user-input-gen-instrument").classList.add("show");
      }.bind(this), 1000);

      setTimeout(function () {
        document.getElementById("user-input-gen-color").classList.add("show");
      }.bind(this), 3000);

      document.getElementById("user-input-generating").classList.add("show");
      document.getElementById("user-input").classList.add("generating");
      document.getElementById("user-input-text").classList.add("hide");
      document.getElementById("user-input-subtext").classList.add("hide");
      document.getElementById("prompt-creation").classList.add("hide");
      this.clearBenchBubbleHighlight();

      this.createAndSendPrompt();
      this.handleAxlTrack("user_played_bench", this.levelName);


    }

    if (this.sparkles) {
      this.sparkles.setToSparkle(200 * 1000, this.myFlamingo.group.position);
    }

  }

  //----------------------- Bench bubble keyboard controls
  isBenchBubbleOpen() {
    let userInputContainer = document.getElementById("user-input-container");
    let promptCreation = document.getElementById("prompt-creation");
    return !!(userInputContainer && userInputContainer.classList.contains("show") && promptCreation && !promptCreation.classList.contains("hide"));
  }

  getBenchBubbleElements() {
    let colorSelect = document.getElementById("user-select-color");
    let instrumentSelect = document.getElementById("user-select-instrument");
    let goBtn = document.getElementById("go-button");
    return [colorSelect, instrumentSelect, goBtn].filter(Boolean);
  }

  initBenchBubbleHighlight() {
    this.updateBenchBubbleHighlight(0);
  }

  updateBenchBubbleHighlight(targetIndex) {
    let elements = this.getBenchBubbleElements();
    if (!elements || elements.length === 0) return;

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= elements.length) targetIndex = elements.length - 1;

    this.highlightedBenchOptionIndex = targetIndex;

    elements.forEach((el, i) => {
      if (i === targetIndex) {
        el.classList.add("highlight");
        el.focus({ preventScroll: true });
      } else {
        el.classList.remove("highlight");
        if (document.activeElement === el) {
          el.blur();
        }
      }
    });
  }

  clearBenchBubbleHighlight() {
    let elements = this.getBenchBubbleElements();
    elements.forEach(el => {
      el.classList.remove("highlight");
      if (document.activeElement === el) {
        el.blur();
      }
    });
    this.highlightedBenchOptionIndex = null;
  }

  navigateBenchBubble(direction) {
    let elements = this.getBenchBubbleElements();
    if (!elements || elements.length <= 1) return;

    let currentPos = this.highlightedBenchOptionIndex;
    if (currentPos == null || currentPos < 0 || currentPos >= elements.length) {
      currentPos = 0;
    }
    let nextPos = (currentPos + direction + elements.length) % elements.length;
    this.updateBenchBubbleHighlight(nextPos);
  }

  changeSelectOption(selectEl, direction) {
    if (!selectEl || !selectEl.options || selectEl.options.length === 0) return;
    let total = selectEl.options.length;
    let current = selectEl.selectedIndex;
    if (current === -1) current = 0;
    let next = (current + direction + total) % total;
    selectEl.selectedIndex = next;
    selectEl.dispatchEvent(new Event("change"));
  }

  //----------------------- camera animation
  animateCameraTo(position, lookTarget, durationCam, durationTarget, delay = 0, state = null) {


    gsap.to(this.camera.position, {
      x: position.x, y: position.y, z: position.z, duration: durationCam, overwrite: true, delay: delay, onComplete: function () {
        this.controlsState = "default";
        this.controls.enabled = true;
      }.bind(this)
    });


    gsap.to(this.controls.target, { x: lookTarget.x, y: lookTarget.y, z: lookTarget.z, duration: durationTarget, overwrite: true, delay: delay });

    this.controlsState = "animated";
    this.controls.enabled = false;

    if (state != null)
      this.controlsState = state;
  }

  //-----------------------  Flamingos: Init elders in world
  setHiddenFlamingos(isFirst = true) {

    let questions = this.dialogManager.getSolutions(this.levelName);

    // resets
    this.allEldersFound = [];
    this.eldersFound = 0;
    this.updateElderUi();
    this.levelCompleted = false;

    this.hexFloor.allTiles.forEach((item, i) => {
      item.hasElder = false;
      item.elderIndex = null;
      if (levelConfig[this.levelName]["benches"].includes(i)) {
        item.hasBench = true;
      }
    });

    // choose tiles that do not have tree / bench / or too close
    let disallowedTiles = [];
    if (this.levelName == "spirit") {
      disallowedTiles = [218, 238, 258, , 278, 318, 337, 356, 355, 372, 371, 389, 387, 367, 346, 326, 325, 203, 202, 182, 161, 141, 121, 123, 102, 82, 64, 65, 66, 87, 68, 69, 70, 72, 92, 53, 54, 55, 76, 96, 97, 117, 137, 157, 178, 198];
    }
    let tiles = this.hexFloor.allTiles.filter((item) => {
      return (item.len > 12 && !item.hasTree && !item.hasWord && !item.hasBench && !item.bench && item.usable && !disallowedTiles.includes(item.tileIndex));
    });


    let angleStep = (Math.PI * 2) / (this.maxFlamingos - 2);
    let tileIndexTaken = [];
    let angles = [0, 0, 0, 120, 240];
    let farD = 18;

    if (this.levelName == "spirit") {
      angles = [0, 0, 120, 240, 360];
      // farD = 10;
    }
    for (let i = 1; i < this.flamingos.length; i++) {

      let angle = degrees_to_radians(angles[i]);//(i-2) * angleStep;
      let x = farD * Math.sin(angle);
      let z = farD * Math.cos(angle);

      let closeTiles = [];
      tiles.forEach((tile) => {
        let d = get_distance(tile.position.x, tile.position.z, x, z);
        if (d < 10 && !tileIndexTaken.includes(tile.tileIndex)) {
          closeTiles.push(tile);
        }
      });
      closeTiles.sort((a, b) => 0.5 - Math.random());

      // add flamingo there
      let tile = closeTiles[0];

      if (i == 1) {
        tile = this.hexFloor.allTiles[this.guardianTile];//129];
      }

      tileIndexTaken.push(tile.tileIndex);

      this.hexFloor.allTiles[tile.tileIndex].hasElder = true;
      this.hexFloor.allTiles[tile.tileIndex].elderIndex = i;

      let position = new THREE.Vector3(tile.position.x, tile.scale.y / 2, tile.position.z);
      this.flamingos[i].group.position.copy(position);

      if (isFirst) {
        this.flamingos[i].addAsOtherUser("", i);
      }

      this.flamingos[i].setAction(this.flamingos[i].animationActions['idle'], 'idle');
      this.flamingoBodies[i].position.copy(position);
      this.flamingoBodies[i].quaternion.copy(this.flamingos[i].group.quaternion);

      if (i == 1) {
        this.flamingos[i].setLookAt(new THREE.Vector3(this.originObj.position.x, this.flamingos[i].group.position.y, this.originObj.position.z));
      }
      this.flamingos[i].question = questions[i - 1];
      this.flamingos[i].tileIndex = tile.tileIndex;

    }

  }

  //-----------------------  Main animation


  animate() {

    if (!this.isAnimating)
      return;

    if (this.stats)
      this.stats.update()

    requestAnimationFrame(function () { this.animate(); }.bind(this));

    now = Date.now();
    delta = now - then;
    //update time dependent animations here at 30 fps
    if (delta > interval) {


      let dt = 0;

      if (this.clock)
        dt = this.clock.getDelta();

      if (!this.pauseAnimation) {
        this.controls.update();
        this.renderer.render(this.scene, this.camera);

        return;
      }

      // controls
      if (this.controls) {
        this.controls.update();
      }

      // other flamingos
      if (this.flamingos) {

        if (this.flamingoBodies) {
          this.flamingoBodies.forEach((item, i) => {
            if (i > 0) {
              this.flamingos[i].group.position.copy(item.position);
            }
          });
        }

        this.flamingos.forEach((item, i) => {

          if (i > 0) {

            if (item.npcMode == "waiting" || item.npcMode == "waiting_close") {
              let dist = this.distVec.subVectors(item.prevPos, this.myFlamingo.group.position).length();
              if (dist < 6) {
                item.npcMode = "waiting_close";
              } else {
                item.npcMode = "waiting";
              }
            } else if (item.npcMode == "pool_waiting" && this.levelCompleted) {
              item.npcMode = "pool_dancing";
              item.setAction(item.animationActions['dance'], 'dance');
            }

            if (item.npcMode == "waiting_close" || item.npcMode == "pool_dancing" || item.npcMode == "talking") {
              let lookAtPt = this.lookAtPtVec.set(this.myFlamingo.group.position.x, item.group.position.y, this.myFlamingo.group.position.z);
              item.setLookAt(lookAtPt);

              if (item.npcMode == "talking") {
                item.setAction(null, null);

              }
            } else if (item.npcMode == "flying_to_pool") {
              let lookAtPt = this.lookAtPtVec.set(1.3, item.group.position.y, -1.42);
              item.setLookAt(lookAtPt);
            }


            item.updateAsOther(dt, item.group.position, this.hexFloor.allTiles);


          }
        });
      }

      //---- update envirnoment
      if (this.birds) {
        if (this.seekTargets.length > 0) {
          this.tempVec.copy(this.myFlamingo.group.position);
          this.tempVec.x = this.tempVec.x + Math.sin(this.clock.elapsedTime);
          this.tempVec.z = this.tempVec.z + Math.cos(this.clock.elapsedTime);
          this.tempVec.y += .5;
          this.seekTargets[0] = this.tempVec;
        }
        this.birds.update(dt, this.seekTargets);//,seekTargets,lookAhead);
      }

      if (this.ocean) {
        this.ocean.material.uniforms['time'].value += 0.05 * dt;
      }

      // music analyser for trees
      if (this.musiclm.analyser) {
        this.musiclm.analyser.getByteFrequencyData(this.musiclm.dataArray);
        this.updateTreesWithAnalyser();
      }

      // flamingos speech bubbles
      if (this.currentFlamingoChatIndex) {
        this.updateSpeechBubble();
      }


      if (this.myFlamingo && this.hexFloor && this.wordSoup) {

        let collisions = [];
        let selectables = [];

        if (this.benches) {
          this.benches.forEach((item, i) => {
            collisions.push(item);
          });
        }

        if (this.planks) {
          this.planks.forEach((item, i) => {
            collisions.push(item);
          });
        }

        // collistion with words or sign posts
        let letterMeshes = this.wordSoup.getShowingLetterMeshes();//wordMeshes;
        letterMeshes.forEach((item, i) => {
          selectables.push(item);
        });

        if (this.flagPosts) {
          this.flagPosts.forEach((item, i) => {
            if (item.visible) {
              this.distVec.set(this.myFlamingo.group.position.x, item.position.y, this.myFlamingo.group.position.z);
              item.lookAt(this.distVec);
            }
          });

        }

        if (this.physicsCharacter && !this.myFlamingo.inOpeningSeq) {
          this.lookAtPtVec.copy(this.physicsCharacter.body.position);
          this.lookAtPtVec.y -= .35;
          this.myFlamingo.group.position.lerp(this.lookAtPtVec, .25); // make faster lerp for slow cpus
          this.myFlamingo.update(this.physicsCharacter, dt, this.hexFloor.instanceMesh, collisions, selectables, this.water, this.floorCircle, this.musicClouds, this.hexFloor.allTiles);

        } else {
          this.myFlamingo.update(this.physicsCharacter, dt, this.hexFloor.instanceMesh, collisions, selectables, this.water, this.floorCircle, this.musicClouds, this.hexFloor.allTiles);

        }

        // update close to others
        if (!this.myFlamingo.inOpeningSeq && !this.myFlamingo.inFlyAbout) {
          let prevClose = this.myFlamingo.closeToOther;
          this.myFlamingo.closeToOther = false;

          for (let i = 1; i < this.flamingos.length; i++) {

            let distToOther = this.myFlamingo.group.position.distanceTo(this.flamingos[i].group.position);

            if (distToOther < 2 && !this.flamingos[i].isFollowing && this.flamingos[i].mode != "flyToPool") {
              this.myFlamingo.closeToOther = true;
              this.myFlamingo.isTalking = false;
              this.physicsCharacter.animation = "idle";
            }
          }
        }

        // update camera follow
        if (this.myFlamingo.mode == "moving") {

          this.wordSoup.closeLimit = this.wordSoup.origCloseLimit;

          if (this.posHelperCam && this.followCam) {
            this.updateFollowCam(dt * 100);
          }

        } else if (this.isMobile() && !this.myFlamingo.inOpeningSeq) {
          this.posHelperTarget.position.copy(this.myFlamingo.controlTarget.position);
          this.myFlamingo.group.localToWorld(this.posHelperTarget.position);
          this.controls.target.lerp(this.posHelperTarget.position, 0.1);

        }

        if (this.myFlamingo.mode == "moving") {
          if (this.physicsCharacter && this.myFlamingo.animationName != 'fly') {

            if (this.isMobile()) {
              this.myFlamingo.group.rotation.y = this.myFlamingo.lerpAngle(this.myFlamingo.group.rotation.y, this.physicsCharacter.targetRotation, .15);
            } else {
              this.myFlamingo.group.rotation.y = this.myFlamingo.lerpAngle(this.myFlamingo.group.rotation.y, this.physicsCharacter.targetRotation, .25);
            }

          }
        }

        if (this.sparkles && this.levelCompleted)
          this.sparkles.updateAsSparkles(dt, this.myFlamingo.group.position);
      }

      //---  physics
      if (this.world) {
        //this.world.fixedStep();
        this.world.step(1 / 60, dt);

        if (this.balls) {
          this.balls.forEach((item, i) => {
            if (!item.dead) {
              item.position.copy(this.ballBodies[i].position);
              item.quaternion.copy(this.ballBodies[i].quaternion);
            }

          });
        }

      }

      if (this.physicsCharacter) {
        this.physicsCharacter.update(dt, this.camera);
      }

      // update words in the space
      if (this.wordSoup && this.myFlamingo) {
        if (!this.myFlamingo.inOpeningSeq)
          this.wordSoup.update(dt, this.flamingos, this.camera);//,this.birds.boids);
      }


      if (this.floorCircle && this.levelCompleted) {
        if (this.floorCircle.isMoving) {
          this.floorCircle.rotation.y += this.floorCircle.rotationSpeed;
        }
      }

      // plants
      if (this.plantMesh) {
        this.plantMesh.instanceMatrix.needsUpdate = true;
        this.plantMesh.computeBoundingSphere();
      }

      // clouds
      if (this.musicClouds && this.musiclm.dataArray && this.levelCompleted) {
        this.updateCloudsWithAnalyzer();
      }

      // minimap
      if (this.myFlamingo) {
        this.updateMiniMap(this.myFlamingo.group.position);
      }

    }

    // RENDER
    if (this.renderer)
      this.renderer.render(this.scene, this.camera);

  }

  //----------------------- Follow cam
  updateFollowCam(dt) {
    this.posHelperCam.position.copy(this.myFlamingo.camTarget.position);
    this.myFlamingo.group.localToWorld(this.posHelperCam.position);
    this.posHelperTarget.position.copy(this.myFlamingo.controlTarget.position);
    this.myFlamingo.group.localToWorld(this.posHelperTarget.position);

    this.camera.position.lerp(this.posHelperCam.position, 0.5);
    this.controls.target.lerp(this.posHelperTarget.position, 0.5);

  }


  //-----------------------  World interactions
  flamingoCaughtAWord() {
    if (this.wordSoup.holdingWordInBeak) {
      if (this.wordSoup.wordShowing) {
        this.addPromptToWordBank(this.wordSoup.wordShowing.name);
        this.wordSoup.flamingoSelectedWord();
      }

    } else {

      this.wordSoup.flamingoSelectedWord();
    }
    this.soundEffects.playClip(this.instrument);



  }

  touchPlay() {


    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersects = this.raycaster.intersectObjects(this.clickableObjects);//this.scene.children );

    if (intersects.length == 0)
      return;

    let intersectNames = [];
    let nameToIndex = {};

    for (let i = 0; i < intersects.length; i++) {

      let name = intersects[i].object.name;
      // console.log("clicked ", name);
      intersectNames.push(name);
      nameToIndex[name] = i;
      if (i == 0 && name == "hex" && this.isDoubleClick) {
        let instanceId = intersects[i].instanceId;

        let targPoint = null;
        if (intersects[i].normal.y == 1) {
          targPoint = intersects[i].point;
        }
        this.doUserClickedTileWhileZoomed(instanceId, targPoint);
      } else if (i == 0 && name == "water" && this.isDoubleClick) {
        this.doUserClickedWaterOrPoolWhileZoomed(intersects[i].point);
      } else if (i == 0 && name == "floor" && this.isDoubleClick) {
        this.doUserClickedWaterOrPoolWhileZoomed(intersects[i].point);
      } else if (i == 0 && name == "bridge" && this.isDoubleClick) {
        this.doUserClickedWaterOrPoolWhileZoomed(intersects[i].point);
      } else if (i == 0 && name == "bench" && this.isDoubleClick) {
        this.doUserClickedWaterOrPoolWhileZoomed(intersects[i].point);

      }
    }


  }

  doFlamingoEats() {
    let duration = 60;
    this.myFlamingo.doEatingAnimation();

  }

  doSitOnBench() {

    if (this.myFlamingo.currentTileIndex == null || !this.myFlamingo.currentTileIndex)
      return;

    let tile = this.hexFloor.allTiles[this.myFlamingo.currentTileIndex];

    if (tile.hasBench && this.levelCompleted) {

      let pos = this.benches[tile.benchIndex].position;
      this.onBenchPos = new THREE.Vector3(pos.x, pos.y + 1, pos.z);

    } else {
      return;
    }

    this.onBench = true;
    if (this.myFlamingo.mode == "moving") {
      this.myFlamingo.mode = "pause_moving";
    }
    gsap.to(this.physicsCharacter.body.position, { x: this.physicsCharacter.body.position.x, y: this.onBenchPos.y, z: this.physicsCharacter.body.position.z, duration: .1 })
    gsap.to(this.physicsCharacter.body.position, {
      x: this.onBenchPos.x, y: this.onBenchPos.y, z: this.onBenchPos.z, duration: .5, delay: 1, onComplete: function () {
        this.distVec.set(0, this.myFlamingo.group.position.y, 0);
        //this.myFlamingo.group.lookAt(this.distVec);
        this.myFlamingo.mode = "pause_moving";
      }.bind(this)
    });

  }

  didJump() {

    if (this.levelCompleted) {
      if (!this.jumpsCount) {
        this.jumpsCount = 0;
      }

      clearTimeout(this.jumpTM2);

      if (!this.jumpTM) {

        this.jumpsCount++;

        this.jumpTM2 = setTimeout(function () {
          this.jumpsCount = 0;
        }.bind(this), 3000);

        if (this.jumpsCount == 3 && this.myFlamingo.environment != null) {

          if (this.myFlamingo.getEnvironment().includes("water") && this.myFlamingo.group.position.y < 1) {
            this.playInParkSong("pond");
            this.handleAxlTrack("play_pond", this.levelName);
            this.physicsCharacter.animation = 'dance';

          } else if ((this.myFlamingo.environment == "floor") && this.myFlamingo.group.position.y < 1) {
            this.playInParkSong("floor");
            this.physicsCharacter.animation = 'dance';
            this.handleAxlTrack("play_floor", this.levelName);

          }

          this.jumpsCount = 0;

        }
      }
    }
  }

  updateBadge(id) {

    if (id == "treeslow") {
      id = "trees";
    }

    let query = "span[data-id='" + id + "']";

    let badgeEl = document.querySelector("span[data-id='" + id + "']");
    if (badgeEl)
      badgeEl.classList.add('completed');
  }

  async playInParkSong(id) {
    let prefix = id + "prefix";
    let vol = (this.isMuted()) ? 0 : this.currentSongVol;

    this.updateBadge(id);

    if (this.resetParkSongTM)
      clearTimeout(this.resetParkSongTM)

    let playIt = await this.setMusicFromPreloadedTrack(id, vol, "prefix");

    if (playIt) {
      let nPrompt = levelConfig[this.levelName][prefix];

      if (id == "final") {
        this.musiclm.preloadMusicLMTrack(nPrompt, id, this.levelName, prefix, "final");
      } else {
        this.musiclm.preloadMusicLMTrack(nPrompt, id, this.levelName, prefix);
      }

      // this.setHelperHint("played-"+id);
      // this.showHelperHint(3000,"play-park");
      // this.setFeedback("played-"+id);
      // this.showFeedback(3000);

      if (id == "pond") {
        this.bubbles.doBubbles();
      } else if (id == "floor") {
        this.floorCircle.rotationSpeed = 0;
        gsap.to(this.floorCircle, { rotationSpeed: .0005, duration: .75 });
        this.floorCircle.isMoving = true;
      }
    } else {
      // play even if no song plays?
      if (id == "pond") {
        this.bubbles.doBubbles();
      }

    }

  }

  async playTheTree(treeIndex) {

    if (this.allEldersFound.length > 0) {

      if (!this.prevPlayedTreeIndex) this.prevPlayedTreeIndex = treeIndex;
      this.prevPlayedTreeIndex = this.playedTreeIndex;
      this.playedTreeIndex = treeIndex;


      if (treeIndex == this.centerTreeIndex) {

        this.playInParkSong("centertree");
        this.handleAxlTrack("play_tree", this.levelName);

      } else if (this.pineTrees[treeIndex].isColoredIn && this.pineTrees[treeIndex].isMusical) {

        this.playInParkSong("trees");
        this.handleAxlTrack("play_tree", this.levelName);

      } else if (this.pineTrees[treeIndex].isColoredIn) {

        this.playInParkSong("treeslow");
        this.handleAxlTrack("play_tree", this.levelName);

      }


    }
  }

  startCloudEffect(index) {
    let cloud = this.musicClouds[index];
    cloud.traverse((item) => {
      if (item.isMesh) {
        this.tweenColor(item.material, item.material.color, new THREE.Color(0xeaacd4), 0, .35);
        //item.material.color: new THREE.Color(colors[this.instrument])});
      }
    });
    cloud.isColoredIn = true;
    this.cloudsOn = true;
  }

  endCloudEffect() {
    this.musicClouds.forEach((cloud, i) => {
      cloud.traverse((item) => {
        if (item.isMesh && cloud.isColoredIn) {
          this.tweenColor(item.material, item.material.color, new THREE.Color(0xffffff), 0, .35);
        }
      });
      cloud.isColoredIn = false;
    });
    this.cloudsOn = false;

  }

  //----------------------- Elder dialogs
  playDialog(fIndex, convoId, foundSolution, startAtIndex = 0) {

    if (this.isMobile()) {
      this.deactivateMobileControls();
      this.hidePlayerButtonsAndUI();
    }

    // double check don't play twice ?
    this.interruptDialog();

    let ppEl = document.getElementById("pause-play-dialog");
    ppEl.classList.remove("hide");
    let rwEl = document.getElementById("rewind-dialog");
    rwEl.classList.add("hide");

    let timer = 0;
    this.dialogPart = 0;
    this.lastSpeaker = null;
    this.currentConvoId = convoId;
    this.foundCurrentSolution = foundSolution;
    this.currentDialogs = [];
    this.dialogState = "dialog";

    let talkingIndex = fIndex;
    if (convoId == "ending" || convoId == "playpark") {
      talkingIndex = this.allEldersFound[0];
    }


    document.getElementById("pause-play-dialog").setAttribute("data-state", "playing");


    if (convoId == "greeting") {

      let dialogs = this.dialogManager.getDialog(this.levelName, fIndex, "greeting");
      this.currentDialogs = JSON.parse(JSON.stringify(dialogs));
      let riddle = this.dialogManager.getDialog(this.levelName, fIndex, "riddles", { "part": "riddle" });
      riddle[0]['isRiddle'] = true;
      this.currentDialogs.push(riddle[0]);


      this.setDialogProgressBar(dialogs.length + 1, startAtIndex);

      let lastText = "";

      this.currentDialogs.forEach((item, i) => {

        if (i >= startAtIndex) {
          if (i > 0 && startAtIndex == 0) {
            timer += this.dialogManager.getReadTimeSeconds(lastText) * 1000;
          } else if (i > startAtIndex) {
            timer += this.dialogManager.getReadTimeSeconds(lastText) * 1000;

          }

          let tm = setTimeout(function () {
            if (this.flamingos[fIndex].npcMode == "talking") {
              this.playDialogPart(this.currentDialogs, i);
              if (/*foundSolution && */item.hasOwnProperty('isRiddle')) {
                tm = setTimeout(function () { this.showWordBank() }.bind(this), 1000);
                this.elderQTms.push(tm);
              }
              this.dialogPart = i;
            }
          }.bind(this), timer);
          this.elderQTms.push(tm);

          lastText = item.dialog;

        }

      });


    } else if (convoId == "ending") {
      let dialogs = this.dialogManager.getDialog(this.levelName, fIndex, "ending");
      let lastText = "";

      this.setDialogProgressBar(dialogs.length + 1, startAtIndex);

      this.currentDialogs = JSON.parse(JSON.stringify(dialogs));

      this.currentDialogs.forEach((item, i) => {

        if (i > 0) {
          timer += this.dialogManager.getReadTimeSeconds(lastText) * 1000;
        }

        let tm = setTimeout(function () {
          this.playDialogPart(this.currentDialogs, i);
          this.dialogPart = i;
        }.bind(this), timer);
        this.elderQTms.push(tm);

        lastText = item.dialog;
      });

      timer += this.dialogManager.getReadTimeSeconds(lastText) * 1000;

    } else {
      let lastText = "";
      let dialogs = this.dialogManager.getDialog(this.levelName, fIndex, convoId);

      this.setDialogProgressBar(dialogs.length + 1, startAtIndex);


      this.currentDialogs = JSON.parse(JSON.stringify(dialogs));

      this.currentDialogs.forEach((item, i) => {

        if (i > 0) {
          timer += this.dialogManager.getReadTimeSeconds(lastText) * 1000;
        }


        let tm = setTimeout(function () {
          if (this.flamingos[talkingIndex].npcMode == "talking") {
            this.playDialogPart(this.currentDialogs, i);
            this.dialogPart = i;
          }
        }.bind(this), timer);
        this.elderQTms.push(tm);

        lastText = item.dialog;
      });

    }

    this.lastDialogData = { fIndex: fIndex, convoId: convoId, foundSolution: foundSolution };
    return timer;
  }

  handleReplayDialog() {
    if (this.lastDialogData) {
      this.elderQTms.forEach((item, i) => {
        clearTimeout(item);
      });
      this.chatDialog.hideAllChats();
      this.chatDialog.clearAllChats();
      this.playDialog(this.lastDialogData.fIndex, this.lastDialogData.convoId, this.lastDialogData.foundSolution);
      document.getElementById('rewind-dialog').classList.add("hide");
      document.getElementById('pause-play-dialog').classList.remove("hide");

      if (this.isMobile()) {
        this.showReplayButton();
      }

    }
  }

  setDialogProgressBar(totalParts, startAtIndex) {
    let progressBar = document.getElementById("dialog-progress");
    progressBar.innerHTML = "";
    for (let i = 0; i < totalParts; i++) {
      let li = document.createElement("li");
      progressBar.appendChild(li);
      if (i < startAtIndex)
        li.classList.add("played");
    }
  }

  pauseDialog() {
    this.elderQTms.forEach((item, i) => {
      clearTimeout(item);
    });
    this.elderQTms = [];
    this.dialogPart++;
  }

  unpauseDialog() {

    this.chatDialog.hideAllChats("young");
    this.playDialog(this.currentFlamingoChatIndex, this.currentConvoId, this.foundCurrentSolution, this.dialogPart);//his.currentDialogPart);
  }

  interruptDialog() {
    this.elderQTms.forEach((item, i) => {
      clearTimeout(item);
    });

    //if(this.elderQTms.length > 0){
    this.chatDialog.clearAllChats();
    //}
    this.elderQTms = [];

    document.getElementById("pause-play-dialog").setAttribute("data-state", "paused");
  }

  handleSkipDialog() {
    this.interruptDialog();

    let lastPart = this.currentDialogs.length - 1;

    //if(this.dialogPart < lastPart){
    this.dialogPart = lastPart + 1;
    this.chatDialog.hideAllChats();
    this.chatDialog.hideAllChats("young");
    this.playDialogPart(this.currentDialogs, lastPart);
    //}

    this.hideReplayButton();
    if (this.isMobile())
      this.showPlayerButtonsAndUI();

  }

  handlePausePlayDialog() {
    let pausePlayButton = document.getElementById("pause-play-dialog");
    let state = pausePlayButton.getAttribute("data-state");
    if (state == "playing") {
      pausePlayButton.setAttribute("data-state", "paused");
      this.pauseDialog();
    } else if (state == "paused") {
      pausePlayButton.setAttribute("data-state", "playing");
      this.unpauseDialog();
    } else if (state == "replay") {
      this.handleReplayDialog();
      pausePlayButton.setAttribute("data-state", "playing");
    }
  }

  playNextDialogPart() {

    if (!this.currentDialogs || this.dialogPart >= this.currentDialogs.length) {
      return;
    }

    //this.interruptDialog();
    this.elderQTms.forEach((item, i) => {
      clearTimeout(item);
    });

    this.elderQTms = [];

    let ppEl = document.getElementById("pause-play-dialog");
    if (ppEl && ppEl.getAttribute("data-state") == "playing") {
      ppEl.setAttribute("data-state", "paused");
      this.dialogPart++;
    }

    if (this.dialogPart >= this.currentDialogs.length) {
      return;
    }

    if (this.currentDialogs[this.dialogPart] && this.currentDialogs[this.dialogPart].speaker == 'elder') {
      this.chatDialog.hideAllChats("young");
    } else {
      this.chatDialog.hideAllChats();
    }
    this.playDialogPart(this.currentDialogs, this.dialogPart);

    this.dialogPart++;
  }

  playPrevDialogPart() {
    if (this.dialogPart <= 1)
      return;
    this.interruptDialog();
    this.dialogPart -= 2;
    this.dialogPart = Math.max(0, this.dialogPart);
    this.chatDialog.hideAllChats();
    this.chatDialog.hideAllChats("young");
    this.playDialogPart(this.currentDialogs, this.dialogPart);
    this.dialogPart += 1;
  }

  updateDialogProgressBar(progressMarker) {
    let lis = document.querySelectorAll(".dialog-controls li");
    lis.forEach((item, i) => {
      if (i <= progressMarker) {
        item.classList.add("played");
      } else {
        item.classList.remove("played");
      }
    });

  }

  playDialogPart(dialogs, index) {

    let item = dialogs[index];

    //this.currentDialogPart = index;

    if (item.hasOwnProperty('speaker')) {
      if (item.speaker != this.lastSpeaker) {
        this.chatDialog.hideAllChats(this.lastSpeaker);
      }
    }

    let classToAdd = null;
    if (item.isRiddle) classToAdd = "riddle";
    else if (item.parable) classToAdd = "parable";

    this.chatDialog.addChat(item.dialog, item.speaker, null, classToAdd);
    this.lastSpeaker = item.speaker;

    // on mobile, if it is last of ending dialog, show the controls
    if (this.isMobile() && index == dialogs.length - 1 && !this.currentConvoId == "ending") {
      let tm = setTimeout(function () { this.showMobileControls(); }.bind(this), 1000);
      this.elderQTms.push(tm);
    }

    // if it is the riddle and have the solution, show the word bank
    if (/*this.foundCurrentSolution && */item.hasOwnProperty('isRiddle')) {
      let tm = setTimeout(function () { this.showWordBank() }.bind(this), 1000);
      this.elderQTms.push(tm);
    }

    // if it is the ending dialog and last one, play the image popup
    if (this.currentConvoId == "ending" && index >= dialogs.length - 1) {
      let tm = setTimeout(function () { this.playEndingImageAndUI(); }.bind(this), 2000);
      this.elderQTms.push(tm);
    }

    // if it is last greeting (riddle), set mode to riddle and show helper hint
    if (this.currentConvoId == "greeting" && index == dialogs.length - 1) {
      this.flamingos[this.currentFlamingoChatIndex].npcInteractionMode = "riddle";
      this.dialogState = "riddle";

      if (this.currentFlamingoChatIndex == 1) {
        this.didGuardian = true;
        this.colorFlagPosts();
        this.blinkElderAndFlagTiles();
      }
    }

    // if last one set play/pause to rewind
    if (index == dialogs.length - 1) {
      let ppEl = document.getElementById("pause-play-dialog");
      ppEl.classList.add("hide");
      let rwEl = document.getElementById("rewind-dialog");
      rwEl.classList.remove("hide");
    }

    // if playing in park (deprecated?)
    if (this.currentConvoId == "playpark" && index >= dialogs.length - 1) {
      let tm = setTimeout(function () { this.exitElderConversation(this.currentFlamingoChatIndex) }.bind(this), 6000);
      this.elderQTms.push(tm);
    }

    this.updateDialogProgressBar(index);
  }

  showReplayButton() {
    document.querySelector(".dialog-controls").classList.remove("hide");
    document.querySelector(".dialog-controls").classList.add("show");
  }

  hideReplayButton() {
    document.querySelector(".dialog-controls").classList.add("hide");
    document.querySelector(".dialog-controls").classList.remove("show");

  }

  hidePlayerButtonsAndUI() {
    let promptItems = document.querySelectorAll(".prompt-display-item");
    promptItems.forEach((item, i) => {
      item.classList.remove("show");
    });
  }

  showPlayerButtonsAndUI() {
    let promptItems = document.querySelectorAll(".prompt-display-item");
    promptItems.forEach((item, i) => {
      item.classList.add("show");
    });
  }

  //----------------------- Flamingo speech bubbles
  updateSpeechBubble() {

    if ((this.isMobile() && window.innerWidth < window.innerHeight) || (window.innerWidth < window.innerHeight && window.innerWidth < 900)) {
      document.getElementById("chat-container").style.bottom = "58vh";
      document.getElementById("chat-container").style.left = "unset";
      document.getElementById("chat-container").style.right = "unset";

      document.getElementById("chat-container-young").style.bottom = "58vh";
      document.getElementById("chat-container-young").style.left = "unset";
      document.getElementById("chat-container-young").style.right = "unset";
      document.getElementById("chat-container").classList.remove("right");
    } else {

      let wordBankOpen = document.getElementById("word-bank").classList.contains("show");

      let elder = this.flamingos[this.currentFlamingoChatIndex];
      if (elder.npcMode == "talking" || elder.npcMode == "tofly") {
        if (!this.speechBubblePos) {
          this.speechBubblePos = new THREE.Vector3();
        }
        this.speechBubblePos.copy(this.flamingos[this.currentFlamingoChatIndex].headP.position);
        let dPosition = this.speechBubblePos;
        this.flamingos[this.currentFlamingoChatIndex].corps.localToWorld(dPosition);
        dPosition.y -= .25;
        dPosition.applyMatrix4(this.camera.matrixWorldInverse);
        dPosition.applyMatrix4(this.camera.projectionMatrix);

        let x = (dPosition.x + 1) * window.innerWidth / 2;
        let y = - (dPosition.y - 1) * window.innerHeight / 2;

        document.getElementById("chat-container").style.bottom = (window.innerHeight - y) + "px";

        let bufferX = window.innerWidth * .05;
        if (x > (window.innerWidth / 2) /*|| wordBankOpen*/) {
          let bb = document.getElementById("chat-container").getBoundingClientRect();
          document.getElementById("chat-container").style.left = (x - bb.width - bufferX) + "px";
          document.getElementById("chat-container").classList.remove("right");

        }
        else {
          document.getElementById("chat-container").style.left = (x + bufferX) + "px";
          document.getElementById("chat-container").classList.add("right");
        }


        // update young
        this.speechBubblePos.copy(this.myFlamingo.headP.position);
        dPosition = this.speechBubblePos;
        this.myFlamingo.corps.localToWorld(dPosition);
        dPosition.y -= .1;
        dPosition.applyMatrix4(this.camera.matrixWorldInverse);
        dPosition.applyMatrix4(this.camera.projectionMatrix);
        x = (dPosition.x + 1) * window.innerWidth / 2;
        y = - (dPosition.y - 1) * window.innerHeight / 2;
        document.getElementById("chat-container-young").style.bottom = (window.innerHeight - y) + "px";


        bufferX = window.innerWidth * .05;
        if (x > (window.innerWidth / 2) + 100 || wordBankOpen) {
          let bb = document.getElementById("chat-container-young").getBoundingClientRect();
          document.getElementById("chat-container-young").style.left = (x - bb.width - bufferX) + "px";
          document.getElementById("chat-container-young").classList.remove("right");

        }
        else {
          document.getElementById("chat-container-young").style.left = (x + bufferX) + "px";
          document.getElementById("chat-container-young").classList.add("right");
        }

      }



    }

  }

  updateEndLevelFlamingoDialogs() {

    if (this.myFlamingo.environment != "water" && this.currentFlamingoChatIndex != null) {
      this.currentFlamingoChatIndex = null;
      this.chatDialog.hideAllChats();
      return;
    }

    let closestDist = 100000;
    let closestIndex = null;
    let limit = 1;

    for (let i = 1; i < this.flamingos.length; i++) {
      let dist = this.myFlamingo.group.position.distanceTo(this.flamingos[i].group.position);
      if (dist < limit && dist < closestDist) {
        closestIndex = i;
        closestDist = dist;
      }
    }

    if (closestIndex != null && this.currentFlamingoChatIndex != closestIndex) {
      this.currentFlamingoChatIndex = closestIndex;
      this.flamingos[this.currentFlamingoChatIndex].npcMode = "talking";
      this.chatDialog.clearAllChats();
      let text = this.translator.getDynamicText(this.levelName + "_parable_" + closestIndex);
      this.chatDialog.addChat(text, "elder");
    } else {
      //this.chatDialog.hideAllChats();
    }
  }


  // ----- Elder encounters
  async enterElderConversation(fIndex) {
    this.handleAxlTrack("talk_to_elder", this.levelName, fIndex);

    if (fIndex == 1 && !this.didBlinkFlags) {
      this.blinkElderAndFlagTiles();
      this.didGuardian = true;
    }

    // clear any previous chat timeouts
    this.elderQTms.forEach((item, i) => {
      clearTimeout(item);
    });

    if (this.isMobile()) {
      this.wasMiniMapOpen = document.querySelector(".minimap").classList.contains("show");
      this.closeMiniMap();
    }

    // clear the chat
    this.chatDialog.clearChat();
    this.chatDialog.clearChat("young");

    // update which words have prev been used for this flamingo
    this.updateWordBankForElder(fIndex);

    // fade out current audio track
    // if(!this.isMuted())
    //   this.musiclm.fadeOutSource(3,.15);

    //set states: need both states?
    this.dialogState = "encounter";
    this.chatState = "question";
    this.currentFlamingoChatIndex = fIndex;

    // hide helper bubbles
    this.closeHelperHint();

    // update flamingo modes
    this.flamingos[fIndex].npcMode = "talking";
    this.flamingos[fIndex].fadeToPink();

    if (this.wordSoup.holdingWordInBeak)
      this.wordSoup.wordIsPlaying();

    // play flamingo calls / dance as greeting
    this.soundEffects.playClip("calls", .25);
    this.flamingos[fIndex].setAction(this.flamingos[fIndex].animationActions['dance'], 'dance');
    setTimeout(function () {
      this.flamingos[fIndex].setAction(this.flamingos[fIndex].animationActions['idle'], 'idle');
    }.bind(this), 2500);

    //------------ make  chat dialog
    let mode = this.flamingos[fIndex].npcInteractionMode;
    let solution = this.flamingos[fIndex]['question']['solution'];
    let foundSolution = this.promptWordBank.includes(this.flamingos[fIndex]['question']['solution']);

    let tm;


    if (fIndex == 1 && mode == "first") {

      mode = "first-guardian";
      this.playDialog(fIndex, "greeting", foundSolution);
      this.showReplayButton();


    } else if (mode == "first" && !this.didGuardian) {
      let text = this.translator.getDynamicText("speak-to-guardian");
      this.chatDialog.addChat(text, "elder", null, "loading");

    } else if (mode == "first") {

      this.playDialog(fIndex, "greeting", foundSolution);
      this.showReplayButton();

    } else {

      // tell riddle on return encounter
      this.chatDialog.addChat('...', "elder", null, "loading");
      let word = this.flamingos[fIndex]['question']['solution'];

      let dialog = this.dialogManager.getDialog(this.levelName, fIndex, "riddles", { "part": "riddle" });
      let response = { "text": dialog[0]['dialog'] };

      if (response.hasOwnProperty('text')) {

        if (this.flamingos[fIndex].npcMode == "talking") {

          this.chatDialog.addChat(response.text, "elder", null, "riddle");
          this.flamingos[fIndex].npcInteractionMode = "riddle";

          //if(foundSolution){
          tm = setTimeout(function () { this.showWordBank() }.bind(this), 1000);
          this.elderQTms.push(tm);
          //}
        }

      }


    }

    if (!this.didGuardian && mode == "first-guardian") {
      this.setHelperHint("hint-guardian", "primary");
    }
    // else if(foundSolution){
    //   this.setHelperHint("hint-words-single-hasword","primary");
    // }
    else {
      this.setHelperHint("hint-words", "primary");
    }

    // setTimeout( function(){
    //   let tile = this.hexFloor.allTiles[ this.myFlamingo.currentTileIndex ];
    //   if(tile.hasElder){
    //     this.moveDir.set(0,0,0);
    //     this.moveDir.subVectors( this.flamingos[this.currentFlamingoChatIndex].group.position,this.myFlamingo.group.position ).y = 0;
    //     let angle =  Math.atan2( this.moveDir.x, this.moveDir.z );
    //     gsap.to(this.myFlamingo.group.rotation,{y:angle,duration:.5,ease:"power2.inOut"});
    //   }
    // }.bind(this), 1000);


  }

  exitElderConversation(fIndex) {

    if (this.isMobile()) {
      this.showPlayerButtonsAndUI();
      if (this.wasMiniMapOpen) {
        this.openMiniMap();
      }
    }

    if (this.elderQTms) {
      this.elderQTms.forEach((item, i) => {
        clearTimeout(item);
      });
    }
    this.chatDialog.hideAllChats();
    this.chatDialog.hideAllChats("young");
    this.hideWordBank();
    this.hideReplayButton();
    if (this.wordSoup.holdingWordInBeak)
      this.wordSoup.wordIsPlaying();

    // end  conversation
    if (fIndex != null) {
      if (this.flamingos[fIndex].npcMode == "tofly") {
        this.flamingos[fIndex].npcMode = "completed";
        let tileIndex = this.flamingos[fIndex].tileIndex;
        this.hexFloor.allTiles[tileIndex].hasElder = false;

        this.stopBlinkTile(tileIndex);
        this.elderFlamingoFliesAway(fIndex);
      } else if (!this.levelCompleted) {
        this.flamingos[fIndex].makeGray();
        let tileIndex = this.flamingos[fIndex].tileIndex;
        if (this.hexFloor.allTiles[tileIndex].hasOwnProperty('blinkTL')) {
          this.hexFloor.allTiles[tileIndex].blinkTL.resume();
        }
      }

      this.flamingos[fIndex].npcMode = "waiting_close";
    }

    if (this.levelCompleted && !this.didEndLevelPond && !this.myFlamingo.getEnvironment().includes("water")) {
      // console.log("didEndLevelPond",this.myFlamingo.environment);
      this.didEndLevelPond = true;
      document.getElementById("end-level-pond-popup").classList.add("show");
      this.soundEffects.playClip("win");
      setTimeout(() => {
        document.getElementById("end-level-pond-popup").classList.remove("show");
      }, 15000);
    } else {
      // console.log("could not pay end level pond",this.levelCompleted,this.didEndLevelPond,this.myFlamingo.getEnvironment());
    }

    if ((fIndex == 1 && this.allEldersFound.length == 0) || (this.levelCompleted && !this.didLevelEnd)) {
      this.showHelperHint();
    }


  }

  async answeredFlamingo(word) {

    this.hideReplayButton();

    let fIndex = this.currentFlamingoChatIndex;

    if (this.flamingos[fIndex].npcMode != "talking")
      return;

    // if did not speak to guardian first
    if (fIndex != 1 && !this.didGuardian) {
      let text = this.translator.getDynamicText("speak-to-guardian");
      this.chatDialog.addChat(text, "elder", null, "loading");
      let tm = setTimeout(function () {
        if (this.wordSoup.holdingWordInBeak)
          this.wordSoup.wordIsPlaying();
      }.bind(this), 2000);
      this.elderQTms.push(tm);
      return;
    }


    this.interruptDialog();
    this.chatDialog.hideAllChats("young");
    if (this.dialogState == "dialog") {
      this.chatDialog.hideAllChats("elder");
    }
    if (this.elderQTms) {
      this.elderQTms.forEach((item, i) => {
        clearTimeout(item);
      });
    }

    let elder = this.flamingos[fIndex];

    this.dialogState = "you-answered";
    this.flamingos[fIndex].npcGuesses++;
    let solution = this.flamingos[fIndex]['question']['solution'];

    if (word == this.flamingos[fIndex]['question']['solution']) {

      // correct
      this.handleAxlTrack("correct_answer", word, fIndex);

      this.gaveCorrectAnswer(word, fIndex, solution);

      let tm = setTimeout(function () {
        if (this.wordSoup.holdingWordInBeak)
          this.wordSoup.wordIsPlaying();
      }.bind(this), 2000);
      this.elderQTms.push(tm);

      if (fIndex == 1) {
        this.didGuardian = true;
        this.colorFlagPosts();
        this.blinkElderAndFlagTiles();
      }
    } else {
      // incorrect
      this.handleAxlTrack("incorrect_answer", word, fIndex);

      this.gaveIncorrectAnswer(word, fIndex, solution);

      let tm = setTimeout(function () {
        if (this.wordSoup.holdingWordInBeak)
          this.wordSoup.wordIsPlaying();
      }.bind(this), 1000);
      this.elderQTms.push(tm);

    }



  }

  async gaveCorrectAnswer(word, fIndex, solution) {

    this.soundEffects.playClip("ding");

    this.flamingos.forEach((flamingo) => {
      flamingo.npcWordsGuessed.push(word);
    });

    this.flamingos[fIndex].fadeToPinkStrong(this.levelName);

    this.chatDialog.addChat('...', "elder", null, "loading");

    let mode = 'correct';
    let isFinalRiddle = this.eldersFound >= (this.maxFlamingos - 2) ? true : false;

    let dialog = this.dialogManager.getDialog(this.levelName, fIndex, "riddles", { "part": "correct" });

    let response = { "text": dialog[0]['dialog'] };

    if (isFinalRiddle) {
      response.text += this.translator.getDynamicText("elder-says-solved");//" You solved all the riddles. <span>Head back to the pond</span> to receive some departing wisdom.";
    } else {
      let totalLeft = (this.maxFlamingos - 1) - (this.allEldersFound.length + 1);
      if (totalLeft == 1) {
        response.text += this.translator.getDynamicText("elder-says-one-left");
      } else {
        response.text += this.translator.getDynamicText("elder-says-there-are") + this.translator.getNumberAsWord(totalLeft) + this.translator.getDynamicText("elder-says-left");
      }
    }

    if (this.flamingos[fIndex].npcMode == "talking") {
      if (response.hasOwnProperty('text')) {
        this.chatDialog.addChat(response.text, "elder", null, "correct");
      }
    }

    this.solvedWords.push(word);
    this.flamingos[fIndex].npcWordsGuessed.push(word);
    this.updateWordBankForElder(fIndex);

    this.completedConversation(fIndex);

    if (isFinalRiddle) {
      this.setHelperHint("hint-return", "primary");
    } else {
      this.setHelperHint("hint-more-elders", "primary");
    }

    this.resetWordBankAfterCorrectAnswer();
  }

  async gaveIncorrectAnswer(word, fIndex, solution) {

    let elder = this.flamingos[fIndex];
    console.log("InCorrect");

    let mode = 'incorrect-needsword';
    if (this.promptWordBank.includes(this.flamingos[fIndex]['question']['solution']))
      mode = 'incorrect-hasword';

    this.chatDialog.addChat('...', "elder", null, "loading");

    let response = {};
    let guessStage = (elder.npcGuesses - 1) % 3;
    if (guessStage == 0) {
      let dialog = this.dialogManager.getDialog(this.levelName, fIndex, "riddles", { "part": "incorrect" });
      response = { "text": dialog[0]['dialog'] };
    } else if (guessStage == 1) {
      let dialog = this.dialogManager.getDialog(this.levelName, fIndex, "riddles", { "part": "hint" });
      response = { "text": dialog[0]['dialog'] };
    } else {
      response = { "text": "Not quite..." };
    }

    this.chatDialog.addChat(response.text, "elder", null);

    this.flamingos[fIndex].npcWordsGuessed.push(word);
    this.updateWordBankForElder(fIndex);

    if (mode == 'incorrect-hasword') {
      this.showWordBank();
    }

    this.myFlamingo.colorInMode = false;

    if (mode == 'incorrect-hasword') {
      this.setHelperHint("hint-words-single-hasword", "primary");
    } else {
      this.setHelperHint("hint-words-needsword", "primary");
      this.showHelperHint();
    }
  }

  async completedConversation(fIndex) {

    // update elders found
    if (!this.allEldersFound.includes(fIndex)) {
      this.allEldersFound.push(fIndex);
      this.eldersFound = this.allEldersFound.length;
      this.updateElderUi();
    }

    if (this.isMobile()) {
      this.showMobileControls();
    }

    // check level Complete?
    if (this.allEldersFound.length >= this.maxFlamingos - 1) {
      this.colorAllTiles(0);
      this.colorInAllTrees();
      if (this.levelName == "spirit") {
        this.colorInBridge();
      }
      this.levelComplete();

      this.resetWordBankForStayAndPlay();

      this.setHelperHint("hint-return", "primary");
    }

    //set song
    let waVIndex = this.eldersFound - 1;
    let wavId = this.flamingos[fIndex]['question']['solution'];

    if (this.levelCompleted) {
      wavId = "final";
    }

    let vol = 1;//this.levelCompleted ? .75 : .5;
    let time = this.levelCompleted ? 2 : 1;
    this.currentSongVol = vol;
    if (this.isMuted()) {
      vol = 0;
    }

    if (this.levelName != "custom") {
      if (this.resetParkSongTM)
        clearTimeout(this.resetParkSongTM)

      this.setMusicFromPreloadedTrack(wavId, vol, "keyword", this.flamingos[fIndex]['question']['solution']);
    }

    // update color of current tile and those nearby

    if (this.myFlamingo.environment == "hex") {
      this.colorInTileAndTileObjects(this.myFlamingo.currentTileIndex);
      let tile = this.hexFloor.allTiles[this.myFlamingo.currentTileIndex];
      this.hexFloor.allTiles.forEach((item, i) => {
        if (i != this.myFlamingo.currentTileIndex) {
          let d = get_distance(item.position.x, item.position.z, this.myFlamingo.group.position.x, this.myFlamingo.group.position.z);
          if (d < 12 && !item.hasElder) {
            setTimeout(function () {
              this.colorInTileAndTileObjects(item.tileIndex, 1);

            }.bind(this), .25 * 1000);

          }
        }

      });


    }

    for (let i = 0; i < 2; i++) {
      this.updateRug();
    }

    // elder flamingo flies off
    this.flamingos[fIndex].npcMode = "tofly";
    let fTm = setTimeout(function () {
      if (this.flamingos[fIndex].npcMode == "tofly") {
        this.flamingos[fIndex].npcMode = "completed";
        let tileIndex = this.flamingos[fIndex].tileIndex;
        this.hexFloor.allTiles[tileIndex].hasElder = false;
        this.stopBlinkTile(tileIndex);
        this.elderFlamingoFliesAway(fIndex);
        this.chatDialog.hideAllChats();
        this.chatDialog.hideAllChats("young");
        this.hideReplayButton();
      }
    }.bind(this), 10000);
    this.elderQTms.push(fTm);


  }

  stopBlinkTile(tileIndex) {
    if (this.hexFloor.allTiles[tileIndex].hasOwnProperty('blinkTL')) {
      if (this.hexFloor.allTiles[tileIndex].blinkTL != null) {
        this.hexFloor.allTiles[tileIndex].blinkTL.kill();
        this.hexFloor.allTiles[tileIndex].blinkTL = null;
        this.hexFloor.changeTileColor(tileIndex, this.hexFloor.allTiles[tileIndex].prevColor);
      }
    }
  }

  elderFlamingoFliesAway(index) {

    // caclulate time based on distance
    let d = get_distance(this.flamingos[index].group.position.x, this.flamingos[index].group.position.y, 0, 0);
    let seconds = Math.max(1, 5 * (d / 20));

    setTimeout(function () {
      this.flamingos[index].npcMode = "flying_to_pool";
      this.flamingos[index].mode = "flyToPool";
      this.flamingos[index].setAction(this.flamingos[index].animationActions['fly'], 'fly');
    }.bind(this), 1000);

    let r = 1.5 + Math.random() * .5;
    let a = index * ((Math.PI * 2) / this.maxFlamingos); //Math.random() * Math.PI * 2
    let x = r * Math.sin(a);
    let yMax = Math.max(this.flamingos[index].group.position.y + 3, 3);
    let z = r * Math.cos(a);
    let yStart = this.flamingos[index].group.position.y + 1;

    if (index == 1) {
      yMax = .25;
      yStart = this.flamingos[index].group.position.y + .25;
    }


    this.flamingoBodies[index].mass = 0;

    let delay = 1;
    gsap.to(this.flamingoBodies[index].position, { y: yStart, duration: .5, delay: delay });
    delay += 1.5;
    gsap.to(this.flamingoBodies[index].position, { x: x, z: z, duration: seconds, delay: delay });
    gsap.to(this.flamingoBodies[index].position, { y: yMax, duration: seconds / 5, delay: delay });
    delay += (seconds - .5);
    gsap.to(this.flamingoBodies[index].position, {
      y: -.1, duration: .5, delay: delay, onComplete: function () {

        if (this.levelCompleted) {
          this.flamingos[index].npcMode = "pool_dancing";
          this.flamingos[index].setAction(this.flamingos[index].animationActions['dance'], 'dance');
        }
        else {
          this.flamingos[index].npcMode = "pool_waiting";
          this.flamingos[index].setAction(this.flamingos[index].animationActions['idle'], 'idle');
        }

        this.flamingoBodies[index].mass = 1;
      }.bind(this)
    });

    if (this.levelCompleted && !this.didEndLevelPond && !this.myFlamingo.getEnvironment().includes("water")) {
      this.didEndLevelPond = true;
      document.getElementById("end-level-pond-popup").classList.add("show");
      this.soundEffects.playClip("win");
      setTimeout(() => {
        document.getElementById("end-level-pond-popup").classList.remove("show");
      }, 15000);
    }


  }

  async returnedToPool() {

    if (this.elderQTms) {
      this.elderQTms.forEach((item, i) => {
        clearTimeout(item);
      });
    }

    this.closeHelperHint();
    document.getElementById("end-level-pond-popup").classList.remove("show");
    this.currentFlamingoChatIndex = this.allEldersFound[0];
    this.flamingos[this.currentFlamingoChatIndex].npcMode = "talking";
    this.chatDialog.clearChat();
    let timetoplay = this.playDialog(1, "ending");
    this.showReplayButton();

    if (!this.didLevelEnd) {
      this.handleAxlTrack("level_end", this.levelName);
      this.didLevelEnd = true;
      document.getElementById("collected-images-level-" + this.levelName).classList.remove("locked");
      this.soundEffects.playClip("flamingos_group");
    }

    if (this.levelName == this.endLevelName) {
      console.log("game completed");
      let seconds = this.getTimeFromStart();
      if (!this.didGameEnd) {
        this.handleAxlTrack("game_end", null, seconds);
        this.didGameEnd = true;
      }
    }

    this.playedDream = true;
  }

  updateWordBankForElder(fIndex) {
    let liItems = document.querySelectorAll("#word-bank-list li");
    liItems.forEach((item, i) => {
      let word = item.getAttribute("data-word");
      if (this.flamingos[fIndex].npcWordsGuessed.includes(word)) {
        item.classList.add("used");
      } else {
        item.classList.remove("used");
      }
    });

  }

  resetWordBankAfterCorrectAnswer() {
    let liItems = document.querySelectorAll("#word-bank-list li");
    liItems.forEach((item, i) => {
      let word = item.getAttribute("data-word");
      if (!this.solvedWords.includes(word)) {
        item.classList.remove("used");
      } else {
        item.classList.remove("used");
        item.classList.add("solved");
      }
    });
  }

  updateElderUi() {

    let el = document.querySelector(".elder-text");
    el.innerText = this.eldersFound + " / " + (this.maxFlamingos - 1);
  }


  //----- level ending
  playEndingImageAndUI() {

    if (this.levelName != "custom") {

      this.closeMiniMap();
      this.chatDialog.hideAllChats();
      this.chatDialog.hideAllChats("young");
      this.hideReplayButton();

      document.getElementById("ending-image").classList.add("show");
      this.hidePlayerButtonsAndUI();

      if (this.isMobile()) {
        this.deactivateMobileControls();
      }
    }

    setTimeout(function () {
      document.getElementById("ending-level-ui").classList.add("show");

      this.addLevelIconToBar(this.levelName);
    }.bind(this), 2000);


    this.flamingos.forEach((item, i) => {
      if (i > 0) {
        item.npcMode = "pool_dancing";
        item.setAction(item.animationActions['dance'], 'dance');
      }
    });

  }

  levelComplete() {

    this.levelCompleted = true;

  }

  getTimeFromStart() {
    return (Date.now() - this.startGameTime) / 1000;
  }

  async handleChooseStayAndPlay() {
    this.handleAxlTrack('stay_and_play', this.levelName);

    document.getElementById("end-level-popup").classList.remove("show");

    // this.resetWordBankForStayAndPlay();

    let promptItems = document.querySelectorAll(".prompt-display-item");
    promptItems.forEach((item, i) => {
      item.classList.add("show");
    });
    document.getElementById("ending-image").classList.remove("show");
    document.getElementById("ending-level-ui").classList.remove("show");

    let wbExplain = this.translator.getDynamicText("word-bank-explain-play");
    document.getElementById("word-bank-explain").innerText = wbExplain;

    if (this.levelName != this.endLevelName)
      document.getElementById("next-level-after").classList.add("show");

    this.exitElderConversation(this.currentFlamingoChatIndex);
    this.closeHelperHint();
    this.setHelperHint("play-park", "primary");
    this.currentFlamingoChatIndex = null;

    if (this.isMobile()) {
      this.showMobileControls();
    }

    if (!this.sparkles) {
      let sparkle = await this.textureLoad("./textures/sparkle.png");
      this.sparkles = new ParticleSystem(this.scene, sparkle);
      this.sparkles.init(100, this.myFlamingo.group.position);

    }

    this.benches.forEach((item, i) => {
      this.blinkBench(i);
    });

  }

  addLevelIconToBar(levelName) {

    document.getElementById("level-art-show-button").classList.remove("hide");
    document.querySelector(".collected-images-buttons-" + levelName).classList.remove("hide");

  }

  showCollectedImages() {
    //document.getElementById("ending-image").classList.add("show");
    document.getElementById("collected-images-popup").classList.add("show");
  }

  resetWordBankForStayAndPlay() {
    let liItems = document.querySelectorAll("#word-bank-list li");
    liItems.forEach((item, i) => {
      item.classList.remove("used");
    });

    let liButtons = document.querySelectorAll("#word-bank-list li button");
    liButtons.forEach((item, i) => {
      item.style.pointerEvents = "all";
    });

    let wbExplain = this.translator.getDynamicText("word-bank-explain-play");
    document.getElementById("word-bank-explain").innerText = wbExplain;


  }


  //---  bridge for level "spirit"
  makeBridge() {

    this.myTones = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

    let startId = 127;//309
    let endId = 132;//166

    let startPos = this.hexFloor.allTiles[startId].position;
    startPos.y = this.hexFloor.allTiles[startId].scale.y / 2;
    let endPos = this.hexFloor.allTiles[endId].position;
    endPos.y = this.hexFloor.allTiles[endId].scale.y / 2;


    let d = startPos.distanceTo(endPos);

    let dir = endPos.clone().sub(startPos).normalize().multiplyScalar(d / 2);
    let midPos = startPos.clone().add(dir);
    midPos.y += 3;

    const curve = new THREE.QuadraticBezierCurve3(startPos, midPos, endPos);

    let spacing = .5;
    let step = d / spacing;

    const geometry = new THREE.BoxGeometry(spacing * 3, .05, spacing - .05);
    //let midpoint =
    this.planks = [];
    this.plankBodies = [];
    const steps = 25;
    for (let i = 1; i < steps; i++) {
      const material = new THREE.MeshToonMaterial({ color: 0x666666 });
      let pos = curve.getPoint(i / steps);
      let prevPos = curve.getPoint((i - 1) / steps);
      let mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(pos);//set(startPos.x,startPos.y,startPos.z + (spacing*i));
      mesh.lookAt(prevPos);
      mesh.name = "bridge";
      this.planks.push(mesh);
      this.scene.add(mesh);

      let body = new CANNON.Body({ mass: 0 });
      body.position.copy(mesh.position);
      body.quaternion.copy(mesh.quaternion);
      body.allowSleep = true;
      const shape = new CANNON.Box(new CANNON.Vec3(spacing * 1.5, .025, spacing * .5 - .025));
      body.addShape(shape, new CANNON.Vec3(0, 0, 0));
      this.world.addBody(body);
      body.bridgeIndex = i - 1;

      this.plankBodies.push(body);

      const onColor = new THREE.Color(0xFE76E2);//);
      const offColor = new THREE.Color(0xB858FE);

      body.addEventListener("collide", function (e) {
        if (e.body.id == this.physicsCharacter.body.id) {

          if (this.myFlamingo.environment != "bridge" && this.currentPreloadTrackId != "bridge") {
            this.myFlamingo.environment = "bridge";
            if (this.levelCompleted) {
              this.playInParkSong("bridge");
              this.handleAxlTrack("play_bridge", this.levelName);
              shuffle(this.myTones);
            } else {
              // const lastHint = this.lastPrimaryHint;//(' ' + this.helperHintLabel).slice(1);
              // this.setHelperHint("hint-bridge");
              // this.showHelperHint(4000,lastHint);
              this.setFeedback("hint-bridge");
              this.showFeedback(3000);
            }
          }

          if (this.levelCompleted) {
            let start = e.target.bridgeIndex - 2;//-1;//25 * (e.target.bridgeIndex / steps);
            let end = start + 2;
            start = Math.max(0, start);
            end = Math.min(end, steps);

            this.planks.forEach((item, j) => {
              if (j == e.target.bridgeIndex) {
                item.material.color = onColor;//new THREE.Color(0xff0000);;
              } else {
                item.material.color = offColor;//new THREE.Color(0xFE76E2);;
              }
            });

            let toneIndex = this.myTones[e.target.bridgeIndex % 8];
            this.soundEffects.playClip("tone-" + toneIndex);

          }


        }

      }.bind(this));

      this.clickableObjects.push(mesh);
    }


  }

  colorInBridge() {
    const myColor = new THREE.Color(0xB858FE);//);

    this.planks.forEach((item, i) => {
      this.tweenColor(item.material, null, myColor, 0, .5);
    });

  }

  removeBridge() {
    this.planks.forEach((item, i) => {
      item.visible = false;
    });

    this.plankBodies.forEach((item, i) => {
      // item.sleep();
      this.world.removeBody(item);
    });
    this.plankBodies = null;
  }

  //--- flag posts
  colorFlagPosts() {

    this.flagPosts.forEach((item, i) => {
      item.traverse((child) => {
        if (child.isMesh && !child.name.includes("00")) {
          child.material.color = new THREE.Color(0xDEBA5C0);
        }
      });
    });
  }

  colorFlagPost(index) {
    let item = this.flagPosts[index];
    item.traverse((child) => {
      if (child.isMesh && !child.name.includes("00")) {
        this.tweenColor(child.material, null, new THREE.Color(0xDEBA5C0), 0, .5);
      }
    });
  }

  blinkElderAndFlagTiles() {
    // blink word tiles
    if (this.didBlinkFlags) {
      return;
    }

    this.didBlinkFlags = true;
    this.blinkTLs = [];
    this.hexFloor.allTiles.forEach((item, i) => {
      if ((item.hasWord || item.hasElder) && !item.hasOwnProperty('blinkTL')) {
        item.prevColor = new THREE.Color().copy(item.material.color);
        let prevColor = new THREE.Color(0x76c4f7);//new THREE.Color().copy(item.material.color);
        let realColor = new THREE.Color(0x76c4f7);//new THREE.Color().copy(item.material.color);
        let highlightColor = new THREE.Color(0xFCD823);

        if (item.hasElder) {
          highlightColor = new THREE.Color(0xE65EBD);
        }
        let tl = this.hexFloor.blinkTile(i, realColor, highlightColor, prevColor);
        this.blinkTLs.push(tl);
        item.blinkTL = tl;
      }
    });
  }

  //------
  doFirstUserMove() {

    this.didFirstMove = true;

    setTimeout(function () {
      document.getElementById("landing-instructions").classList.add("hide");
    }.bind(this), 5000);

    document.getElementById("how-to-popup").classList.remove("show");

  }

  //-----------------------  window , keyboard and mouse events
  onWindowResize() {


    if (!this.camera)
      return;

    if (this.mode == "preview" && this.camera) {
      this.camera.aspect = (window.innerWidth / 2) / window.innerHeight;
      this.renderer.setSize(window.innerWidth / 2, window.innerHeight);
      this.camera.updateProjectionMatrix();
    } else {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.camera.updateProjectionMatrix();
    }

    if (window.innerWidth < 920) {
      document.getElementById("next-level-after").innerHTML = this.translator.getDynamicText("next-level-after-mobile");
    }


  }

  onWheel(e) {
    if (this.myFlamingo) {
      if (this.myFlamingo.mode == "moving") {
        this.myFlamingo.mode = "pause_moving";
      }

      if (this.controls.getDistance() > 10) {
        if (this.wordSoup) {
          this.wordSoup.closeLimit = 1000;
        }
      } else {
        if (this.wordSoup) {
          this.wordSoup.closeLimit = this.wordSoup.origCloseLimit;
        }
        if (this.isZoomed) {
          this.endZoomOut();
          this.isZoomed = false;
          //this.controls.maxDistance = 12;
        }
      }
    }
  }

  onPointerDown(e) {

    // e.preventDefault();

    this.isPointerDown = true;
    this.isDragging = false;

    this.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.pointer.y = - (e.clientY / window.innerHeight) * 2 + 1;

    let isDouble = false;
    if (this.checkDouble) {
      clearTimeout(this.checkDouble);
      this.checkDouble = null;
      isDouble = true;
    }

    if (!isDouble) {
      this.checkDouble =
        setTimeout(function () { this.checkDouble = null; }.bind(this), 300);
    }

    this.isDoubleClick = isDouble;

    if (this.myFlamingo && !this.isMobile() && !TEST_MOBILE) {
      if (this.myFlamingo.mode == "moving") {
        this.myFlamingo.mode = "pause_moving";
      }
    }

    this.touchPlay();


    if (this.isMobile()) {
      if (this.mobilepad.gamePadActive == false) {
        if (this.myFlamingo.mode == "moving") {
          this.myFlamingo.mode = "pause_moving";
        }
      }
      if (!this.isDoubleClick)
        this.setFlamingRotationToCameraDir();
    }

    let aboutPopup = document.getElementById("info-bio-popup");
    if (aboutPopup.classList.contains("show")) {
      if (e.target != aboutPopup) {
        aboutPopup.classList.remove("show");
        document.getElementById("info-bio-popup-bd").classList.remove("show");
      }
    }
  }

  onPointerMove(e) {
    e.preventDefault();
    if (this.isPointerDown) {
      this.isDragging = true;
      if (this.isMobile() && this.mobilepad.gamePadActive == true) {
        this.setFlamingRotationToCameraDir();
      }
    }
  }

  onPointerUp(e) {

    // e.stopPropagation();
    this.isPointerDown = false;
    this.isDragging = false;

    if (this.isMobile()) {
      this.setFlamingRotationToCameraDir();
    }


  }

  doUserClickedTileWhileZoomed(instanceId, targPoint = null) {

    if (this.isZoomed) {
      this.isZoomed = false;
    }

    let tile = this.hexFloor.allTiles[instanceId];

    if (this.clickToMove /*|| tile.hasBench*/) {
      // return;
      if (this.clickToMoveAnim) this.clickToMoveAnim.kill();
      if (this.clickToMoveYAnim) this.clickToMoveYAnim.kill()

    }

    this.clickToMove = true;

    let userDestPos = { x: this.hexFloor.allTiles[instanceId].position.x, y: this.hexFloor.allTiles[instanceId].scale.y * .5 + .4, z: this.hexFloor.allTiles[instanceId].position.z };

    if (tile.hasElder) {
      this.tempVec.set(userDestPos.x, userDestPos.y, userDestPos.z);
      this.myFlamingo.directionVec.subVectors(this.tempVec, this.myFlamingo.group.position).normalize();
      let dist = this.myFlamingo.farVec.subVectors(this.tempVec, this.myFlamingo.group.position).length() - 1.15;
      userDestPos.x = this.myFlamingo.group.position.x + dist * this.myFlamingo.directionVec.x;
      userDestPos.z = this.myFlamingo.group.position.z + dist * this.myFlamingo.directionVec.z;
    } else if (targPoint != null) {
      userDestPos.x = targPoint.x;
      userDestPos.z = targPoint.z;
    } else if (tile.hasTree || tile.hasBench) {
      userDestPos.z -= 1;
    }



    this.animateUserDoubleClickMove(userDestPos);

  }

  doUserClickedWaterOrPoolWhileZoomed(clickPos) {
    let userDestPos = { x: clickPos.x, y: clickPos.y + .4, z: clickPos.z };
    if (this.clickToMove) {
      // return;
      if (this.clickToMoveAnim) this.clickToMoveAnim.kill();
      if (this.clickToMoveYAnim) this.clickToMoveYAnim.kill()

    }
    this.clickToMove = true;
    this.animateUserDoubleClickMove(userDestPos);

  }

  animateUserDoubleClickMove(userDestPos) {

    this.tempVec.set(userDestPos.x, this.myFlamingo.group.position.y, userDestPos.z);
    let origin = this.myFlamingo.originVec.set(this.myFlamingo.group.position.x, this.myFlamingo.group.position.y, this.myFlamingo.group.position.z);
    let dest = userDestPos;
    let direction = this.myFlamingo.directionVec;
    let far = this.myFlamingo.farVec;

    this.myFlamingo.raycaster.set(origin, direction.subVectors(dest, origin).normalize());
    this.myFlamingo.raycaster.far = far.subVectors(dest, origin).length(); // comment this line to have an infinite ray
    let intersection = this.myFlamingo.raycaster.intersectObject(this.hexFloor.instanceMesh);

    let highestVal = userDestPos.y;

    if (intersection.length > 0) {
      intersection.forEach((item, i) => {
        let tile = this.hexFloor.allTiles[item.instanceId];
        if (((tile.scale.y / 2) + .5) > highestVal) {
          highestVal = (tile.scale.y / 2) + .5;
        }
      });
    }

    userDestPos.y = highestVal;

    let dist = this.tempVec.distanceTo(this.myFlamingo.group.position);
    this.moveDir.set(0, 0, 0);
    this.moveDir.subVectors(this.tempVec, this.myFlamingo.group.position).y = 0;
    let angle = Math.atan2(this.moveDir.x, this.moveDir.z);
    this.myFlamingo.group.rotation.y = angle;
    this.myFlamingo.mode = "flying";
    this.myFlamingo.setAction(this.myFlamingo.animationActions['fly'], 'fly');
    this.controls.enabled = false;

    this.physicsCharacter.body.mass = 0;

    let delay = 0;

    if (this.myFlamingo.group.position.y < userDestPos.y) {
      let d = userDestPos.y - this.myFlamingo.group.position.y;
      d /= 2;
      this.clickToMoveYAnim = gsap.to(this.myFlamingo.group.position, {
        y: userDestPos.y, duration: d, onUpdate: function () {
          this.updatePhysicsAndCamWhileFlyingToPoint();
        }.bind(this)
      });
      delay = d;
    }


    this.clickToMoveAnim = gsap.to(this.myFlamingo.group.position, {
      x: userDestPos.x, y: userDestPos.y, z: userDestPos.z, duration: dist * .25, delay: delay, onUpdate: function () {

        this.updatePhysicsAndCamWhileFlyingToPoint();

      }.bind(this),
      onComplete: function () {
        this.physicsCharacter.body.mass = 1;
        this.physicsCharacter.body.position.copy(this.myFlamingo.group.position);
        this.physicsCharacter.body.quaternion.copy(this.myFlamingo.group.quaternion);

        this.controls.enabled = true;
        this.clickToMove = false;
        this.myFlamingo.mode = "moving";
        this.physicsCharacter.animation = "jump";
        this.myFlamingo.setAction(null, 'jump');
      }.bind(this)
    });
  }

  updatePhysicsAndCamWhileFlyingToPoint() {
    this.posHelperCam.position.copy(this.myFlamingo.camTarget.position);
    this.myFlamingo.group.localToWorld(this.posHelperCam.position);
    this.posHelperTarget.position.copy(this.myFlamingo.controlTarget.position);
    this.myFlamingo.group.localToWorld(this.posHelperTarget.position);

    this.camera.position.lerp(this.posHelperCam.position, .25);
    this.controls.target.lerp(this.posHelperTarget.position, .25);

    this.physicsCharacter.body.position.copy(this.myFlamingo.group.position);
    this.physicsCharacter.body.quaternion.copy(this.myFlamingo.group.quaternion);
  }

  canTriggerUIAction(e) {
    if (e.repeat) return false;
    let isSpace = (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar');
    let isEnter = (e.key == 'Enter' || e.code == 'Enter');
    if (isSpace && this.spaceNeedsLift) return false;
    if (isEnter && this.enterNeedsLift) return false;
    return true;
  }

  registerUIAction(e) {
    let isSpace = (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar');
    let isEnter = (e.key == 'Enter' || e.code == 'Enter');
    if (isSpace) {
      this.spaceNeedsLift = true;
      this.ignoreNextSpaceUp = true;
    }
    if (isEnter) {
      this.enterNeedsLift = true;
    }
  }

  onKeyPress(e) {

    if (DEBUG_MODE == true) {
      if (e.key == '+') {

        // this.colorAllTiles(0);
        // this.colorInAllTrees();
        // this.beginResetLevel("community");
        // setInterval( function(){
        //   this.updateRug();
        // }.bind(this), 100);
        // this.colorAllTiles(12);
        // this.colorInAllTrees(2);
        // this.colorFlagPosts();
        // this.flamingos.forEach((item, i) => {
        //   if(i > 0){
        //     this.flamingos[i].fadeToPinkStrong(this.levelName);
        //   }
        // });
        //
        // this.levelComplete();
        // this.returnedToPool();
        // if(this.levelName == "spirit"){
        //   this.colorInBridge();
        // }
        let userDestPos = { x: 1, y: .5, z: 1 };
        this.tempVec.set(userDestPos.x, this.myFlamingo.group.position.y, userDestPos.z);
        this.moveDir.set(0, 0, 0);
        this.moveDir.subVectors(this.tempVec, this.myFlamingo.group.position).y = 0;
        let angle = Math.atan2(this.moveDir.x, this.moveDir.z);
        this.myFlamingo.group.rotation.y = angle;
        this.myFlamingo.mode = "flying";
        this.myFlamingo.setAction(this.myFlamingo.animationActions['fly'], 'fly');
        gsap.to(this.myFlamingo.group.position, {
          x: userDestPos.x, y: userDestPos.y, z: userDestPos.z, duration: 5, delay: 0, onComplete: function () {

            this.physicsCharacter.body.position.copy(this.myFlamingo.group.position);
            this.physicsCharacter.body.quaternion.copy(this.myFlamingo.group.quaternion);
            this.myFlamingo.mode = "none";
            this.myFlamingo.setAction(this.myFlamingo.animationActions['idle'], 'idle');

          }.bind(this)
        });

      } else if (e.key == '=') {
        this.moveDir.set(0, 0, 0);
        this.moveDir.subVectors(this.flamingos[this.currentFlamingoChatIndex].group.position, this.myFlamingo.group.position).y = 0;
        let angle = Math.atan2(this.moveDir.x, this.moveDir.z);
        // this.myFlamingo.group.rotation.y = angle;
        gsap.to(this.myFlamingo.group.rotation, { y: angle, duration: .5 });
        //this.beginResetLevel("community");
        // setInterval( function(){
        //   this.updateRug();
        // }.bind(this), 100);
        // this.colorAllTiles(12);
        // this.colorInAllTrees(2);
        // this.colorFlagPosts();
        // this.flamingos.forEach((item, i) => {
        //   if(i > 0){
        //     this.flamingos[i].fadeToPinkStrong(this.levelName);
        //   }
        // });

        // this.followCam = false;
        // let cameraDestPos = {x: this.camera.position.x-2, y: this.camera.position.y+1, z: this.camera.position.z};//{x: 1.591506232129052, y: 1.707681150158471, z: -6.162724739732608};
        // let targetPos = this.controls.target;//{x: -6.12323399573677e-17, y: 0.6568128708866758, z: -1.444385};
        // this.animateCameraTo({x: cameraDestPos.x, y: cameraDestPos.y, z: cameraDestPos.z},{x:targetPos.x,y:targetPos.y,z:targetPos.z},10,0,0);
        // this.physicsCharacter.doRec = true;

      } else if (e.key == '-') {
        this.followCam = true;
        // this.physicsCharacter.doRec = false;
        // console.log("cam",this.camera.position);
        // console.log("controls",this.controls.target);
      } else if (e.key == ']') {
        //this.followCam = true;
        this.musiclm.useMonoToStereo = !this.musiclm.useMonoToStereo;
        console.log("this.musiclm.useMonoToStereo", this.musiclm.useMonoToStereo);
      }
    }



    let keybinds = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', 'W', 'A', 'S', 'D', 'i', 'm', ' ', 'j', 'k', 'l', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '?', 'Enter', 'Escape'];

    if (keybinds.includes(e.key) || e.code == 'Space') {
      e.stopPropagation();
      e.preventDefault();
    }

    // Initiate start on landing screen using Space or Enter
    if (!this.gameStarted) {
      let landingEnter = document.getElementById("landing-enter");
      let isLandingEnterVisible = landingEnter && !landingEnter.classList.contains("hide");
      if (isLandingEnterVisible && (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter')) {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          this.didFirstStart = true;
          this.handleAxlTrack("click_enter");
          this.handleStartButtonClicked();
        }
      }
      return;
    }

    // Dismiss info modal (how-to-popup) with Space, Enter, or Escape
    let howToPopup = document.getElementById("how-to-popup");
    if (howToPopup && howToPopup.classList.contains("show")) {
      if (e.key == 'Escape') {
        this.dismissHowToPopup();
        return;
      }
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          this.dismissHowToPopup();
        }
        return;
      }
    }

    // Dismiss info-bio-popup on Escape or Space or Enter
    let bioPopup = document.getElementById("info-bio-popup");
    if (bioPopup && bioPopup.classList.contains("show")) {
      if (e.key == 'Escape') {
        bioPopup.classList.remove("show");
        document.getElementById("info-bio-popup-bd").classList.remove("show");
        if (this.player2) {
          this.player2.pauseVideo();
        }
        return;
      }
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          bioPopup.classList.remove("show");
          document.getElementById("info-bio-popup-bd").classList.remove("show");
          if (this.player2) {
            this.player2.pauseVideo();
          }
        }
        return;
      }
    }

    // Dismiss / continue ending-image modal (#continue-image) with Space or Enter
    let endingImage = document.getElementById("ending-image");
    if (endingImage && endingImage.classList.contains("show")) {
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          let continueBtn = document.getElementById("continue-image");
          if (continueBtn) {
            continueBtn.click();
          } else {
            endingImage.classList.remove("show");
          }
        }
        return;
      }
    }

    // Dismiss collected-images-popup with Space, Enter, or Escape
    let collectedImagesPopup = document.getElementById("collected-images-popup");
    if (collectedImagesPopup && collectedImagesPopup.classList.contains("show")) {
      if (e.key == 'Escape') {
        let closeBtn = document.getElementById("collected-images-popup-close");
        if (closeBtn) closeBtn.click();
        else collectedImagesPopup.classList.remove("show");
        return;
      }
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          let closeBtn = document.getElementById("collected-images-popup-close");
          if (closeBtn) closeBtn.click();
          else collectedImagesPopup.classList.remove("show");
        }
        return;
      }
    }

    // Dismiss end-level-pond-popup with Space, Enter, or Escape
    let endPondPopup = document.getElementById("end-level-pond-popup");
    if (endPondPopup && endPondPopup.classList.contains("show")) {
      if (e.key == 'Escape') {
        let closeBtn = document.getElementById("end-level-pond-popup-close");
        if (closeBtn) closeBtn.click();
        else endPondPopup.classList.remove("show");
        return;
      }
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          let closeBtn = document.getElementById("end-level-pond-popup-close");
          if (closeBtn) closeBtn.click();
          else endPondPopup.classList.remove("show");
        }
        return;
      }
    }

    // Dismiss / advance end-level-popup with Left/Right, A/D, and Space/Enter
    let endLevelPopup = document.getElementById("end-level-popup");
    if (endLevelPopup && endLevelPopup.classList.contains("show")) {
      if (this.highlightedEndLevelBtnIndex == null) {
        this.initEndLevelPopupHighlight();
      }

      if ((e.key == 'ArrowLeft' || e.key == 'a' || e.key == 'A') && !e.repeat) {
        this.navigateEndLevelPopup(-1);
        return;
      }

      if ((e.key == 'ArrowRight' || e.key == 'd' || e.key == 'D') && !e.repeat) {
        this.navigateEndLevelPopup(1);
        return;
      }

      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          this.selectHighlightedEndLevelPopupItem();
        }
        return;
      }

      let movementKeys = [' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', 'W', 'A', 'S', 'D'];
      if (movementKeys.includes(e.key) || e.code == 'Space') {
        return;
      }
    }

    // Advance ending-level-ui with Space or Enter (moves to next level)
    let endingLevelUI = document.getElementById("ending-level-ui");
    if (endingLevelUI && endingLevelUI.classList.contains("show")) {
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          let continueBtn = document.getElementById("continue-image");
          let nextLevelBtn = document.getElementById("go-to-next-level");
          let nextLevelAfter = document.getElementById("next-level-after");
          let uiBtn = endingLevelUI.querySelector("button");

          if (uiBtn && !uiBtn.classList.contains("hide")) {
            uiBtn.click();
          } else if (continueBtn && endingImage && endingImage.classList.contains("show")) {
            continueBtn.click();
          } else if (nextLevelBtn && nextLevelBtn.style.display !== "none" && !nextLevelBtn.classList.contains("hide")) {
            nextLevelBtn.click();
          } else if (nextLevelAfter && nextLevelAfter.classList.contains("show")) {
            nextLevelAfter.click();
          }
        }
        return;
      }
    }

    // Advance intro dialogue with Space or Enter
    let hintsCont = document.getElementById("helper-hints-container");
    if (hintsCont && hintsCont.classList.contains("onboarding")) {
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          this.handleNextOnboarding();
        }
      }
      return;
    }

    // Word bank keyboard navigation and selection
    let wordBank = document.getElementById("word-bank");
    let wbOpen = wordBank && wordBank.classList.contains("show");
    let hasSelectableWords = wbOpen && document.querySelectorAll("#word-bank-list li").length > 0;
    if (wbOpen) {
      if (e.key == 'Escape') {
        this.hideWordBank();
        return;
      }

      if (e.key == 'i' && !e.repeat) {
        this.toggleWordBank();
        return;
      }

      if (hasSelectableWords) {
        if ((e.key == 'ArrowLeft' || e.key == 'a' || e.key == 'A') && !e.repeat) {
          this.navigateWordBank(-1);
          return;
        }

        if ((e.key == 'ArrowRight' || e.key == 'd' || e.key == 'D') && !e.repeat) {
          this.navigateWordBank(1);
          return;
        }

        if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
          if (Date.now() < this.wordBankCooldownUntil) {
            return;
          }
          if (this.canTriggerUIAction(e)) {
            this.registerUIAction(e);
            this.selectHighlightedWordBankItem();
          }
          return;
        }

        let numberKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
        if (numberKeys.includes(e.key) && !e.repeat) {
          let indexLi = parseInt(e.key) - 1;
          this.updateWordBankHighlight(indexLi);
          return;
        }

        let movementKeys = [' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', 'W', 'A', 'S', 'D'];
        if (movementKeys.includes(e.key) || e.code == 'Space') {
          return;
        }
      }
    }

    // Bench options bubble keyboard navigation and selection
    if (this.isBenchBubbleOpen()) {
      if (e.key == 'Escape') {
        this.hideUserInputBubble();
        return;
      }

      if (this.highlightedBenchOptionIndex == null) {
        this.initBenchBubbleHighlight();
      }

      let elements = this.getBenchBubbleElements();
      let currentEl = elements[this.highlightedBenchOptionIndex];

      if ((e.key == 'ArrowLeft' || e.key == 'a' || e.key == 'A') && !e.repeat) {
        e.preventDefault();
        this.navigateBenchBubble(-1);
        return;
      }

      if ((e.key == 'ArrowRight' || e.key == 'd' || e.key == 'D') && !e.repeat) {
        e.preventDefault();
        this.navigateBenchBubble(1);
        return;
      }

      if ((e.key == 'ArrowUp' || e.key == 'w' || e.key == 'W') && !e.repeat) {
        e.preventDefault();
        if (currentEl && currentEl.tagName === 'SELECT') {
          this.changeSelectOption(currentEl, -1);
        }
        return;
      }

      if ((e.key == 'ArrowDown' || e.key == 's' || e.key == 'S') && !e.repeat) {
        e.preventDefault();
        if (currentEl && currentEl.tagName === 'SELECT') {
          this.changeSelectOption(currentEl, 1);
        }
        return;
      }

      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        e.preventDefault();
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          if (currentEl) {
            if (currentEl.tagName === 'SELECT') {
              this.changeSelectOption(currentEl, 1);
            } else if (currentEl.id === 'go-button' || currentEl.tagName === 'BUTTON') {
              currentEl.click();
            }
          }
        }
        return;
      }

      let movementKeys = [' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', 'W', 'A', 'S', 'D'];
      if (movementKeys.includes(e.key) || e.code == 'Space') {
        return;
      }
    }

    // Advance / skip flamingo dialogue with Space or Enter (#next-dialog)
    let dialogControls = document.querySelector(".dialog-controls");
    let nextDialogBtn = document.getElementById("next-dialog");
    let rwEl = document.getElementById("rewind-dialog");
    let isDialogControlsVisible = dialogControls && dialogControls.classList.contains("show") && !dialogControls.classList.contains("hide");
    let isAtDialogEnd = rwEl && !rwEl.classList.contains("hide");
    let hasMoreDialog = this.currentDialogs && this.dialogPart < this.currentDialogs.length && !isAtDialogEnd;

    if (isDialogControlsVisible && this.currentFlamingoChatIndex != null && hasMoreDialog) {
      if (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar' || e.key == 'Enter') {
        if (this.canTriggerUIAction(e)) {
          this.registerUIAction(e);
          if (nextDialogBtn) {
            nextDialogBtn.click();
          } else {
            this.playNextDialogPart();
          }
        }
        return;
      }
    }

    // If still in opening sequence or not ready to move, ignore further movement keys
    if (this.myFlamingo && this.myFlamingo.inOpeningSeq) {
      return;
    }

    if (!this.readyToMove)
      return;

    if (e.key == 'i' && !e.repeat) {
      this.toggleWordBank();
      return;
    } else if (e.key == 'm' && !e.repeat) {
      this.toggleUserZoomFromButton();
    } else if (e.key == '?' && !e.repeat) {
      this.toggleHelperHint();
    } else if (e.key == 'c' && !e.repeat) {
      this.toggleMiniMap();
    }

    let movementKeys = [' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', 'W', 'A', 'S', 'D'];

    if (this.myFlamingo) {

      if (movementKeys.includes(e.key) || e.code == 'Space') {
        if (this.isZoomed) {
          this.endZoomOut();
          this.isZoomed = false;
        }
      }

      if (this.myFlamingo.sittingMinTM) {
        return;
      }

      let isSpace = (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar');
      if (isSpace && this.spaceNeedsLift) {
        return;
      }

      this.myFlamingo.keyHash[e.key] = true;
      if (isSpace) {
        this.myFlamingo.keyHash[' '] = true;
      }

      if (this.myFlamingo.mode != "moving" && (movementKeys.includes(e.key) || isSpace) && this.didFirstMove) {
        this.setFlamingRotationToCameraDir();
      }

      if ((movementKeys.includes(e.key) || isSpace) && this.myFlamingo.isSitting) {
        this.myFlamingo.endSittingAnimation();
      }

      if (this.physicsCharacter && this.myFlamingo.isMoving()) {
        this.physicsCharacter.setKeyHash(e.key);
        if (isSpace) {
          this.physicsCharacter.setKeyHash(' ');
        }
      }

      if ((movementKeys.includes(e.key) || isSpace) && !this.didFirstMove) {
        this.doFirstUserMove();
      }

      if (this.myFlamingo.keyHash.ArrowUp || this.myFlamingo.keyHash.ArrowLeft || this.myFlamingo.keyHash.ArrowRight || this.myFlamingo.keyHash.ArrowDown) {
        return;
      }

    }

  }

  onKeyUp(e) {
    let isSpace = (e.key == ' ' || e.code == 'Space' || e.key == 'Spacebar');
    let isEnter = (e.key == 'Enter' || e.code == 'Enter');

    if (isSpace) {
      this.spaceNeedsLift = false;
    }
    if (isEnter) {
      this.enterNeedsLift = false;
    }

    if (this.myFlamingo) {
      this.myFlamingo.keyHash[e.key] = false;
      if (isSpace) {
        this.myFlamingo.keyHash[' '] = false;
      }
      if (isSpace) {
        if (this.ignoreNextSpaceUp) {
          this.ignoreNextSpaceUp = false;
        } else if (this.readyToMove && !this.myFlamingo.inOpeningSeq) {
          this.didJump();
        }
      }
    }

    if (this.physicsCharacter) {
      this.physicsCharacter.keyHash[e.key] = false;
      if (isSpace) {
        this.physicsCharacter.keyHash[' '] = false;
      }
    }
  }

  setFlamingRotationToCameraDir() {
    // ppint where i am looking if camera rotated by user
    this.AXIS_Y.set(0, 1, 0);
    this.moveDir.set(0, 0, 0);
    this.moveDir.subVectors(this.camera.position, this.myFlamingo.group.position).y = 0;
    this.moveDir.applyAxisAngle(this.AXIS_Y, Math.PI);
    this.moveDir.normalize();
    let angle = Math.atan2(this.moveDir.x, this.moveDir.z);
    this.myFlamingo.group.rotation.y = angle;
  }

  //-------------------------- physics
  addPhysicsObjects() {
    const radius = 1; // m

    // ground
    const groundMaterial = new CANNON.Material('ground');

    let groundBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: new CANNON.Plane(),
      material: groundMaterial
    });
    groundBody.position.y = -.09;
    groundBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0); // make it face up


    groundBody.addEventListener("collide", function (e) {
      if (e.body.id == this.physicsCharacter.body.id) {
        if (this.levelCompleted && this.myFlamingo.environment == "floor" && this.didEnterFloor && this.didLevelEnd) {
          // this.setHelperHint("hint-floor-dance");
          // this.showHelperHint(4000,"play-park");
          this.setFeedback("hint-floor-dance");
          this.showFeedback(3000);
          this.didEnterFloor = false;
        }
      }

      //
    }.bind(this));

    this.world.addBody(groundBody);



    this.addBarrierPhysics();

    // main user flamingo
    this.flamingoBodies = [];
    for (let i = 0; i < this.maxFlamingos; i++) {

      if (i == 0) {
        const physicsCharacter = new CharacterController();
        physicsCharacter.init(1, new CANNON.Sphere(.35), { x: this.destinationX, y: 0, z: 0 }, groundMaterial);
        physicsCharacter.active = true;
        this.world.addBody(physicsCharacter.body);
        this.physicsCharacter = physicsCharacter;//n
        this.flamingoBodies.push(physicsCharacter.body);

        // this.scene.add(this.physicsCharacter.group);

      } else {
        const radius = 1; // m
        const body = new CANNON.Body({
          // type:CANNON.Body.STATIC,
          mass: 0,
          shape: new CANNON.Sphere(.35)
        });
        body.active = false;
        this.flamingoBodies.push(body);
        this.world.addBody(body);

      }

    }


    // Adjust constraint equation parameters for ground/ground contact
    const ground_ground = new CANNON.ContactMaterial(groundMaterial, groundMaterial, {
      friction: 0,
      restitution: 0.3,
      // contactEquationStiffness: 1e8,
      contactEquationRelaxation: 3,
      frictionEquationStiffness: 1e8,
      // frictionEquationRegularizationTime: 3,
    });
    this.world.addContactMaterial(ground_ground);
    this.groundMaterial = groundMaterial;

    // hex tiles\
    this.hexBodies = [];
    this.hexFloor.allTiles.forEach((hex, i) => {

      //if(hex.scale.y > 0){
      const hexBody = new CANNON.Body({
        material: groundMaterial,
        mass: 0,
        // shape: new CANNON.Cylinder(this.hexFloor.hexSize,this.hexFloor.hexSize,hex.scale.y, 6),
        shape: new CANNON.Cylinder(this.hexFloor.hexSize, this.hexFloor.hexSize, 100, 6),

      });
      hexBody.position.copy(hex.position);
      hexBody.position.y = -50 + (hex.scale.y / 2);
      if (hex.scale.y <= 0)
        hexBody.position.y = -1000;
      this.world.addBody(hexBody);
      this.hexBodies.push(hexBody);
      //}

    });

    // tree trunks
    const h = 4;
    const cylinderShape = new CANNON.Cylinder(.1, .25, h, 6);

    this.treeBodies = [];
    this.pineTrees.forEach((item, i) => {

      const cylinderBody = new CANNON.Body({
        mass: 0,
        shape: cylinderShape,
        position: new CANNON.Vec3(item.position.x, item.position.y + (h / 2), item.position.z),
      });
      cylinderBody.treeIndex = i;
      cylinderBody.addEventListener("collide", function (e) {
        if (e.body.id == this.physicsCharacter.body.id && this.pineTrees[i].isColoredIn) {
          this.addBall(this.pineTrees[i]);
        }
        if (this.levelCompleted) {
          this.playTheTree(cylinderBody.treeIndex);

          let clipIndices = [0, 2, 7, 4];
          let randCLip = Math.floor(Math.random() * clipIndices.length);
          this.soundEffects.playClip("tone-" + clipIndices[randCLip]);
        }

        //
      }.bind(this));
      this.world.addBody(cylinderBody);
      this.treeBodies.push(cylinderBody);
    });

  }

  addBarrierPhysics() {

    let geoPlane = new THREE.PlaneGeometry(40, 20);
    let matPlane = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, transparent: true, opacity: .5, color: new THREE.Color(0xff0000) });
    let shape = new CANNON.Plane(100, 200);
    let r = 27.1;

    for (let i = 0; i < 12; i++) {
      r = 27.1;
      let planeMesh = new THREE.Mesh(geoPlane, new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, transparent: true, opacity: .5, color: new THREE.Color(0xff0000) }));
      if (i == 3) {
        r = 24;
      }
      let angle = i * (Math.PI * 2 / 12);
      planeMesh.position.x = r * Math.cos(angle);
      planeMesh.position.z = r * Math.sin(angle);
      planeMesh.lookAt(new THREE.Vector3());
      const barrierBody = new CANNON.Body({
        type: CANNON.Body.STATIC,
        shape: shape
      });
      barrierBody.position.x = r * Math.cos(angle);
      barrierBody.position.z = r * Math.sin(angle);
      barrierBody.quaternion.copy(planeMesh.quaternion); // make it face up
      barrierBody.addEventListener("collide", function (e) {
        if (e.body.id == this.physicsCharacter.body.id) {
          this.barrierMesh.visible = true;
          gsap.to(this.barrierMesh.material, { opacity: .25, duration: .25 });
          gsap.to(this.barrierMesh.material, {
            opacity: 0, duration: 2, delay: .25, onComplete: function () {
              this.barrierMesh.visible = false;
            }.bind(this)
          });
        }
        //
      }.bind(this));
      this.world.addBody(barrierBody);

    }

    let geo = new THREE.CylinderGeometry(r + 1.5, r + 1.5, 1000, 12);
    let mat = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide, transparent: true, opacity: 0, color: new THREE.Color(0xff00ff) });
    let mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.y = degrees_to_radians(16);
    this.scene.add(mesh);
    this.barrierMesh = mesh;
    this.barrierMesh.visible = false;
  }

  async addBall(tree) {
    let colors = {
      "community": [0xF73A3A, 0xE7348A, 0xF8C43A],
      "spirit": [0xB657FF, 0xE361C0, 0xFFE744]
    }

    if (!this.balls) {
      this.balls = [];
    }

    if (!this.ballBodies)
      this.ballBodies = [];

    if (!this.ballModels) {
      this.ballModels = [];
      let a = await this.modelLoader("./models/apple2_1.glb");
      a.scene.traverse((item) => {
        if (item.isMesh && item.name == "Mesh") {
          item.material = new THREE.MeshLambertMaterial({ color: new THREE.Color(colors[this.levelName][0]) });//b8f4a2 });//.color = new THREE.Color(0xffffff);c5c6c4
          item.closeTo = true;
        }
      });
      this.ballModels.push(a);
    }


    let rn = Math.floor(Math.random() * 3);
    let mesh = clone(this.ballModels[0].scene);//new THREE.Mesh(geometry,material);
    mesh.traverse((item) => {
      if (item.isMesh && item.name == "Mesh") {
        item.material = new THREE.MeshLambertMaterial({ color: new THREE.Color(colors[this.levelName][rn]) });//b8f4a2 });//.color = new THREE.Color(0xffffff);c5c6c4
      }
    });

    let angle = Math.random() * (Math.PI * 2);
    let br = .25;
    let scaleMe = 2;
    if (tree.isCentralTree) {
      br = .75;
      scaleMe = 3;
    }

    let size = .03125 * scaleMe;//.125 * .5;

    mesh.position.copy(tree.position);
    mesh.position.y += 4;
    mesh.position.x += (br + Math.random() * .5) * Math.sin(angle);
    mesh.position.z += (br + Math.random() * .5) * Math.cos(angle);
    mesh.scale.set(scaleMe, scaleMe, scaleMe);
    this.scene.add(mesh);

    mesh.myIndex = this.balls.length;
    this.balls.push(mesh);

    var mat3 = new CANNON.Material();
    let shape = new CANNON.Sphere(size);
    let body = new CANNON.Body({
      angularDamping: .9999,
      // linearDamping: .9999,
      material: mat3,
      mass: .5,
      shape: shape,
      position: new CANNON.Vec3(mesh.position.x, mesh.position.y, mesh.position.z),
    });
    this.world.addBody(body);
    this.ballBodies.push(body);


    gsap.to(mesh.scale, { x: 0, y: 0, z: 0, delay: 10, duration: .25 });

    setTimeout(function () {
      let index = mesh.myIndex;
      this.world.removeBody(body);
      this.balls[index].parent.remove(this.balls[index]);
      this.balls[index].dead = true;
    }.bind(this), 10 * 1250);

  }

  //----------------------- environment
  async createInitialEnvironment() {

    // groundplane
    const geometry = new THREE.PlaneGeometry(170, 170, 1, 1);//1500,1500,1,1);
    const groundPlane = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: new THREE.Color(0xe9fcff) }));//0xe9fcff

    groundPlane.position.y = -.15;
    groundPlane.rotation.x = -Math.PI / 2;
    this.scene.add(groundPlane);

    // add other elements
    this.addSunAndSkyAndFog();
    this.addClouds();
    this.addWater();
    this.addFloorPattern();
    console.log("Sky, fog, water, floor created.");

    await this.hexFloor.makeFloor(this.instrument);
    this.clickableObjects.push(this.hexFloor.instanceMesh);
    console.log("Tiles created.");

    // ?? move this to later - all not needed
    if (!this.pineModels) {
      this.pineModels = [];
      let p = await this.modelLoader("./models/tree_london_1.glb");
      this.pineModels.push(p);
      let p2 = await this.modelLoader("./models/tree_london_2_2.glb");
      this.pineModels.push(p2);
      let p3 = await this.modelLoader("./models/tree1.glb");
      this.pineModels.push(p3);

    }

    console.log("Loaded tree models.");


  }

  async addTreesToScene() {
    let maxTrees = 30;
    if (this.isMobile()) {
      maxTrees = 20;
    }
    let musicals = [];
    for (let i = 0; i < 8; i++) {
      musicals.push(Math.floor(Math.random() * maxTrees));
    }
    let count = 0;
    for (let i = 0; i < this.hexFloor.floorData.length; i++) {

      if (count < maxTrees && this.hexFloor.floorData[i].tree == 1) {
        let tile = this.hexFloor.allTiles[i];
        let x = tile.position.x;
        let y = tile.scale.y * .5;
        let z = tile.position.z;
        let tileIndex = tile.tileIndex;
        this.hexFloor.allTiles[tileIndex].treeIndex = count;
        this.hexFloor.allTiles[tileIndex].hasTree = true;
        let isMusical = musicals.includes(count) ? true : false;
        await this.addPineTree(x, y, z, .35, Math.random() * .25 + 1, tile.tileIndex, isMusical);

        count++;
      }
    }


  }

  async addWater() {

    const wText = await this.textureLoad('./textures/water2.png');
    wText.colorSpace = THREE.SRGBColorSpace;

    let r = 5.245 * this.hexFloor.hexSize;
    let waterGeo = new THREE.PlaneGeometry(r, r);
    let waterPlaneMat = new THREE.MeshStandardMaterial({ map: wText, transparent: true, opacity: .5 });
    //0x22BAE0
    let waterPlane = new THREE.Mesh(waterGeo, waterPlaneMat);
    waterPlane.position.y = .01;
    waterPlane.rotation.x = -Math.PI / 2;
    waterPlane.name = "water";
    this.clickableObjects.push(waterPlane);

    this.water = waterPlane;


    let waterPlaneGeo = new THREE.PlaneGeometry(r, r);
    let water_texture_size = 128;

    if (this.isMobile()) {
      water_texture_size = 32;
    }

    let okForWater = true;
    let iosVersion = this.iOSversion();
    if (iosVersion) {
      if (iosVersion < 16) {
        okForWater = false;
      }
    }
    const params = {
      color: '#87C7E8',//22BAE0',
      scale: 1,
      flowX: .2351,
      flowY: .2351
    };
    const waterGeometry = new THREE.PlaneGeometry(r, r);
    let water;


    if (this.isMobile() && !this.isIOS()) {
      water = new Ocean(
        waterPlaneGeo,
        {
          textureWidth: water_texture_size,
          textureHeight: water_texture_size,
          waterNormals: new THREE.TextureLoader().load('./textures/waternormals.jpg', function (texture) {
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
          }),
          alpha: 1,
          alphaTest: .5,
          sunDirection: new THREE.Vector3(),
          sunColor: 0xffffff,
          waterColor: 0x22BAE0,
          distortionScale: 3,
          fog: this.scene.fog !== undefined

        }
      );

      let water_level = .005;
      water.rotation.x = - Math.PI / 2;
      water.position.set(0, water_level, 0);
      this.ocean = water;
      waterPlane.position.y = .01;
      waterPlane.material.opacity = .5;

    } else {
      water = new Water(waterGeometry, {
        color: params.color,
        scale: params.scale,
        flowDirection: new THREE.Vector2(params.flowX, params.flowY),
        textureWidth: 1024,
        textureHeight: 1024
      });

      water.position.y = .025;
      water.rotation.x = Math.PI * - 0.5;
    }





    if (okForWater) {
      this.scene.add(water);
    } else {
      waterPlane.material.opacity = .85;
    }
    this.scene.add(waterPlane);

  }

  addSunAndSkyAndFog() {

    // fog

    let colorFog = new THREE.Color(0xe9fcff);//e1f9ff);
    this.scene.fog = new THREE.FogExp2(colorFog, 0.008510);

    // sky and sun
    this.sky = new Sky();
    this.sky.scale.setScalar(450000);
    this.scene.add(this.sky);

    // Add Sun Helper
    let sunSphere = new THREE.Mesh(
      new THREE.SphereGeometry(20000, 16, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    sunSphere.position.y = - 700000;
    var distance = 400000;
    var effectController = {
      turbidity: 6,
      rayleigh: 0.642, // 2,
      mieCoefficient: 0.005,
      mieDirectionalG: 0.9, // 0.8,
      luminance: 0.9, // 1,
      inclination: 0.245, // 0.49, // elevation / inclination
      azimuth: 0.35, // 0.25, // Facing front,
      sun: true
    };

    var uniformsS = this.sky.material.uniforms;
    uniformsS.turbidity.value = effectController.turbidity;
    uniformsS.rayleigh.value = effectController.rayleigh;
    uniformsS.mieCoefficient.value = effectController.mieCoefficient;
    uniformsS.mieDirectionalG.value = effectController.mieDirectionalG;
    this.skyUniforms = uniformsS;


    var theta = Math.PI * (effectController.inclination - 0.5);
    var phi = 2 * Math.PI * (effectController.azimuth - 0.5);

    sunSphere.position.x = distance * Math.cos(phi);
    sunSphere.position.y = distance * Math.sin(phi) * Math.sin(theta);
    sunSphere.position.z = distance * Math.sin(phi) * Math.cos(theta);
    sunSphere.visible = effectController.sun;
    uniformsS.sunPosition.value.copy(sunSphere.position);

  }

  async addFloorPattern() {

    let texturePath = floorTextureData[this.instrument].path + "/" + floorTextureData[this.instrument].path + "_0.png";

    let texture = await this.textureLoad("./textures/" + texturePath);

    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(12, 12 * floorTextureData[this.instrument].ratio);
    texture.colorSpace = THREE.SRGBColorSpace;

    const geometry = new THREE.CylinderGeometry(10 * this.hexFloor.hexSize, 10 * this.hexFloor.hexSize, .02, 20);
    const material = new THREE.MeshLambertMaterial({ color: 0xffffff, map: texture, transparent: false, opacity: 0 });

    this.floorTexture = texture;

    const floorPlane = new THREE.Mesh(geometry, material);
    floorPlane.receiveShadow = true;
    floorPlane.position.y = -.1;

    this.floorCircle = floorPlane;
    this.floorCircle.name = "floor";
    this.clickableObjects.push(this.floorCircle);


    const floorPlane2 = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: new THREE.Color(0xd8d6d8) }));
    floorPlane2.position.y = -.125;
    this.scene.add(floorPlane2);


    this.scene.add(floorPlane);
    this.floorTextureRepeat = 8;

  }

  async addClouds() {
    this.musicClouds = [];
    let total = 12;
    let r = 90;
    let y = 10;
    let cloud = await this.modelLoader("./models/cartoon_cloud.glb");
    cloud.scene.traverse((child) => {
      if (child.isMesh)
        child.material.color = new THREE.Color(0xffffff);
    });

    for (let i = 0; i < total; i++) {
      let radius = Math.random() * 5 + r;
      y = 8;
      if (i > 7) {
        radius = Math.random() * 6 + 14;
        y = 10;
      }
      let angle = Math.random() * (2 * Math.PI);
      let x = radius * Math.sin(angle);
      let z = radius * Math.cos(angle);
      let c = clone(cloud.scene);
      let s = .25 + Math.random() * .5;
      c.scale.set(s, s, s);
      c.position.set(x, y, z);
      c.lookAt(0, 9 + Math.random() * 2, 0);
      this.scene.add(c);
      //if(i > 8){
      this.musicClouds.push(c);
      //}
    }
  }

  async addPlants(tileMesh, type = "plant") {

    if (!this.allGrass)
      this.allGrass = [];

    if (!this.plantModels) {
      this.plantModels = [];
      let model1 = await this.modelLoader("./models/plant_small.glb");
      this.plantModels.push(model1);
      let model2 = await this.modelLoader("./models/plant_small.glb");
      this.plantModels.push(model2);
      model1.scene.traverse((child) => {
        if (child.isMesh && tileMesh.finalColor)
          child.material.color = new THREE.Color(this.yinkaColors.getRandomGrass());
      });
      model2.scene.traverse((child) => {
        if (child.isMesh && tileMesh.finalColor)
          child.material.color = new THREE.Color(this.yinkaColors.getRandomGrass());
      });

    }

    if (this.plantCount >= 500)
      return;

    let randModel = Math.floor(Math.random() * 2);
    let o = clone(this.plantModels[randModel].scene);
    let scale = .5 + Math.random() * .5;

    let r = .25 + Math.random() * (.75 * this.hexFloor.hexSize);
    let angle = Math.random() * (Math.PI * 2);
    let x = tileMesh.position.x + r * Math.sin(angle);;
    let y = tileMesh.position.y + tileMesh.scale.y * .5;// + .25;
    let z = tileMesh.position.z + r * Math.cos(angle);

    // set the instance position and scale and rotation
    let dummy = new THREE.Object3D();
    let _position = new THREE.Vector3(x, y, z);

    this.plantCount++;

    o.position.x = x;//x + r * Math.sin(angle);
    o.position.y = y;
    o.position.z = z;// + r  * Math.cos(angle);
    o.scale.set(0, 0, 0);
    gsap.to(o.scale, { x: scale, y: scale, z: scale, duration: 1, ease: "elastic.out(1, 0.3)" });
    o.rotateY(angle);



    this.scene.add(o);
  }

  async instancedMeshPlants() {
    let count = 1000;
    this.plantMeshMax = count;

    if (!this.plantModel) {
      this.plantModel = await this.modelLoader("./models/fern_1.glb");
      //Cube001

    }


    this.plantMeshData = [];

    const o = this.plantModel.scene.getObjectByName('Cube001');//leaf_5003');

    let plantGeometry = o.geometry.clone();
    let plantMaterial = new THREE.MeshToonMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    //plantMaterial.depthWrite = true;
    const defaultTransform = new THREE.Matrix4()
      .makeRotationX(Math.PI)
      .multiply(new THREE.Matrix4().makeScale(1, 1, 1));

    plantGeometry.applyMatrix4(defaultTransform);
    this.plantMesh = new THREE.InstancedMesh(plantGeometry, plantMaterial, count);
    this.plantMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    let colorPalette = [0x1DAA7F, 0x6BB647, 0x1DAA7F];//0x01FF0D,0x1DAA7F,0x6BB647,0x1DAA7F];//6BB647];

    for (let i = 0; i < count; i++) {
      let color = new THREE.Color(colorPalette[i % 3]);
      this.plantMesh.setColorAt(i, color);
    }
    let dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
      let _position = new THREE.Vector3(Math.random() * 10 - 10, 0, Math.random() * 10 - 10);
      let _scale = (.25 + Math.random() * .75);
      dummy.position.copy(_position);
      dummy.scale.set(_scale, _scale, _scale);
      dummy.updateMatrix();
      this.plantMesh.setMatrixAt(i, dummy.matrix);
      this.plantMeshData[i] = { oscale: _scale, dscale: _scale };
    }

    this.plantMesh.instanceMatrix.needsUpdate = true;
    this.scene.add(this.plantMesh);
    this.plantMesh.count = 0;


  }

  addNewFernPlant(tileMesh, delay = 0) {

    if (!this.plantMesh)
      return;

    this.plantMesh.count++;

    if (this.plantMesh.count >= this.plantMeshMax) {
      return;
    }

    let dummy = new THREE.Object3D();

    const thisOne = this.plantMesh.count - 1;
    const toScale = (.15 + Math.random() * .15);
    const rotZ = Math.random() * Math.PI * 2;;

    let r = .25 + Math.random() * (.75 * this.hexFloor.hexSize);
    if (tileMesh.hasTree)
      r = .5 + Math.random() * (.5 * this.hexFloor.hexSize);

    let angle = Math.random() * (Math.PI * 2);
    let x = tileMesh.position.x + r * Math.sin(angle);;
    let y = tileMesh.scale.y * .5 + .25 * toScale;
    let z = tileMesh.position.z + r * Math.cos(angle);
    let _position = new THREE.Vector3(x, y, z);
    let _scale = { val: 0 };
    this.plantMesh.getMatrixAt(this.plantMesh.count - 1, dummy.matrix);
    dummy.position.copy(_position);
    dummy.scale.set(0, 0, 0);
    dummy.updateMatrix();

    this.plantMeshData[thisOne].oscale = toScale;
    this.plantMeshData[thisOne].dscale = toScale;
    this.plantMeshData[thisOne].position = new THREE.Vector3().copy(_position);
    this.plantMeshData[thisOne].baseY = tileMesh.position.y + tileMesh.scale.y * .5;
    this.plantMeshData[thisOne].tileIndex = tileMesh.tileIndex;
    this.plantMeshData[thisOne].rotation = new THREE.Vector3(0, 0, 0);
    this.plantMesh.setMatrixAt(this.plantMesh.count - 1, dummy.matrix);
    this.plantMesh.instanceMatrix.needsUpdate = true;

    gsap.to(_scale, {
      val: toScale, duration: 1.25, delay: delay, ease: "elastic.out(1, 0.3)", onUpdate: function () {
        let dummy = new THREE.Object3D();
        dummy.position.copy(_position);
        dummy.rotation.y = rotZ;//degrees_to_radians(-90);
        dummy.rotation.z = degrees_to_radians(-180);;
        dummy.scale.set(_scale.val, _scale.val, _scale.val);
        dummy.updateMatrix();
        this.plantMesh.setMatrixAt(thisOne, dummy.matrix);
        this.plantMeshData[thisOne].dscale = _scale.val;
      }.bind(this)
    });

  }

  async addBench(prompts, bufferIndex, tileIndex) {

    if (!this.benches)
      this.benches = [];

    let index = tileIndex;

    if (!this.hexFloor.allTiles[index].usable) {
      // console.log("bad bench index", index);
      return;
    }

    let model = await this.modelLoader("./models/bench_1.glb");

    model.scene.traverse((item) => {
      if (item.isMesh)
        item.castShadow = true;
      item.name = "bench";
      item.instrument = prompts;
      item.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0x999999) });
      item.benchIndex = this.benches.length;
      this.clickableObjects.push(item);
    });


    let pos = new THREE.Vector3();

    this.hexFloor.allTiles[index].hasBench = true;
    this.hexFloor.allTiles[index].benchIndex = this.benches.length;
    pos.copy(this.hexFloor.allTiles[index].position);

    model.scene.instrument = prompts;
    model.scene.position.copy(pos);
    model.scene.lookAt(new THREE.Vector3(0, pos.y, 0));
    model.scene.rotation.y += (Math.PI);//Math.atan2(-pos.z,-pos.x) + (Math.PI/2);
    model.scene.scale.set(1.5, 0, 0);
    model.scene.name = "bench";
    model.scene.position.y += this.hexFloor.allTiles[index].scale.y * .5;

    this.scene.add(model.scene);

    gsap.to(model.scene.scale, { x: 1.5, y: 1, z: 1, duration: .5, ease: "sine.inOut" });

    // add physics

    // var bbox = new THREE.Box3().setFromObject(model.scene);
    //const size = new THREE.Vector3(.365,.275,.475);
    const size = new THREE.Vector3(.365 * 1.5, .275, .4);

    let body = new CANNON.Body({ mass: 0 });
    body.position.copy(pos);
    body.position.y += this.hexFloor.allTiles[index].scale.y * .5 + size.y;
    body.quaternion.copy(model.scene.quaternion);

    const bottomShape = new CANNON.Box(new CANNON.Vec3(size.x, size.y, size.z));
    body.addShape(bottomShape, new CANNON.Vec3(0, 0, 0));

    const backShape = new CANNON.Box(new CANNON.Vec3(size.x, size.y * 1.4, size.z * .45));
    body.addShape(backShape, new CANNON.Vec3(0, size.y * 2.25, size.z * .95));

    body.prompts = prompts;
    model.scene.instrument = prompts;
    model.scene.benchIndex = this.benches.length;

    body.bufferIndex = bufferIndex;
    this.world.addBody(body);

    body.addEventListener("collide", function (e) {
      if (e.body.id == this.physicsCharacter.body.id) {
        if (this.myFlamingo.enterBench) {
          //this.enterBench(model.scene);

        }
        if (!this.onBench && this.levelCompleted)
          this.doSitOnBench();

      }
      //
    }.bind(this));
    this.benchBodies.push(body);
    this.benches.push(model.scene);
  }

  async addPineTree(x, y, z, r, h, tileIndex, isMusical) {

    if (this.pineModels.length == 0)
      return;

    let finalBarkColor = 0x824312;

    let modelsToUse = levelConfig[this.levelName]['trees'];
    let rn = Math.floor(Math.random() * (modelsToUse.length));
    rn = modelsToUse[rn];
    let o = this.pineModels[rn].scene.clone();

    if (isMusical) {
      o.isMusical = true;
      finalBarkColor = 0x423019;
    }

    let colorPalette = [this.yinkaColors.getDarkGreen(), 0x299147, 0x6BB647];
    let grayColors = [0xcccccc, 0x848484, 0xcecece];
    o.traverse((item) => {
      item.castShadow = true;
      if (item.isMesh) {
        if (item.name == "Cube001_3" || item.name == "bark") {
          //FFC823
          o.bark = item;
          o.finalBarkColor = finalBarkColor;
          item.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0x606060) });
          //color =
          //new THREE.Color(0x999999);//E38AB0);//E361C0);
        } else {
          o.leaves = item;

          o.finalLeavesColor = colorPalette[rn];//this.yinkaColors.getRandomGrass();


          // item.material.color = new THREE.Color(0xececec);
          item.material = new THREE.MeshToonMaterial({ color: new THREE.Color(grayColors[rn]) });

        }
      }
    });

    this.scene.add(o);

    o.position.x = x;
    o.position.z = z;
    o.position.y = y - .25;
    o.rotateY(Math.random() * Math.PI * 2);

    let scale = (.1 + (Math.random() * .05)) * 1.5;
    if (rn == 2)
      scale *= 1.5;
    // scale = .75;
    o.scale.set(scale, scale, scale);
    o.orot = new THREE.Vector3().copy(o.rotation);
    o.oscale = new THREE.Vector3().copy(o.scale);
    o.tileIndex = tileIndex;

    this.pineTrees.push(o);


  }

  async addCentralTree() {
    if (this.pineModels.length == 0)
      return;

    let finalBarkColor = 0x824312;
    let o = await this.modelLoader("./models/bigtree.glb");
    o = o.scene;

    let colorPalette = [this.yinkaColors.getDarkGreen(), 0x299147, 0x6BB647];
    o.traverse((item) => {
      item.castShadow = true;
      if (item.isMesh) {
        if (item.name == "Cube001_3" || item.name == "bark") {
          o.bark = item;
          o.finalBarkColor = finalBarkColor;
          item.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0x999999) });
        } else {
          o.leaves = item;
          o.finalLeavesColor = colorPalette[0];//this.yinkaColors.getRandomGrass();
          item.material = new THREE.MeshToonMaterial({ color: new THREE.Color(0xececec) });
        }
      }
    });


    o.position.x = 0;
    o.position.z = 0;
    o.position.y = -.25;//-6.25;

    let scale = .75;
    o.scale.set(1.25 * scale, scale, 1.25 * scale);
    o.orot = new THREE.Vector3().copy(o.rotation);
    o.oscale = new THREE.Vector3().copy(o.scale);

    o.isCentralTree = true;

    this.centralTree = o;

    this.scene.add(o);
    this.pineTrees.push(o);

    let h = 20;
    let cylinderShape = new CANNON.Cylinder(.5, .75, h, 6);

    const cylinderBody = new CANNON.Body({
      mass: 0,
      shape: cylinderShape,
      position: new CANNON.Vec3(o.position.x, o.position.y + (h / 2), o.position.z),
    });
    cylinderBody.treeIndex = this.pineModels.length;
    this.centerTreeIndex = this.pineModels.length;
    cylinderBody.addEventListener("collide", function (e) {
      if (e.body.id == this.physicsCharacter.body.id && this.pineTrees[cylinderBody.treeIndex].isColoredIn) {
        this.addBall(this.pineTrees[this.pineTrees.length - 1]);

      }

      if (this.levelCompleted)
        this.playTheTree(cylinderBody.treeIndex);
    }.bind(this));
    this.centralTreeBody = cylinderBody;


    this.world.addBody(cylinderBody);

    const geometry = new THREE.CylinderGeometry(1.5, 1.5, .1, 6);
    const material = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    const cylinder = new THREE.Mesh(geometry, material);
    cylinder.position.set(4, 6.15, 2);

    let cylinder2 = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0xff0000 }));
    cylinder2.position.set(-3.5, 10.65, -1);

    let cylinder3 = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0xff00ff }));
    cylinder3.position.set(1.5, 12.65, -3);

    let cylinder4 = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0x00ff00 }));
    cylinder4.position.set(4, 9.75, 0);

    let positions = [[4, 6.25, 2], [-3.5, 10.75, -1], [1.5, 12.75, -3], [4, 10.2, 0]];
    let cylinderShapeLeaf = new CANNON.Cylinder(1.5, 1.5, .01, 6);
    this.centralLeafBodies = [];
    positions.forEach((p, i) => {
      const cylinderBody = new CANNON.Body({
        mass: 0,
        shape: cylinderShapeLeaf,
        position: new CANNON.Vec3(p[0], p[1], p[2]),
      });
      this.world.addBody(cylinderBody);
      this.centralLeafBodies.push(cylinderBody);
      cylinderBody.addEventListener("collide", function (e) {
        if (e.body.id == this.physicsCharacter.body.id && this.levelCompleted) {
          this.playInParkSong("leaf");
          this.centralTree.finalLeavesColor = 0x299147;
          this.colorInTree(this.pineTrees.length - 1);

          this.tempVec.copy(this.myFlamingo.group.position);
          this.tempVec.y += .5;
          this.seekTargets[0] = this.tempVec;

        }
      }.bind(this));

    });


  }


  //----------------------- Coloring in environment (some of this may be obsolete)
  colorInTileAndTileObjects(tileIndex, tweenDuration) {

    let tile = this.hexFloor.allTiles[tileIndex];

    let destColor = new THREE.Color(tile.finalColor);

    if ((tile.hasElder || tile.hasWord) && tile.hasOwnProperty('blinkTL')) {
      tile.blinkTL.kill();
      tile.blinkTL = null;

      if (!this.levelCompleted) {
        let prevColor = new THREE.Color().copy(tile.finalColor);
        let realColor = new THREE.Color().copy(tile.finalColor);
        let highlightColor = new THREE.Color(0xFCD823);

        if (tile.hasElder) {
          highlightColor = new THREE.Color(0xE65EBD);
        }

        let tl = this.hexFloor.blinkTile(tile.tileIndex, realColor, highlightColor, prevColor);
        this.blinkTLs.push(tl);
        tile.blinkTL = tl;
      }
    }


    if (destColor != tile.material.color) {
      this.tweenColor(tile.material, null, destColor, 0, tweenDuration);
      this.hexFloor.tweenTileColor(tile.tileIndex, tile.material.color, destColor, .5);
      tile.isColoredIn = true;
      tile.prevColor = destColor;
    }

    if (tile.hasBench && tile.benchIndex != null) {
      this.colorInBench(tile.benchIndex);
    }

    if (tile.hasTree && tile.treeIndex != null) {
      this.colorInTree(tile.treeIndex);
    }

    let totalColored = 0;
    this.hexFloor.allTiles.forEach((item, i) => {
      if (item.isColoredIn)
        totalColored++;
    });

  }

  colorInBench(index) {
    this.benches[index].traverse((item) => {
      if (item.isMesh)
        item.material.color = new THREE.Color(0xe28bb0);
    });
  }

  colorInAllTrees(delay = 0) {
    this.pineTrees.forEach((item, i) => {
      this.colorInTree(i, delay + i * .05);
    });

  }

  colorInTree(index, delay = 0) {
    let o = this.pineTrees[index];
    o.isColoredIn = true;
    o.traverse((item) => {
      if (item.isMesh) {
        if (item.name != "Cube001_3" && item.name != "bark") {
          this.tweenColor(item.material, null, new THREE.Color(o.finalLeavesColor), delay);
        } else if (item.name == "Cube001_3" || item.name == "bark") {
          this.tweenColor(item.material, null, new THREE.Color(o.finalBarkColor), delay);
        }
      }
    });
  }

  tweenColor(material, initial, final, delay = 0, duration = .35) {
    if (material.isTweening)
      return;
    material.isTweening = true;
    let initialColor = new THREE.Color().copy(material.color);
    return gsap.to(initialColor, {
      r: final.r,
      g: final.g,
      b: final.b,
      ease: 'sine.inOut',
      delay: delay,
      duration: duration,
      overwrite: true,
      onUpdate: function () { material.color = initialColor },
      onComplete: function () { material.isTweening = false; }
    });
  }

  colorAllTiles(delay = .05) {
    this.colorInTilesTweens = [];

    this.hexFloor.lenTiles.forEach((tile, i) => {
      // color hex
      let item = this.hexFloor.allTiles[tile.index];

      if ( /*(item.hasElder || item.hasWord) &&*/ item.hasOwnProperty('blinkTL')) {
        if (item.blinkTL != null) {
          item.blinkTL.kill();
        }

      }

      item.isColoredIn = true;

      setTimeout(function () {
        this.hexFloor.tweenTileColor(tile.index, item.material.color, new THREE.Color(item.finalColor), .5);
      }.bind(this), i * delay)


      if (item.hasBench && item.benchIndex != null) {
        this.colorInBench(item.benchIndex);
      }


    });
  }

  //----------------------- Floor pattern (rug)

  async preloadRugTextures() {
    this.rugTextures = [];
    let total = floorTextureData[this.instrument].limit + 1;
    let path = "./textures/" + floorTextureData[this.instrument].path + "/" + floorTextureData[this.instrument].path + "_";

    for (let i = 0; i < total; i++) {
      let thisPath = path + i + ".png";

      if (!thisPath.includes(floorTextureData[this.instrument].path)) {
        console.log("prev level textures");
        return;
      }
      let texture = await this.textureLoad(thisPath);
      if (!thisPath.includes(floorTextureData[this.instrument].path)) {
        console.log("prev level textures 2");
        return;
      }
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(12, 12 * floorTextureData[this.instrument].ratio);


      texture.needsUpdating = true;
      this.rugTextures.push(texture);
    }
    return true;
  }

  updateRug() {
    if (!this.rugCount) {
      this.rugCount = 1;
    }

    if ((this.rugTextures.length - 1) < this.rugCount) {
      // console.log("Could not update rug",this.rugTextures.length,this.rugCount);
      this.rugCount++;
      return;
    }

    let limit = floorTextureData[this.instrument].limit;

    if (this.rugCount > limit)
      return;

    let texture = this.rugTextures[this.rugCount];

    this.floorTextureRepeat = 12;

    texture.repeat.set(this.floorTextureRepeat, this.floorTextureRepeat * floorTextureData[this.instrument].ratio);

    texture.colorSpace = THREE.SRGBColorSpace;
    this.floorTexture = texture;
    this.floorTexture.needsUpdating = true;
    this.floorCircle.material.color = new THREE.Color(0xffffff);
    this.floorCircle.material.map = this.floorTexture;
    this.floorCircle.needsUpdating = true;
    this.floorCircle.receiveShadow = true;

    this.rugCount++;
  }

  updateTreesWithAnalyser() {

    this.pineTrees.forEach((item, i) => {
      if (this.pineTrees[i].isColoredIn && !item.isCentralTree) {
        if (!this.pineTrees[i].prevScaleY) {
          this.pineTrees[i].prevScaleY = this.pineTrees[i].scale.y;
          this.pineTrees[i].prevRot = this.pineTrees[i].rotation.x;
        }

        let v = this.musiclm.dataArray[i % 15] * .001;
        v *= v;
        const orot = this.pineTrees[i].orot;
        this.pineTrees[i].scale.y = .01 * (v + (this.pineTrees[i].oscale.y)) + .99 * this.pineTrees[i].prevScaleY;
        this.pineTrees[i].prevScaleY = this.pineTrees[i].scale.y;
        let rot = this.pineTrees[i].orot.x - v;
        this.pineTrees[i].rotation.x = .1 * rot + .9 * this.pineTrees[i].prevRot;
        this.pineTrees[i].prevRot = this.pineTrees[i].rotation.x;

      }

    });
  }

  updateCloudsWithAnalyzer() {
    if (!this.cloudsOn)
      return;
    this.musicClouds.forEach((item, i) => {
      if (!item.prevScaleY) {
        item.prevScaleY = item.scale.y;
        item.oscale = item.scale.y;
      }

      let v = this.musiclm.dataArray[i % 15] * .0025;
      v *= v;
      let scale = .1 * (item.oscale + v) + .9 * item.prevScaleY;

      item.scale.set(scale, scale, scale);
      item.prevScaleY = item.scale.y;

    });
  }


  //----------------------- Other environment interactions (som eto be removed)

  enterBench(data) {

    if (this.levelCompleted) {
      data.traverse((item) => {
        if (item.isMesh)
          item.material.color = new THREE.Color(0xe28bb0);
      });
      let text = this.translator.getDynamicText("bench-popup");

      if (this.levelName == "spirit") {
        text = this.translator.getDynamicText("bench-popup-spirit");
      }
      this.setUserInputBubble(text, "bench");

      this.showUserInputBubble();


      this.closeHelperHint();
      // this.setHelperHint("enter-bench");

      // if(!this.isMobile()){
      //   // setTimeout( function(){
      //   //   // if(this.helperHintLabel == "enter-bench")
      //   //     // this.showHelperHint(5000);
      //   // }.bind(this),1000);
      //
      // }
      this.currentBenchIndex = data.benchIndex;
    } else if (this.helperHintLabel != "bench-before-complete") {
      // const lastHint = this.lastPrimaryHint;//(' ' + this.helperHintLabel).slice(1);
      // this.setHelperHint("bench-before-complete");
      // this.showHelperHint(4000,lastHint);

    }

  }

  exitBench() {
    this.onBench = false;
    //this.closeInfoPopUp('bench');
    this.currentBenchIndex = null;
    this.hideUserInputBubble();

    if (this.levelCompleted) {
      this.closeHelperHint();
      // this.setHelperHint("play-park","primary");
    }
    if (this.sparkles) {
      //this.sparkles.setToSparkle(0,this.myFlamingo.group.position);
    }
  }

  blinkBench(benchIndex = null) {
    if (benchIndex == null)
      benchIndex = this.currentBenchIndex;
    let bench = this.benches[benchIndex];
    bench.blinkTMs = [];
    bench.traverse((item) => {
      if (item.isMesh) {
        let realColor = new THREE.Color().copy(item.material.color);
        let highlightColor = new THREE.Color(0xE65EBD);
        let prevColor = new THREE.Color().copy(item.material.color);
        let tl = gsap.timeline({ repeat: -1 });
        tl.to(realColor, {
          r: highlightColor.r,
          g: highlightColor.g,
          b: highlightColor.b,
          ease: 'sine.inOut',
          duration: 1,
          onUpdate: function () { item.material.color = realColor }
        });
        tl.to(realColor, {
          r: prevColor.r,
          g: prevColor.g,
          b: prevColor.b,
          ease: 'sine.inOut',
          duration: 1,
          onUpdate: function () { item.material.color = realColor }
        });
        bench.blinkTMs.push(tl);
      }

    });


  }

  stopBlinkBench(bench) {
    if (bench.blinkTMs) {
      bench.blinkTMs.forEach((item, i) => {
        item.kill();
      });
    }

    bench.traverse((item) => {
      if (item.isMesh)
        item.material.color = new THREE.Color(0xe28bb0);
    });

    this.myFlamingo.endSittingAnimation();
  }

  highlightTileOnTone(tile) {

    tile.prevColor = new THREE.Color().copy(tile.material.color);
    let prevColor = new THREE.Color().copy(tile.material.color);
    let realColor = new THREE.Color().copy(tile.material.color);
    let highlightColor = new THREE.Color(0xffe97b);
    this.hexFloor.highlightTileOnTone(tile.tileIndex, realColor, highlightColor, prevColor);

  }

  //-----------------------   utils
  async modelLoader(url) {
    this.modelsLoaded++;
    // console.log(this.modelsLoaded,url);
    return new Promise((resolve, reject) => {
      this.loader.load(url, data => resolve(data), null, reject);
    });
  }

  async textureLoad(url) {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(url, data => resolve(data), null, reject);
    });
  }

  iOSversion() {
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
      // supports iOS 2.0 and later: <http://bit.ly/TJjs1V>
      var v = (navigator.appVersion).match(/OS (\d+)_(\d+)_?(\d+)?/);
      return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || 0, 10)];
    }
    return null;
  }

  isIOS() {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  }

}
